import React, { useState, useEffect, useMemo, useRef} from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import axios from 'axios';
import { io } from 'socket.io-client';
import { sendToServer } from '../../utilities/hubspot_import';
import FilterModal from '../../components/filterModal';
import DisplayContactCounts from '../../components/contactCount';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useFilter } from '../../context/FilterContext';
import { useSelectedFranchisee } from '../../context/SelectedFranchiseeContext';
import DataTable from 'react-data-table-component';
import createApiInstance from '../../utilities/apiAuth';
import GuideModal from '../GuideModal';
import {trackEvent} from '../../utilities/analytics';
import CollisionModal from '../CollisionModal';
import TagModal from '../../components/TagModal';
/* global BigInt */

function ProjectUpload({ setLoading, toggleModal, selectedSource, user,onFileDataChange, onStepChange, onChangeSource }) {
  const [fileData, setFileData] = useState(null);
  const [fileInfo, setFileInfo] = useState({ name: '', type: '' });
  const [filteredData, setFilteredData] = useState([]);
  const [invalidData, setInvalidData] = useState([]);
  const [duplicateData, setDuplicateData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [displayImportSummary,setDisplayImportSummary] = useState(false);
  const [filterModal, toggleFilterModal] = useState(false);
  const {filters} = useFilter();
  const {selectedFranchisee, updateSelectedFranchisee} = useSelectedFranchisee();
  const [isBlacklistMode, setIsBlacklistMode] = useState(false); // Toggle for whitelist/blacklist filtering
  const [selectedColumn, setSelectedColumn] = useState("Select Column to Filter");
  const [searchText, setSearchText] = useState(""); // Search input value
  const [filteredSuggestions, setFilteredSuggestions] = useState([]); // Dropdown suggestions
  const [allColumnsBase, setAllColumnsBase] = useState([]); // Base list for "All Columns"
  const [whitelistFilters, setWhitelistFilters] = useState({});
  const [blacklistFilters, setBlacklistFilters] = useState({});
  const dropdownRef = useRef(null);
  const columnSelectRef = useRef(null);
  const searchInputRef = useRef(null);
  const [detectedHeaders, setDetectedHeaders] = useState([]);
  const [columnMappings, setColumnMappings] = useState({});
  const [shouldFlatten, setShouldFlatten] = useState(false);
  const [presets, setPresets] = useState([]); // Store user's saved presets
  const [selectedPreset, setSelectedPreset] = useState(""); // Currently selected preset
  const [newPresetName, setNewPresetName] = useState(""); // New preset name input
  const [emailColumn, setEmailColumn] = useState(""); // Stores user-selected email column
  const [zipsColumn, setZipsColumn] = useState(""); // Stores user-selected zips column
  const [projectIdColumn, setProjectIdColumn] = useState(""); // State for selected column
  const [addProjectID, setAddProjectID] = useState(false); // Toggle for adding Project ID
  const addProjectIDRef = useRef(addProjectID); 
  const [rawProjectTitle, setRawProjectTitle] = useState("");
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [newProjectID, setNewProjectID] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(""); 
  const [originalFile, setOriginalFile] = useState(null); // ✅ Add this
  const [initialDetectedHeaders, setInitialDetectedHeaders] = useState([]);
  const [hasFilteredOnce, setHasFilteredOnce] = useState(false);
  const [validContactsOnly, setValidContactsOnly] = useState([]);
  const [collisionData, setCollisionData] = useState(null);
  const [processingCollisions, setProcessingCollisions] = useState(false);
  const [showCollisionModal, setShowCollisionModal] = useState(false);
  const [showNoCollisionModal, setShowNoCollisionModal] = useState(false);
  const [excludedProjects, setExcludedProjects] = useState(new Set()); // Track excluded project IDs
  const onResolve = useRef(null); // Add this ref for collision resolution
  const [importInProgress, setImportInProgress] = useState(false);
  const [importCounts, setImportCounts] = useState(null);
  const [isFlattenHelpOpen, setIsFlattenHelpOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [currentLoadingModal, setCurrentLoadingModal] = useState(null);
  const [isAddProjectIdHelpOpen, setIsAddProjectIdHelpOpen] = useState(false);
  const [isDropdownsHelpOpen, setIsDropdownsHelpOpen] = useState(false);
  const [isImportTypesHelpOpen, setIsImportTypesHelpOpen] = useState(false);
  const [isSummaryHelpOpen, setIsSummaryHelpOpen] = useState(false);
  const [includeTempEmails, setIncludeTempEmails] = useState(false);
  const [collisionContacts, setCollisionContacts] = useState([]);
  const [excludedContacts, setExcludedContacts] = useState(new Set());
  const [selectedNameColumn, setSelectedNameColumn] = useState('');
  const [collisionSearchTerm, setCollisionSearchTerm] = useState('');
  const [useFullName, setUseFullName] = useState(true);
  const [selectedFirstNameColumn, setSelectedFirstNameColumn] = useState('');
  const [selectedLastNameColumn, setSelectedLastNameColumn] = useState('');
  const [originalInvalidData, setOriginalInvalidData] = useState([]); // Preserve original data

  // Multi-page UI state
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    if (typeof onStepChange === 'function') {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  // Search input open logic handled inline on the input; keeping effect as no-op for clarity
  useEffect(() => {
    // no-op
  }, [selectedColumn]);

  // Helper function to check if preset name already exists - must be defined before useMemo hooks
  const isPresetNameExists = (name) => {
    return presets.some(preset =>   
      preset.preset_name.toLowerCase() === name.toLowerCase() && 
      preset.source === selectedSource
    );
  };

  // Create preset without running an import; auto-select it on success
  const handleCreatePresetNow = async () => {
    const trimmedName = (newPresetName || '').trim();

    if (!trimmedName) {
      await Swal.fire({
        title: 'Preset name required',
        text: 'Please enter a name for your preset.',
        icon: 'warning',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    if (isPresetNameExists(trimmedName)) {
      await Swal.fire({
        title: 'Duplicate preset name',
        text: `A preset named "${trimmedName}" already exists for ${selectedSource}.`,
        icon: 'info',
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    try {
      const api = createApiInstance();
      const res = await api.post('/savePreset', {
        name: trimmedName,
        mappings: columnMappings,
        source: selectedSource,
        created_by: user.id,
      });

      if (res.status === 201) {
        const created = res.data?.preset;
        // Optimistically add to local list so it appears immediately
        if (created) {
          setPresets(prev => [created, ...prev]);
        }
        // Re-fetch to ensure server state and order are reflected
        try {
          const refreshed = await api.get(`/getUserPresets?user_id=${user.id}`);
          if (Array.isArray(refreshed.data)) {
            setPresets(refreshed.data);
          }
        } catch (_) {}
        // Auto-select newly created preset, which hides the input
        setSelectedPreset(trimmedName);
        await Swal.fire({
          title: 'Preset created',
          text: `"${trimmedName}" is now selected.`,
          icon: 'success',
          confirmButtonColor: '#16a34a'
        });
      } else {
        await Swal.fire({
          title: 'Unable to create preset',
          text: `Server responded with status ${res.status}.`,
          icon: 'error',
          confirmButtonColor: '#dc2626'
        });
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Unknown error';
      console.error('❌ Failed to create preset:', message);
      await Swal.fire({
        title: 'Failed to create preset',
        text: message,
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  // TagModal state and handlers
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagModalMode, setTagModalMode] = useState('whitelist');
  const [tagModalColumn, setTagModalColumn] = useState('');

  const openTagModal = (mode, column) => {
    setTagModalMode(mode);
    setTagModalColumn(column);
    setIsTagModalOpen(true);
  };

  const tagModalValues = useMemo(() => {
    const source = tagModalMode === 'blacklist' ? blacklistFilters : whitelistFilters;
    return (source[tagModalColumn] || []).slice();
  }, [tagModalMode, tagModalColumn, whitelistFilters, blacklistFilters]);

  const handleRemoveTagValue = (value) => {
    if (!tagModalColumn) return;
    const currentLength = tagModalValues.length;
    if (tagModalMode === 'blacklist') {
      setBlacklistFilters((prev) => {
        const updated = (prev[tagModalColumn] || []).filter((v) => v !== value);
        if (updated.length === 0) {
          const { [tagModalColumn]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [tagModalColumn]: updated };
      });
    } else {
      setWhitelistFilters((prev) => {
        const updated = (prev[tagModalColumn] || []).filter((v) => v !== value);
        if (updated.length === 0) {
          const { [tagModalColumn]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [tagModalColumn]: updated };
      });
    }
    // Auto-close modal if only one (or none) tag remains after removal
    if (currentLength - 1 <= 1) {
      setIsTagModalOpen(false);
    }
  };

  // Toggle a specific value between whitelist and blacklist from within the TagModal
  const handleToggleTagValue = (value) => {
    if (!tagModalColumn) return;
    if (tagModalMode === 'blacklist') {
      // Remove from blacklist
      setBlacklistFilters(prev => {
        const updated = (prev[tagModalColumn] || []).filter(v => v !== value);
        if (updated.length === 0) {
          const { [tagModalColumn]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [tagModalColumn]: updated };
      });
      // Add to whitelist
      setWhitelistFilters(prev => {
        const current = prev[tagModalColumn] || [];
        if (current.includes(value)) return prev;
        return { ...prev, [tagModalColumn]: [...current, value] };
      });
    } else {
      // Remove from whitelist
      setWhitelistFilters(prev => {
        const updated = (prev[tagModalColumn] || []).filter(v => v !== value);
        if (updated.length === 0) {
          const { [tagModalColumn]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [tagModalColumn]: updated };
      });
      // Add to blacklist
      setBlacklistFilters(prev => {
        const current = prev[tagModalColumn] || [];
        if (current.includes(value)) return prev;
        return { ...prev, [tagModalColumn]: [...current, value] };
      });
    }

    // If after toggle, the current modal list would drop to 1 or 0, we close it for smoother UX
    const currentLength = tagModalValues.length;
    if (currentLength - 1 <= 1) {
      setIsTagModalOpen(false);
    }
  };

  const handleClearAllTags = () => {
    if (!tagModalColumn) return;
    if (tagModalMode === 'blacklist') {
      setBlacklistFilters((prev) => {
        const { [tagModalColumn]: _omit, ...rest } = prev;
        return rest;
      });
    } else {
      setWhitelistFilters((prev) => {
        const { [tagModalColumn]: _omit, ...rest } = prev;
        return rest;
      });
    }
    // Close modal when clearing all tags
    setIsTagModalOpen(false);
  };

  // Move all current column tag values from one mode to the opposite
  const handleMoveAllTags = () => {
    if (!tagModalColumn) return;
    const fromBlacklist = tagModalMode === 'blacklist';
    const source = fromBlacklist ? blacklistFilters : whitelistFilters;
    const values = source[tagModalColumn] || [];
    if (values.length === 0) return;

    const addToDest = fromBlacklist ? setWhitelistFilters : setBlacklistFilters;
    const removeFromSource = fromBlacklist ? setBlacklistFilters : setWhitelistFilters;

    addToDest(prev => {
      const current = prev[tagModalColumn] || [];
      const updated = [...new Set([...current, ...values])];
      return { ...prev, [tagModalColumn]: updated };
    });

    removeFromSource(prev => {
      const { [tagModalColumn]: _omit, ...rest } = prev;
      return rest;
    });
  };

  // Toggle a single-value tag between whitelist and blacklist for the given column
  const toggleSingleTagMode = (originMode, column) => {
    if (!column) return;
    if (originMode === 'blacklist') {
      const value = (blacklistFilters[column] || [])[0];
      if (value == null) return;
      setBlacklistFilters(prev => {
        const remaining = (prev[column] || []).filter(v => v !== value);
        if (remaining.length === 0) {
          const { [column]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [column]: remaining };
      });
      setWhitelistFilters(prev => {
        const current = prev[column] || [];
        if (current.includes(value)) return prev;
        return { ...prev, [column]: [...current, value] };
      });
    } else {
      const value = (whitelistFilters[column] || [])[0];
      if (value == null) return;
      setWhitelistFilters(prev => {
        const remaining = (prev[column] || []).filter(v => v !== value);
        if (remaining.length === 0) {
          const { [column]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [column]: remaining };
      });
      setBlacklistFilters(prev => {
        const current = prev[column] || [];
        if (current.includes(value)) return prev;
        return { ...prev, [column]: [...current, value] };
      });
    }
  };

  // Reset toggles that are only relevant to Project List when switching sources
  useEffect(() => {
    if (selectedSource !== "Project List") {
      if (shouldFlatten) setShouldFlatten(false);
      if (addProjectID) setAddProjectID(false);
    }
  }, [selectedSource]);

  // Table height: revert to original fixed size

  // Compute import disabled state and reason for hover tooltip

  useEffect(() => {
    // Get the stored franchisee from localStorage when the component mounts
    const storedFranchisee = JSON.parse(localStorage.getItem('selectedFranchisee'));
    if (storedFranchisee) {
      updateSelectedFranchisee(storedFranchisee);
    }
  }, []); 

  useEffect(() => {
    if (!selectedSource) return;
  
    resetImportState(); // ✅ Reset everything when source changes
  }, [selectedSource]);

  // Notify parent component when file data changes
  useEffect(() => {
    if (onFileDataChange) {
      onFileDataChange(fileData && fileData.length > 0);
    }
  }, [fileData, onFileDataChange]);  

  useEffect(() => {
    addProjectIDRef.current = addProjectID; // Always sync the latest state
    // console.log("fileData:", fileData);
    // console.log("fileInfo:", fileInfo);
  
    if (fileData && fileInfo?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      console.log("🔄 addProjectID changed - Reprocessing file...");
      convertXLSXtoCSV(fileData, addProjectID);
    }
  }, [addProjectID, fileData]); // ✅ Re-runs if addProjectID OR fileData changes

  // Update location in existing data when franchisee changes
  useEffect(() => {
    if (fileData && fileData.length > 0 && (selectedSource === "PlanHub" || selectedSource === "ConstructConnect")) {
      console.log("🏢 Franchisee changed, updating location in existing data");
      const newLocation = (() => {

      const name = user?.role === 'agent' 
        ? user?.selectedFranchisee?.name
          : selectedFranchisee?.name;
        
        if (typeof name === 'string' && name.trim()) {
          return name.replace(/^TWS\s+/i, '').trim();
        }
        return "Unknown Location";
      })();
      
      console.log("🏢 New location:", newLocation);
      
      const updatedFileData = fileData.map(row => ({
        ...row,
        Location: newLocation
      }));
      
      setFileData(updatedFileData);
    }
  }, [user?.selectedFranchisee, selectedSource, selectedFranchisee]);
  
  useEffect(() => {
    const titlesPerProject = {};
    filteredData.forEach(row => {
      const id = row["Project ID"];
      const title = row["Project Title"];
      if (!titlesPerProject[id]) titlesPerProject[id] = new Set();
      titlesPerProject[id].add(title);
    });
  
    const conflicts = Object.entries(titlesPerProject).filter(([_, titles]) => titles.size > 1);
    if (conflicts.length > 0) {
      console.warn("⚠️ Conflicting titles for same Project ID:", conflicts);
      // Optional: show this in UI
    }
  }, [filteredData]);
  

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilteredSuggestions([]); // Close dropdown when clicking outside
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };  
  }, []);  

  // Function to update progress in the loading modal
  const updateProgressInModal = (progress, message) => {
    setImportProgress(progress);
    setProgressMessage(message);
    
    if (currentLoadingModal) {
      // Check if the modal is still valid by checking if it has the update method
      if (typeof currentLoadingModal.update === 'function') {
        try {
          currentLoadingModal.update({
            html: `
              <div class="text-center">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p class="text-gray-600">Processing collision resolution and continuing import...</p>
                <p class="text-sm text-gray-500 mt-2">This may take a few moments</p>
                <div class="mt-4">
                  <div class="w-full bg-gray-200 rounded-full h-2.5">
                    <div class="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style="width: ${progress}%"></div>
                  </div>
                  <p class="text-xs text-gray-500 mt-2">${message || 'Initializing...'}</p>
                </div>
              </div>
            `
          });
        } catch (error) {
          console.warn('Failed to update loading modal:', error);
          // Clear the modal reference if update fails
          setCurrentLoadingModal(null);
        }
      } else {
        // Modal is no longer valid, clear the reference
        setCurrentLoadingModal(null);
      }
    }
  };

  // Progress tracking for import operations
  useEffect(() => {
    // Connect to the Socket.IO server
    const socket = io(process.env.REACT_APP_BACKEND_URL);

    socket.on('connect', () => {
      console.log('Socket.IO connection opened for progress tracking');
    });

    socket.on('job-progress', (data) => {
      console.log('Received progress update:', data);
      if (data.progress !== undefined) {
        // Map backend progress (0-100) to our modal progress
        const modalProgress = Math.max(25, data.progress); // Start at 25% minimum
        updateProgressInModal(modalProgress, `Processing... ${data.progress}% complete`);
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket.IO connection closed');
    });

    return () => {
      socket.disconnect();
    };
  }, [currentLoadingModal]);

  useEffect(() => {
    if (fileData && fileData.length > 0) {
      // Detect and update mappings first
      if (Object.keys(columnMappings).length === 0) {
        const detectedMappings = detectHeadersAndMap(Object.keys(fileData[0] || {}));
        setColumnMappings(detectedMappings);
      }
    }
  }, [fileData]);

  const initialMappingApplied = useRef(false);

  useEffect(() => {
    if (!fileData || fileData.length === 0 || Object.keys(columnMappings).length === 0) return;
    
    // For PlanHub and ConstructConnect, only set initial filteredData if it hasn't been filtered yet
    if ((selectedSource === "PlanHub" || selectedSource === "ConstructConnect") && isFiltered) return;

    const sourceToUse = initialMappingApplied.current ? validContactsOnly : fileData;
    if (selectedSource === 'PlanHub') {
      console.log('[PlanHub][columnMappings]', {
        Name: columnMappings["Name"],
        ProjectTitle: columnMappings["Project Title"],
        Email: columnMappings["Email"],
        Location: columnMappings["Location"],
      });
    }
    // For PlanHub, if auto-detection mapped Name/Email poorly, correct to first Contact N field
    let effectiveMappings = { ...columnMappings };
    if (selectedSource === 'PlanHub') {
      const headerKeys = detectedHeaders && detectedHeaders.length ? detectedHeaders : Object.keys(sourceToUse[0] || {});
      const findFirst = (regex) => headerKeys.find(h => regex.test(h));
      const contactNameKey = findFirst(/^Contact\s+\d+\s+Name$/i);
      const contactEmailKey = findFirst(/^Contact\s+\d+\s+Email$/i);
      if (!effectiveMappings['Name'] || /^(Project\s+Name|Location)$/i.test(effectiveMappings['Name'])) {
        if (contactNameKey) effectiveMappings['Name'] = contactNameKey;
      }
      if (!effectiveMappings['Email'] || !/^Contact\s+\d+\s+Email$/i.test(effectiveMappings['Email'])) {
        if (contactEmailKey) effectiveMappings['Email'] = contactEmailKey;
      }
      console.log('[PlanHub][fixMappings]', effectiveMappings);
    }

    const mappedData = sourceToUse.map(row => {
      let adjustedRow = {};
      desiredColumns.forEach(requiredHeader => {
        // Always preserve Location from the row (it's hardcoded from franchisee selection)
        if (requiredHeader === "Location") {
          const fallbackLocation = (() => {
            const name = user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name;
            if (typeof name === 'string' && name.trim()) {
              return name.replace(/^TWS\s+/i, '').trim();
            }
            return "Unknown Location";
          })();
          adjustedRow["Location"] = row["Location"] ?? fallbackLocation;
          return;
        }
        let detectedHeader = effectiveMappings[requiredHeader];
        let value = detectedHeader in row ? row[detectedHeader] : null;
        if (selectedSource === 'PlanHub') {
          // Fallbacks for blank Contact 1 fields: pick first populated Contact N
          if ((requiredHeader === 'Name' || requiredHeader === 'Email' || requiredHeader === 'Role' || requiredHeader === 'Company') && (value == null || String(value).trim() === '')) {
            const base = requiredHeader === 'Role' ? 'Title' : requiredHeader; // Contact N Title maps to Role
            const pick = getFirstContactField(row, base);
            if (pick.key) {
              detectedHeader = pick.key;
              value = pick.value;
            }
          }
          if (requiredHeader === 'Name') {
            console.log('[PlanHub][map Name]', { detectedHeader, sampleValue: value });
          }
        }
        adjustedRow[requiredHeader] = value;
      });
      return adjustedRow;
    });
  
    setFilteredData(mappedData);
    
    initialMappingApplied.current = true; // ✅ prevents overwriting real filtered results
  }, [columnMappings, validContactsOnly, isFiltered, selectedSource]);
  
  useEffect(() => {
    const shouldDefaultRole = (selectedSource === "PlanHub" || selectedSource === "ConstructConnect") && columnMappings["Role"] && columnMappings["Role"] !== "__blank__";
    setSelectedColumn(shouldDefaultRole ? "Role" : "Select Column to Filter");
  }, [selectedSource, columnMappings.Role]);
  
  
  useEffect(() => {
    const fetchPresets = async () => {
      if (!user?.id) {
        return; 
      }
  
      try {
        const api = createApiInstance();
        const res = await api.get(`/getUserPresets?user_id=${user.id}`);
        const data = res.data;
        setPresets(data);
  

      } catch (err) {
        console.error("Failed to fetch presets:", err.response?.data || err.message);
      }
    };
  
    if (user?.id) {
      fetchPresets();
    }
  }, [user?.id, selectedPreset]);
  
  
  useEffect(() => {
    if (selectedPreset !== "New Preset") {
      setNewPresetName("");
    }
  }, [selectedPreset]);
  
  

  useEffect(() => {
    const selected = presets.find(p => p.preset_name === selectedPreset);
    if (selected && selected.mappings) {
      setColumnMappings(selected.mappings);
      
      // Reset the selected column filter when preset changes
      setSelectedColumn("Select Column to Filter");
      setSearchText("");
      setFilteredSuggestions([]);
      
      // If the preset has mappings for Email and ZIP, automatically select them in the dropdowns
      if (selected.mappings["Email"]) {
        setEmailColumn(selected.mappings["Email"]);
      }
      if (selected.mappings["ZIP"]) {
        setZipsColumn(selected.mappings["ZIP"]);
      }
    }
  }, [selectedPreset]);
  
  useEffect(() => {
    if ((selectedSource === "Lead List" || selectedSource === "Project List") && detectedHeaders.length) {
      // Only auto-detect if no preset is selected or using "New Preset"
      if (selectedPreset === "" || selectedPreset === "New Preset") {
        // Get merged synonyms from all presets
        const mergedSynonyms = extractSynonymsFromPresets(presets);
        
        // Auto-detect email column using merged synonyms
        if (!emailColumn) {
          const emailSynonyms = mergedSynonyms["Email"] || [];
          const emailGuess = detectedHeaders.find(h => 
            h.toLowerCase().includes("email") || 
            emailSynonyms.some(syn => h.toLowerCase().includes(syn.toLowerCase()))
          );
          if (emailGuess) setEmailColumn(emailGuess);
        }
    
        // Auto-detect ZIP column using merged synonyms
        if (!zipsColumn) {
          const zipSynonyms = mergedSynonyms["ZIP"] || [];
          const zipGuess = detectedHeaders.find(h => 
            h.toLowerCase().includes("zip") || 
            h.toLowerCase().includes("postal") ||
            zipSynonyms.some(syn => h.toLowerCase().includes(syn.toLowerCase()))
          );
          if (zipGuess) setZipsColumn(zipGuess);
        }
    
        // Auto-detect Project ID column
        if (!projectIdColumn) {
          const projectGuess = detectedHeaders.find(h => 
            h.toLowerCase().includes("project title") || 
            h.toLowerCase().includes("project name")) ||
            detectedHeaders.find(h => h.toLowerCase().includes("project"));
          if (projectGuess) setProjectIdColumn(projectGuess);
        }
      }
    }
  }, [detectedHeaders, selectedSource, selectedPreset, presets]);


  useEffect(() => {
    if ((selectedSource === "Lead List" || selectedSource === "Project List") && sheetNames.length > 0 && !selectedSheet) {
      const first = sheetNames[0];
      setSelectedSheet(first);
      convertXLSXtoCSV(originalFile, first);
    }
  }, [sheetNames, selectedSource, selectedSheet, originalFile]);  

  useEffect(() => {
    if (selectedPreset === "") {
      setColumnMappings(initialDetectedHeaders); // Reset mappings to empty object
    }
  }, [selectedPreset, initialDetectedHeaders]);

  // Regenerate Project IDs when projectIdColumn changes for Project List with addProjectID
  useEffect(() => {
    if (selectedSource === "Project List" && addProjectID && projectIdColumn && fileData && fileData.length > 0) {
      // console.debug("Project ID column changed - regenerating Project IDs");
      // console.debug("State:", { selectedSource, addProjectID, projectIdColumn, fileDataLength: fileData.length });
      
      // Clear usedHashes to ensure fresh generation
      usedHashes.clear();
      
      // Regenerate Project IDs for all rows
      const updatedFileData = fileData.map(async (row, index) => {
        if (row && Object.keys(row).length > 0) {
          const columnValue = row[projectIdColumn] || `Row_${index + 1}`;
          const generatedId = await generateNumericHash(String(columnValue));
          // console.debug(`[useEffect] Regenerated Project ID for row ${index + 1}: ${generatedId} (from: "${columnValue}")`);
          
          return {
            ...row,
            "Project ID": generatedId
          };
        }
        return row;
      });
      
      // Wait for all async operations to complete
      Promise.all(updatedFileData).then(updatedData => {
        setFileData(updatedData);
        // console.debug("Project IDs regenerated successfully");
      });
    }
  }, [projectIdColumn, selectedSource, addProjectID]); 

  const desiredProjectColumns = ["Project ID","Dealname", "Pipeline", "Dealstage", "Description", "Postal Code"];
  
  const desiredForImport = ["First Name", "Last Name", "Role", "Phone", "Mobile Phone", "Company Phone", "Email", "Website", "Address", "City", "State", "ZIP", "Location"]; 
  
  const desiredColumns = selectedSource === "PlanHub"
  ? ["Project ID", "Name", "Project Title", "Role", "Company", "Phone", "Email", "Website", "Project Types", "Address", "City", "State", "ZIP", "Location"]
  : selectedSource === "ConstructConnect" 
  ? ["Project ID", "Name", "Project Title", "Role", "Company", "Phone", "Email", "Website", "Project Description", "Building Uses", "Project Types", "Project Category", "Address", "City", "State", "ZIP", "Location"]
  : selectedSource === "Lead List"  // For "Lead List", exclude the unwanted columns
  ? ["Project ID", "First Name", "Last Name", "Project Title", "Role", "Company", "Phone", "Mobile Phone", "Company Phone", "Email", "Website", "Address", "City", "State", "ZIP","Location"]
  : ["Project ID", "First Name", "Last Name", "Project Title", "Role", "Company", "Phone", "Mobile Phone", "Company Phone", "Email", "Website", "Project Description", "Building Uses", "Project Types", "Project Category", "Address", "City", "State", "ZIP","Location"];

  const importFile = async () => {
    // Declare loading modal variable outside try block for access in catch
    let dataLoadingModal;
    try {
        // Always reset exclusion and pre-import state at the start of each attempt
        console.log('🔄 Resetting exclusion and pre-import state for a fresh attempt');
        window.excludedDealNames = [];
        window.excludedProjectIds = [];
        window.projectActions = {};
        window.preImportData = null;

        // Check if import is disabled via environment variable
        if (process.env.REACT_APP_DISABLE_IMPORT === 'true') {
          console.log('❌ Import functionality is disabled via REACT_APP_DISABLE_IMPORT environment variable');
          await Swal.fire({
            title: 'Import Disabled',
            text: 'Import functionality is currently disabled. Please contact your administrator.',
            icon: 'warning',
            confirmButtonColor: '#dc2626'
          });
          return;
        }

        let dealStage, hubkey, pipeline;
        // Local variables for collision resolution
        let localNewProjectID = newProjectID;
        let localNewProjectTitle = newProjectTitle;

        // Get location based on user role
        const location = (() => {
          const name = user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name;
          
          if (typeof name === 'string' && name.trim()) {
            return name.replace(/^TWS\s+/i, '').trim();
          }
          return "Unknown Location";
        })();

        console.log("Import Data for Location: ", location);
        
        // Normalize phone numbers in the data before processing
        let normalizedData = filteredResults.map(contact => {
          const updatedContact = {...contact};
          
          // For Lead List with collision resolution, update with resolved project info
          if (selectedSource === "Lead List" && localNewProjectID && localNewProjectTitle) {
            updatedContact["Project ID"] = localNewProjectID;
            updatedContact["Project Title"] = localNewProjectTitle;
          }
          
          // Normalize all three phone fields if they exist
          const phoneFields = ["Phone", "Mobile Phone", "Company Phone"];
          
          phoneFields.forEach(field => {
            if (updatedContact[field]) {
              updatedContact[field] = normalizePhoneNumber(updatedContact[field]);
            }
          });
          
          return updatedContact;
        });
        
        console.log("✅ Using normalized contacts for import:", normalizedData.length);

        // NOTE: temp-email invalid contacts are already merged into `filteredResults`
        // (see the includeTempEmails branch in the filteredResults useMemo) AND run through
        // the whitelist/blacklist filter there. Re-adding raw `invalidData` here double-counted
        // them and bypassed the user's row filter, pushing filtered-out rows to HubSpot.
        // Project ID/Title for Lead List is resolved for every row above (selectedSource === "Lead List").

        // 🔹 Get projects & companies data dynamically
        let updatedProjectsData = await initializeProjectsData(normalizedData, localNewProjectID, localNewProjectTitle);
        const updatedCompanyData = await initializeCompanyData(normalizedData);

      console.log("Updated Company Data Before Blob:", updatedCompanyData);
      console.log("Updated Project Data Before Blob:", updatedProjectsData);

      const createCSVBlob = (data, columns) => {
          const csvData = Papa.unparse(data, { columns });
          return new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      };

        const prepareHubSpotData = (data) => {
            return data.map(row => {
                let adjustedRow = {};
                Object.keys(row).forEach(key => {
                    const newKey = headerMapping[key] || key;
                    adjustedRow[newKey] = row[key];
                });
                return adjustedRow; 
            });
        };
        let cleanContactData = [...normalizedData];

        if (selectedSource === "PlanHub" || selectedSource ===  "ConstructConnect") {
          cleanContactData = normalizedData.map(contact => ({
            ...contact,
            "First Name": contact["Name"]?.trim() || "",
            "Last Name": "", // If you ever want to parse last names later, do it here
            "Location": location, // Add location here too
          }));
        }


        hubkey = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.hubspot_api_key : selectedFranchisee?.client_type?.hubspot_api_key;
        dealStage = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.pipelines?.[0]?.hubspot_stage_id : selectedFranchisee?.client_type?.pipelines?.[0]?.hubspot_stage_id;
        pipeline = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.pipeline : selectedFranchisee?.client_type?.pipeline;
        
        let result;
        console.log("HubKey: ", hubkey);
        console.log("Deal Stage: ", dealStage);
        console.log("Pipeline: ", pipeline);
        if (dealStage && hubkey) {
          // Internal collision detection: same Project ID, different Deal Names in the import file
          const idToDealNames = {};
          updatedProjectsData.forEach(project => {
            const id = project["Project ID"];
            const name = project["Dealname"];
            if (!idToDealNames[id]) idToDealNames[id] = new Set();
            idToDealNames[id].add(name);
          });
          const internalCollisions = Object.entries(idToDealNames)
            .filter(([id, names]) => names.size > 1)
            .map(([id, names]) => ({
              projectId: id,
              dealNames: Array.from(names)
            }));

          if (internalCollisions.length > 0) {
            // Helper to strip project ID from end of deal name
            const stripProjectId = (dealName, projectId) => {
              const suffix = `_${projectId}`;
              return dealName.endsWith(suffix)
                ? dealName.slice(0, -suffix.length)
                : dealName;
            };
            await Swal.fire({
              title: 'Duplicate ID Detected in Import File',
              html: `<ul style="text-align:left; padding-left: 1.5em;">${internalCollisions.map(
                c => `<li style="margin-bottom: 0.5em;"><b>ID:</b> ${c.projectId} <br><b>Names:</b> ${c.dealNames.map(name => stripProjectId(name, c.projectId)).join(', ')}</li>`
              ).join('')}</ul>
              <p style="text-align:center;">Please resolve these before importing. <br><span style="color: red;">Hint: Rename one of the deals and reupload the file.</span></p>`,
              icon: 'error'
            });
            setLoading(false);
            return;
          }
          // COLLISION DETECTION - BEFORE LOADING SPINNER
          console.log("🔍 Checking for project collisions in HubSpot...");
          
          // Show loading modal immediately for collision detection
                          // Show loading modal for the entire data fetching process
                dataLoadingModal = Swal.fire({
                  title: 'Preparing Import...',
                  html: `
                    <div class="text-center">
                      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p class="text-gray-600">Checking for existing projects and emails...</p>
                      <p class="text-sm text-gray-500 mt-2">This may take a few moments</p>
                    </div>
                  `,
                  showConfirmButton: false,
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                  didOpen: () => {
                    Swal.showLoading();
                  }
                });
          
          // Reset the proceed flag at the start of collision detection
          window.proceedWithExcluded = false;
          
          // Add a flag to track if user cancelled the import
          let userCancelledImport = false;
          
          // Extract unique project IDs from the import data
          const uniqueProjectIDs = [...new Set(updatedProjectsData.map(project => project["Project ID"]).filter(Boolean))];
          console.log(`🎯 Found ${uniqueProjectIDs.length} unique project IDs for collision detection:`, uniqueProjectIDs);
          
          // Step 1: Pull deals from HubSpot that match our project IDs with auto-retry
          console.log("📥 Pulling deals from HubSpot for specific project IDs...");
          
          // Auto-retry configuration for collision detection with exponential backoff
          const MAX_RETRIES = 3;
          const BASE_DELAY = 1000; // 1 second base delay
          let retryCount = 0;
          let allDealsResponse = null;
          let hubspotDeals = [];
          let collisionDetectionSuccess = false;
          
          while (retryCount < MAX_RETRIES && !collisionDetectionSuccess) {
            try {
              if (retryCount > 0) {
                const exponentialDelay = BASE_DELAY * Math.pow(2, retryCount - 1); // 1s, 2s, 4s
                console.log(`🔄 Retry attempt ${retryCount}/${MAX_RETRIES} for collision detection...`);
                console.log(`⏳ Waiting ${exponentialDelay}ms before retry (exponential backoff)...`);
                await new Promise(resolve => setTimeout(resolve, exponentialDelay));
              }
              
            // Get deals from HubSpot that match our project IDs - use direct backend URL since this endpoint is not under /api/auth
              allDealsResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
              hubkey,
              projectIDs: uniqueProjectIDs
            });
              
              hubspotDeals = allDealsResponse.data?.results || [];
              collisionDetectionSuccess = true;
              
              // Don't close the loading modal yet - keep it open for email check
              
              if (retryCount > 0) {
                console.log(`✅ Collision detection successful after ${retryCount} retry attempts`);
                // Show a brief success message for retry attempts
                Swal.fire({
                  title: 'Retry Successful',
                  text: `Collision detection succeeded after ${retryCount} retry attempts.`,
                  icon: 'success',
                  timer: 2000,
                  showConfirmButton: false
                });
              } else {
                console.log(`✅ Collision detection successful on first attempt`);
              }
              
            } catch (error) {
              retryCount++;
              console.error(`❌ Collision detection attempt ${retryCount} failed:`, error.message);
              
              if (retryCount >= MAX_RETRIES) {
                console.error(`❌ All ${MAX_RETRIES} retry attempts failed for collision detection`);
                throw error; // Re-throw to trigger the catch block below
              }
              
              const nextDelay = BASE_DELAY * Math.pow(2, retryCount); // Calculate next delay
              console.log(`⏳ Waiting ${nextDelay}ms before retry (exponential backoff)...`);
            }
          }
              
            console.log(`📊 Found ${allDealsResponse.data?.total || 0} deals in HubSpot`);
            
          // DEBUG: Log the actual HubSpot deals data to see the structure
          console.log('🔍 HubSpot deals data structure:', hubspotDeals);
          console.log('🔍 First deal properties:', hubspotDeals[0]?.properties);
          console.log('🔍 Project IDs we are looking for:', uniqueProjectIDs);
            
            // Step 2: Check for collisions based on project ID
            const collisions = [];
          
          try {
            
            // 1. Detect duplicate project IDs in the import
            const projectIdCounts = updatedProjectsData.reduce((acc, project) => {
              acc[project["Project ID"]] = (acc[project["Project ID"]] || 0) + 1;
              return acc;
            }, {});
            const hasDuplicateId = Object.values(projectIdCounts).some(count => count > 1);
            
            for (const project of updatedProjectsData) {
              const incomingProjectId = project["Project ID"];
              const incomingDealName = project["Dealname"];
              
              console.log(`🔍 Checking project: ID=${incomingProjectId}, Name=${incomingDealName}`);
              
              // Find deals with the same project ID
              const matchingDeals = hubspotDeals.filter(deal => {
                const dealProjectId = deal.properties?.project_id;
                console.log(`🔍 Comparing: incoming=${incomingProjectId} vs HubSpot=${dealProjectId} (type: ${typeof dealProjectId})`);
                
                // Handle type mismatches - convert both to strings for comparison
                const incomingIdStr = String(incomingProjectId);
                const dealIdStr = String(dealProjectId);
                const isMatch = incomingIdStr === dealIdStr;
                
                console.log(`🔍 Type-safe comparison: "${incomingIdStr}" === "${dealIdStr}" = ${isMatch}`);
                
                return isMatch;
              });
              
              if (matchingDeals.length > 0) {
                // Found deals with this project ID - check if deal names match
                const existingDeal = matchingDeals.find(deal => 
                  deal.properties?.dealname === incomingDealName
                );
                
                if (existingDeal) {
                  // Same project ID, same deal name - this is an exact match (treat as collision)
                  collisions.push({
                    projectId: incomingProjectId,
                    projectTitle: incomingDealName,
                    existingDeal: existingDeal,
                    reason: "Exact match - same Project ID and Deal Name",
                    previewNewTitle: `${incomingDealName} (2)_${incomingProjectId}`,
                    previewNewId: incomingProjectId
                  });
                  
                  console.log(`⚠️ Exact match collision found: "${incomingDealName}" with project ID ${incomingProjectId}`);
                } else {
                  // Same project ID, different deal name - collision!
                  collisions.push({
                    projectId: incomingProjectId,
                    projectTitle: incomingDealName,
                    existingDeal: matchingDeals[0],
                    reason: "Same Project ID, different Deal Name",
                    previewNewTitle: `${incomingDealName} (2)_${incomingProjectId}`,
                    previewNewId: incomingProjectId
                  });
                  
                  console.log(`⚠️ Collision found: Project ID ${incomingProjectId} exists with different deal name "${matchingDeals[0].properties?.dealname}"`);
                }
              } else {
                console.log(`✅ No collision: Project ID ${incomingProjectId} is new`);
              }
            }
              
              console.log(`📊 Found ${collisions.length} collisions out of ${updatedProjectsData.length} projects`);
              
                            // Pre-import email existence check - RIGHT AFTER collision detection
              console.log('🔍 Performing pre-import email existence check...');
              
              // Update the loading modal text to show email check progress
              dataLoadingModal.update({
                title: 'Preparing Import...',
                html: `
                  <div class="text-center">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p class="text-gray-600">Checking which emails already exist in HubSpot...</p>
                    <p class="text-sm text-gray-500 mt-2">This helps optimize your import</p>
                  </div>
                `
              });
              
              const importedEmails = [...new Set(cleanContactData.map(c => c.Email).filter(Boolean))];
              console.log('📧 Unique emails to import:', importedEmails);
              
              // Create a mapping of emails to their project IDs for later exclusion adjustment
              const emailToProjectMapping = {};
              cleanContactData.forEach(contact => {
                if (contact.Email && contact["Project ID"]) {
                  const email = contact.Email.toLowerCase();
                  if (!emailToProjectMapping[email]) {
                    emailToProjectMapping[email] = new Set();
                  }
                  emailToProjectMapping[email].add(contact["Project ID"]);
                }
              });
              window.emailToProjectMapping = emailToProjectMapping;
              console.log('🗺️ Email to Project mapping:', emailToProjectMapping);
              
              // Email existence check with auto-retry
              let existingEmailsResponse = null;
              let existingEmails = [];
              let newEmails = importedEmails;
              let emailCheckSuccess = false;
              let emailRetryCount = 0;
              
              while (emailRetryCount < MAX_RETRIES && !emailCheckSuccess) {
                try {
                  if (emailRetryCount > 0) {
                    const exponentialDelay = BASE_DELAY * Math.pow(2, emailRetryCount - 1); // 1s, 2s, 4s
                    console.log(`🔄 Retry attempt ${emailRetryCount}/${MAX_RETRIES} for email existence check...`);
                    console.log(`⏳ Waiting ${exponentialDelay}ms before retry (exponential backoff)...`);
                    await new Promise(resolve => setTimeout(resolve, exponentialDelay));
                  }
                  
                  existingEmailsResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/checkExistingEmails`, {
                  hubkey,
                  emails: importedEmails
                });
                
                  existingEmails = existingEmailsResponse.data.existingEmails || [];
                  newEmails = importedEmails.filter(email => !existingEmails.includes(email.toLowerCase()));
                  emailCheckSuccess = true;
                  if (emailRetryCount > 0) {
                    console.log(`✅ Email existence check successful after ${emailRetryCount} retry attempts`);
                    // Show a brief success message for retry attempts
                    Swal.fire({
                      title: 'Retry Successful',
                      text: `Email existence check succeeded after ${emailRetryCount} retry attempts.`,
                      icon: 'success',
                      timer: 2000,
                      showConfirmButton: false
                    });
                  } else {
                    console.log(`✅ Email existence check successful on first attempt`);
                  }
                  
                } catch (error) {
                  emailRetryCount++;
                  console.error(`❌ Email existence check attempt ${emailRetryCount} failed:`, error.message);
                  
                  if (emailRetryCount >= MAX_RETRIES) {
                    console.error(`❌ All ${MAX_RETRIES} retry attempts failed for email existence check`);
                    // Continue with import even if pre-import check fails
                    window.preImportData = {
                      new: importedEmails.length,
                      existing: 0,
                      newEmails: importedEmails,
                      existingEmails: []
                    };
                    
                    // Don't close the loading modal yet - keep it open
                    break;
                  }
                  
                  const nextDelay = BASE_DELAY * Math.pow(2, emailRetryCount); // Calculate next delay
                  console.log(`⏳ Waiting ${nextDelay}ms before email check retry (exponential backoff)...`);
                }
              }
              
              if (emailCheckSuccess) {
                console.log(`📊 Pre-import analysis: ${newEmails.length} new emails, ${existingEmails.length} existing emails`);
                console.log('🆕 New emails (not in HubSpot):', newEmails);
                console.log('🔄 Existing emails (already in HubSpot):', existingEmails);
                
                // Store pre-import data for the modal
                window.preImportData = {
                  new: newEmails.length,
                  existing: existingEmails.length,
                  newEmails: newEmails,
                  existingEmails: existingEmails
                };
                
                // Immediately apply current exclusions (if any were toggled just before)
                try {
                  if (window.excludedDealNames?.length || window.excludedProjectIds?.length) {
                    console.log('🔁 Applying existing exclusions to pre-import counts');
                    const { updatedProjectsData: filteredProjects, cleanContactData: filteredContacts } = applyExclusionsToImportData(updatedProjectsData, cleanContactData, window.excludedDealNames || []);

                    const emailToAllProjects = {};
                    filteredContacts.forEach(contact => {
                      if (contact.Email && contact["Project ID"]) {
                        const email = contact.Email.toLowerCase();
                        if (!emailToAllProjects[email]) emailToAllProjects[email] = new Set();
                        emailToAllProjects[email].add(contact["Project ID"]);
                      }
                    });

                    const excludedIds = new Set(window.excludedProjectIds || []);
                    const filteredNewEmails = newEmails.filter(email => {
                      const emailProjects = emailToAllProjects[email.toLowerCase()] || new Set();
                      const remainingProjects = [...emailProjects].filter(projectId => !excludedIds.has(projectId));
                      return remainingProjects.length > 0;
                    });
                    const filteredExistingEmails = existingEmails.filter(email => {
                      const emailProjects = emailToAllProjects[email.toLowerCase()] || new Set();
                      const remainingProjects = [...emailProjects].filter(projectId => !excludedIds.has(projectId));
                      return remainingProjects.length > 0;
                    });

                    window.preImportData = {
                      ...window.preImportData,
                      new: filteredNewEmails.length,
                      existing: filteredExistingEmails.length,
                      newEmails: filteredNewEmails,
                      existingEmails: filteredExistingEmails,
                    };
                    console.log('✅ Pre-import counts updated with current exclusions');
                  }
                } catch (e) {
                  console.warn('⚠️ Failed to apply exclusions immediately after pre-count, proceeding with base counts');
                }
              }
              
              // Function to update pre-import counts based on excluded projects
              const updatePreImportCounts = () => {
                const excludedIds = new Set(window.excludedProjectIds || []);
                const emailMapping = window.emailToProjectMapping || {};
                const preImportData = window.preImportData || { newEmails: [], existingEmails: [] };
                
                // Apply exclusions to get filtered data for accurate counts
                const { updatedProjectsData: filteredProjects, cleanContactData: filteredContacts } = applyExclusionsToImportData(updatedProjectsData, cleanContactData, window.excludedDealNames || []);
                
                // Create a mapping of emails to ALL their project IDs (not just the first one)
                const emailToAllProjects = {};
                filteredContacts.forEach(contact => {
                  if (contact.Email && contact["Project ID"]) {
                    const email = contact.Email.toLowerCase();
                    if (!emailToAllProjects[email]) {
                      emailToAllProjects[email] = new Set();
                    }
                    emailToAllProjects[email].add(contact["Project ID"]);
                  }
                });
                
                // Filter emails: only exclude if ALL their projects are excluded
                const filteredNewEmails = preImportData.newEmails.filter(email => {
                  const emailProjects = emailToAllProjects[email.toLowerCase()] || new Set();
                  const remainingProjects = [...emailProjects].filter(projectId => !excludedIds.has(projectId));
                  return remainingProjects.length > 0; // Keep if at least one project remains
                });
                
                const filteredExistingEmails = preImportData.existingEmails.filter(email => {
                  const emailProjects = emailToAllProjects[email.toLowerCase()] || new Set();
                  const remainingProjects = [...emailProjects].filter(projectId => !excludedIds.has(projectId));
                  return remainingProjects.length > 0; // Keep if at least one project remains
                });
                
                // Update the pre-import data with filtered counts
                window.preImportData = {
                  ...window.preImportData,
                  new: filteredNewEmails.length,
                  existing: filteredExistingEmails.length,
                  newEmails: filteredNewEmails,
                  existingEmails: filteredExistingEmails
                };
                
                console.log(`📊 Updated pre-import counts after exclusions: ${filteredNewEmails.length} new, ${filteredExistingEmails.length} existing`);
                console.log('🆕 Filtered new emails:', filteredNewEmails);
                console.log('🔄 Filtered existing emails:', filteredExistingEmails);
                console.log('🗺️ Email to all projects mapping:', emailToAllProjects);
              };
              
              // Step 3: Get contact counts for existing deals and show collision modal
              if (collisions.length > 0) {
                console.log("🎯 Getting contact counts for existing deals...");
                
                // Get contact counts for existing deals
                let dealContactCounts = {};
                // Compute incoming contacts per project from current (possibly excluded) contact data
                const incomingCountsByProjectId = (() => {
                  const map = {};
                  try {
                    (cleanContactData || []).forEach(row => {
                      const pid = row && row["Project ID"] != null ? String(row["Project ID"]) : "";
                      if (pid) map[pid] = (map[pid] || 0) + 1;
                    });
                  } catch (_) {}
                  return map;
                })();
                try {
                  const existingDealIds = collisions.map(collision => collision.existingDeal.id);
                  console.log(`📊 Fetching contact counts for ${existingDealIds.length} existing deals...`);
                  
                  const contactCountsResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getDealContactCounts`, {
                    hubkey,
                    dealIds: existingDealIds
                  });
                  
                  dealContactCounts = contactCountsResponse.data?.dealContactCounts || {};
                  console.log(`✅ Contact counts fetched:`, dealContactCounts);
                } catch (error) {
                  console.error(`❌ Error fetching contact counts:`, error.message);
                  // Continue without contact counts if the API call fails
                  dealContactCounts = {};
                }
                
                console.log("🎯 Showing collision resolution modal...");
                
                // Show collision resolution modal
                const result = await Swal.fire({
                  title: 'Project Collisions Detected',
                  html: `
                    <div class="text-left">
                      <p class="mb-4">Found <strong>${collisions.length}</strong> projects that already exist in HubSpot:</p>
                      <div class="mb-3">
                        <a href="https://www.loom.com/share/3bf920ed52c94e99995036c2d202d7ca?sid=b32e1158-f2a8-49a7-9be1-ed605f6c1d67" target="_blank" rel="noopener noreferrer" class="inline-flex items-center px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs">
                          Watch Explanation
                        </a>
                      </div>
                      ${(() => {
                        const totalContacts = Object.values(dealContactCounts).reduce((sum, count) => sum + count, 0);
                        const dealsAtLimit = Object.values(dealContactCounts).filter(count => count >= 1000).length;
                        const dealsNearLimit = Object.values(dealContactCounts).filter(count => count >= 900 && count < 1000).length;
                        
                        let summaryHtml = '';
                        if (totalContacts > 0) {
                          summaryHtml += `<div class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p class="text-sm text-blue-800"><strong>📊 Contact Summary:</strong> Total of <strong>${totalContacts}</strong> contacts across all existing deals</p>`;
                          
                          if (dealsAtLimit > 0) {
                            summaryHtml += `<p class="text-sm text-red-600 mt-1"><strong>Warning:</strong> ${dealsAtLimit} deal${dealsAtLimit !== 1 ? 's' : ''} at HubSpot's 1000 contact limit</p>`;
                          }
                          
                          if (dealsNearLimit > 0) {
                            summaryHtml += `<p class="text-sm text-yellow-600 mt-1"><strong>⚠️ Caution:</strong> ${dealsNearLimit} deal${dealsNearLimit !== 1 ? 's' : ''} approaching the 1000 contact limit</p>`;
                          }
                          
                          summaryHtml += `</div>`;
                        }
                        return summaryHtml;
                      })()}
                      <div class="bg-gray-100 p-4 rounded text-sm max-h-60 overflow-y-auto">
                        ${collisions.map((collision, index) => {
                          const isDuplicateId = projectIdCounts[collision.projectId] > 1;
                          const excludeButton = selectedSource === "Lead List" ? '' : `<button data-dealname="${collision.projectTitle}" class="exclude-btn absolute top-1 right-1 w-6 h-6 rounded-full text-gray-400 hover:text-red-500 font-bold text-lg flex items-center justify-center hover:bg-gray-200">×</button>`;
                          const contactCount = dealContactCounts[collision.existingDeal.id] || 0;
                          const incomingCount = incomingCountsByProjectId[String(collision.projectId)] || 0;
                          const contactCountDisplay = contactCount > 0 ? 
                            `<span class="text-xs text-blue-600 font-medium">📧 ${contactCount} associated contact${contactCount !== 1 ? 's' : ''}</span>` : 
                            `<span class="text-xs text-gray-500">📧 No associated contacts</span>`;
                          const hubspotLimitWarning = contactCount >= 1000 ? 
                            `<span class="text-xs text-red-600 font-bold">⚠️ HubSpot limit: 1000 contacts per deal</span>` : '';
                          
                          return `<div id="col-${collision.projectTitle}" class="relative mb-3 p-2 bg-white rounded hover:bg-gray-100 transition-colors">
                            ${excludeButton}
                            <strong>${index + 1}. ${collision.projectTitle}</strong> (ID: ${collision.projectId})<br>
                            <span class="text-gray-600">⚠️ ${collision.reason || 'Already exists in HubSpot'}</span><br>
                            <span class="text-xs text-gray-500">Existing: ${collision.existingDeal?.properties?.dealname || 'Unknown'}</span><br>
                            ${contactCountDisplay}<br>
                            <span class="text-xs text-green-600 font-medium">⬇️ ${incomingCount} incoming contact${incomingCount !== 1 ? 's' : ''} from file</span><br>
                            ${hubspotLimitWarning}
                          </div>`;
                        }).join('')}
                      </div>
                      <p class="mt-4 text-sm text-gray-600">⚠️ <strong>Please choose an action for each project below:</strong></p>
                      <ul class="text-xs text-gray-500 mt-2 list-disc list-inside">
                        <li><strong>Update Existing:</strong> Replace existing HubSpot deal with new data</li>
                        <li><strong>Create New:</strong> Create new deal with different ID</li>
                        <li><strong>Proceed:</strong> Continue importing all deals that don't have collisions</li>

                      </ul>
                      <div class="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        <strong>ℹ️ Note:</strong> HubSpot limits each deal to a maximum of 1,000 associated contacts. 
                        If you're approaching this limit, consider creating a new deal instead of updating the existing one.
                      </div>
                    </div>
                  `,
                  icon: 'warning',
                  showCancelButton: true,
                  showDenyButton: true,
                  confirmButtonColor: '#22c55e', // green-500
                  cancelButtonColor: '#6b7280',
                  denyButtonColor: '#dc2626',
                  confirmButtonText: 'Create New',
                  denyButtonText: 'Update Existing',
                  cancelButtonText: 'Cancel',
                  width: '800px',
                  showConfirmButton: selectedSource !== "ConstructConnect", // Show Create New button for all sources except ConstructConnect
                                                          didOpen: () => {
                      window.excludedDealNames = [];
                      window.projectActions = {}; // Store actions for each project
                      
                      // Initialize empty actions - user must choose
                      collisions.forEach(collision => {
                        window.projectActions[collision.projectId] = '';
                      });
                      
                      // Create collision tracking variable
                      function getCollisionCounts() {
                        const collisionCounts = {};
                        
                        // Group collisions by project ID
                        collisions.forEach(collision => {
                          if (!collisionCounts[collision.projectId]) {
                            collisionCounts[collision.projectId] = {
                              total: 0,
                              nonExcluded: 0,
                              excluded: 0
                            };
                          }
                          collisionCounts[collision.projectId].total++;
                          
                          if (window.excludedDealNames.includes(collision.projectTitle)) {
                            collisionCounts[collision.projectId].excluded++;
                          } else {
                            collisionCounts[collision.projectId].nonExcluded++;
                          }
                        });
                        
                        return collisionCounts;
                      }
                      
                      // Helper function to check if Update Existing should be disabled
                      function shouldDisableUpdateExisting() {
                        const collisionCounts = getCollisionCounts();
                        
                        // If all collisions are excluded, disable Update Existing
                        if (window.excludedDealNames.length === collisions.length) {
                          return true; // Disable Update Existing
                        }
                        
                        // Check if any project ID has multiple non-excluded collisions
                        for (const projectId in collisionCounts) {
                          if (collisionCounts[projectId].nonExcluded > 1) {
                            return true; // Disable Update Existing
                          }
                        }
                        return false; // Enable Update Existing
                      }
                      
                      // Helper function to get project IDs with multiple collisions
                      function getProjectIdsWithMultipleCollisions() {
                        const collisionCounts = getCollisionCounts();
                        const projectIds = [];
                        
                        for (const projectId in collisionCounts) {
                          if (collisionCounts[projectId].nonExcluded > 1) {
                            projectIds.push(projectId);
                          }
                        }
                        
                        return projectIds;
                      }
                      
                      // Helper function to check if Create New should be disabled
                      function shouldDisableCreateNew() {
                        // If all collisions are excluded, disable Create New
                        return window.excludedDealNames.length === collisions.length;
                      }
                      
                      // Helper function to check if Proceed should be disabled
                      function shouldDisableProceed() {
                        // Only enable if all collisions are excluded
                        return window.excludedDealNames.length !== collisions.length;
                      }
                    
                    // Handle exclude buttons
                    document.querySelectorAll('.exclude-btn').forEach(btn => {
                      btn.addEventListener('click', () => {
                        const dealname = btn.dataset.dealname;
                        const row = document.getElementById(`col-${dealname}`);
                        if (!row) return;
                        const isExcluded = row.classList.toggle('opacity-50');
                        row.classList.toggle('bg-gray-200', isExcluded);
                        if (isExcluded) {
                          window.excludedDealNames.push(dealname);
                          console.log(`⏭️ EXCLUDED: Deal "${dealname}" - added to exclusion list`);
                        } else {
                          window.excludedDealNames = window.excludedDealNames.filter(name => name !== dealname);
                          console.log(`✅ INCLUDED: Deal "${dealname}" - removed from exclusion list`);
                        }
                        
                        // Update pre-import counts based on exclusions
                        updatePreImportCounts();
                        
                        // Update Update Existing button state
                        const updateExistingBtn = document.querySelector('.swal2-deny') || 
                                                 document.querySelector('[aria-label="Update Existing"]') ||
                                                 document.querySelector('.swal2-styled[style*="background-color: rgb(220, 38, 38)"]');
                        if (updateExistingBtn) {
                          const shouldDisable = shouldDisableUpdateExisting();
                          updateExistingBtn.disabled = shouldDisable;
                          updateExistingBtn.style.backgroundColor = shouldDisable ? '#d1d5db' : '#dc2626';
                          updateExistingBtn.style.color = shouldDisable ? '#6b7280' : 'white';
                          
                          // Add or update tooltip
                          let tooltip = document.querySelector('.update-existing-tooltip');
                          if (shouldDisable && !tooltip) {
                            const projectIds = getProjectIdsWithMultipleCollisions();
                            tooltip = document.createElement('div');
                            tooltip.className = 'update-existing-tooltip text-xs text-red-500 mt-2';
                            tooltip.textContent = `Update Existing is disabled because these project IDs appear more than once in your import: [${projectIds.join(', ')}]`;
                            tooltip.style.display = 'none'; // Hidden by default
                            updateExistingBtn.parentNode.appendChild(tooltip);
                            // Show tooltip on hover
                            updateExistingBtn.addEventListener('mouseenter', () => {
                              tooltip.style.display = 'block';
                            });
                            updateExistingBtn.addEventListener('mouseleave', () => {
                              tooltip.style.display = 'none';
                            });
                          } else if (shouldDisable && tooltip) {
                            // Update existing tooltip with current project IDs
                            const projectIds = getProjectIdsWithMultipleCollisions();
                            tooltip.textContent = `Update Existing is disabled because these project IDs appear more than once in your import: [${projectIds.join(', ')}]`;
                          } else if (!shouldDisable && tooltip) {
                            tooltip.remove();
                          }
                        } else {
                          console.log('Update Existing button not found during exclude');
                        }
                        
                        // Update Create New button state
                        const createNewBtn = document.querySelector('.swal2-confirm') || 
                                           document.querySelector('[aria-label="Create New"]') ||
                                           document.querySelector('.swal2-styled[style*="background-color: rgb(34, 197, 94)"]');
                        console.log('Updating Create New button, found:', createNewBtn);
                        if (createNewBtn) {
                          const shouldDisable = shouldDisableCreateNew();
                          console.log('Should disable Create New (after exclude):', shouldDisable);
                          createNewBtn.disabled = shouldDisable;
                          createNewBtn.style.backgroundColor = shouldDisable ? '#d1d5db' : '#22c55e';
                          createNewBtn.style.color = shouldDisable ? '#6b7280' : 'white';
                          
                          // Add or update tooltip for Create New
                          let createNewTooltip = document.querySelector('.create-new-tooltip');
                          if (shouldDisable && !createNewTooltip) {
                            createNewTooltip = document.createElement('div');
                            createNewTooltip.className = 'create-new-tooltip text-xs text-red-500 mt-2';
                            createNewTooltip.textContent = 'Create New is disabled because all collisions are excluded.';
                            createNewTooltip.style.display = 'none';
                            createNewBtn.parentNode.appendChild(createNewTooltip);
                            createNewBtn.addEventListener('mouseenter', () => {
                              createNewTooltip.style.display = 'block';
                            });
                            createNewBtn.addEventListener('mouseleave', () => {
                              createNewTooltip.style.display = 'none';
                            });
                          } else if (!shouldDisable && createNewTooltip) {
                            createNewTooltip.remove();
                          }
                        } else {
                          console.log('Create New button not found during exclude');
                        }
                        
                        // Update Proceed button state
                        const proceedBtn = document.getElementById('proceed-import-btn');
                        if (proceedBtn) {
                          const shouldDisable = shouldDisableProceed();
                          proceedBtn.disabled = shouldDisable;
                          proceedBtn.style.backgroundColor = shouldDisable ? '#d1d5db' : '#22c55e';
                          proceedBtn.style.color = shouldDisable ? '#6b7280' : 'white';
                          // Add or update tooltip for Proceed
                          let proceedTooltip = document.querySelector('.proceed-tooltip');
                          if (shouldDisable && !proceedTooltip) {
                            proceedTooltip = document.createElement('div');
                            proceedTooltip.className = 'proceed-tooltip text-xs text-red-500 mt-2';
                            proceedTooltip.textContent = 'Proceed is disabled. You must exclude all but one of each duplicate project ID before proceeding.';
                            proceedTooltip.style.display = 'none';
                            proceedBtn.parentNode.appendChild(proceedTooltip);
                            proceedBtn.addEventListener('mouseenter', () => {
                              proceedTooltip.style.display = 'block';
                            });
                            proceedBtn.addEventListener('mouseleave', () => {
                              proceedTooltip.style.display = 'none';
                            });
                          } else if (!shouldDisable && proceedTooltip) {
                            proceedTooltip.remove();
                          }
                        }
                      });
                    });
                    
                    // Handle action dropdowns
                    document.querySelectorAll('.action-select-btn').forEach(btn => {
                      btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const pid = btn.dataset.pid;
                        const dropdown = document.getElementById(`dropdown-menu-${pid}`);
                        
                        // Close all other dropdowns
                        document.querySelectorAll('.action-dropdown').forEach(d => {
                          if (d !== dropdown) d.classList.add('hidden');
                        });
                        
                        // Toggle current dropdown
                        dropdown.classList.toggle('hidden');
                      });
                    });
                    
                    // Handle action options
                    document.querySelectorAll('.action-option').forEach(option => {
                      option.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const pid = option.dataset.pid;
                        const action = option.dataset.action;
                        
                        // Update the action text
                        const actionText = document.querySelector(`.action-text-${pid}`);
                        if (actionText) {
                          actionText.textContent = option.textContent;
                        }
                        
                        // Store the action
                        window.projectActions[pid] = action;
                        
                        // Close dropdown
                        const dropdown = document.getElementById(`dropdown-menu-${pid}`);
                        dropdown.classList.add('hidden');
                        
                        console.log(`🎯 Project ${pid} action set to: ${action}`);
                        
                        // Update proceed button state
                        const proceedBtn = document.getElementById('proceed-import-btn');
                        if (proceedBtn) {
                          const shouldDisable = shouldDisableProceed();
                          proceedBtn.disabled = shouldDisable;
                          proceedBtn.style.backgroundColor = shouldDisable ? '#d1d5db' : '#22c55e';
                          proceedBtn.style.color = shouldDisable ? '#6b7280' : 'white';
                          // Add or update tooltip for Proceed
                          let proceedTooltip = document.querySelector('.proceed-tooltip');
                          if (shouldDisable && !proceedTooltip) {
                            proceedTooltip = document.createElement('div');
                            proceedTooltip.className = 'proceed-tooltip text-xs text-red-500 mt-2';
                            proceedTooltip.textContent = 'Proceed is disabled. You must exclude all but one of each duplicate project ID before proceeding.';
                            proceedTooltip.style.display = 'none';
                            proceedBtn.parentNode.appendChild(proceedTooltip);
                            proceedBtn.addEventListener('mouseenter', () => {
                              proceedTooltip.style.display = 'block';
                            });
                            proceedBtn.addEventListener('mouseleave', () => {
                              proceedTooltip.style.display = 'none';
                            });
                          } else if (!shouldDisable && proceedTooltip) {
                            proceedTooltip.remove();
                          }
                        }
                        
                        // Also update Update Existing button state when actions change
                        const updateExistingBtn = document.querySelector('.swal2-deny') || 
                                                 document.querySelector('[aria-label="Update Existing"]') ||
                                                 document.querySelector('.swal2-styled[style*="background-color: rgb(220, 38, 38)"]');
                        if (updateExistingBtn) {
                          const shouldDisable = shouldDisableUpdateExisting();
                          updateExistingBtn.disabled = shouldDisable;
                          updateExistingBtn.style.backgroundColor = shouldDisable ? '#d1d5db' : '#dc2626';
                          updateExistingBtn.style.color = shouldDisable ? '#6b7280' : 'white';
                          
                          // Add or update tooltip
                          let tooltip = document.querySelector('.update-existing-tooltip');
                          if (shouldDisable && !tooltip) {
                            const projectIds = getProjectIdsWithMultipleCollisions();
                            tooltip = document.createElement('div');
                            tooltip.className = 'update-existing-tooltip text-xs text-red-500 mt-2';
                            tooltip.textContent = `Update Existing is disabled because these project IDs appear more than once in your import: [${projectIds.join(', ')}]`;
                            tooltip.style.display = 'none'; // Hidden by default
                            updateExistingBtn.parentNode.appendChild(tooltip);
                            // Show tooltip on hover
                            updateExistingBtn.addEventListener('mouseenter', () => {
                              tooltip.style.display = 'block';
                            });
                            updateExistingBtn.addEventListener('mouseleave', () => {
                              tooltip.style.display = 'none';
                            });
                          } else if (shouldDisable && tooltip) {
                            // Update existing tooltip with current project IDs
                            const projectIds = getProjectIdsWithMultipleCollisions();
                            tooltip.textContent = `Update Existing is disabled because these project IDs appear more than once in your import: [${projectIds.join(', ')}]`;
                          } else if (!shouldDisable && tooltip) {
                            tooltip.remove();
                          }
                        } else {
                          console.log('Update Existing button not found during action change');
                        }
                      });
                    });
                    
                    // Close dropdowns when clicking outside
                    document.addEventListener('click', () => {
                      document.querySelectorAll('.action-dropdown').forEach(d => d.classList.add('hidden'));
                    });
                    // Add Proceed button logic (only for sources with multiple deal names)
                    if (selectedSource !== "Lead List") {
                      const modalFooter = document.querySelector('.swal2-actions');
                      if (modalFooter && !document.getElementById('proceed-import-btn')) {
                        const proceedBtn = document.createElement('button');
                        proceedBtn.id = 'proceed-import-btn';
                        proceedBtn.textContent = 'Proceed';
                        proceedBtn.className = 'swal2-styled swal2-cancel';
                        
                        // Check if all collisions are excluded to determine initial state
                        proceedBtn.disabled = collisions.length > 0; // Disabled by default if there are collisions
                        
                        proceedBtn.style.marginLeft = '8px';
                        proceedBtn.style.backgroundColor = proceedBtn.disabled ? '#d1d5db' : '#22c55e';
                        proceedBtn.style.color = proceedBtn.disabled ? '#6b7280' : 'white';
                        proceedBtn.style.borderradius ='.25em';
                        proceedBtn.onclick = () => {
                          console.log("🟢 Proceed button clicked - setting window.proceedWithExcluded = true");
                          window.proceedWithExcluded = true;
                          console.log("🟢 About to close modal with Swal.close()");
                          Swal.close();
                          console.log("🟢 Modal closed");
                        };
                        modalFooter.appendChild(proceedBtn);
                      }
                    }
                    
                    // Set initial state for Update Existing button
                    setTimeout(() => {
                      // Try different selectors to find the Update Existing button
                      const updateExistingBtn = document.querySelector('.swal2-deny') || 
                                               document.querySelector('[aria-label="Update Existing"]') ||
                                               document.querySelector('.swal2-styled[style*="background-color: rgb(220, 38, 38)"]');
                      if (updateExistingBtn) {
                        const shouldDisable = shouldDisableUpdateExisting();
                        updateExistingBtn.disabled = shouldDisable;
                        updateExistingBtn.style.backgroundColor = shouldDisable ? '#d1d5db' : '#dc2626';
                        updateExistingBtn.style.color = shouldDisable ? '#6b7280' : 'white';
                        
                        // Add tooltip if disabled
                        if (shouldDisable) {
                          const projectIds = getProjectIdsWithMultipleCollisions();
                          const tooltip = document.createElement('div');
                          tooltip.className = 'update-existing-tooltip text-xs text-red-500 mt-2';
                          tooltip.textContent = `Update Existing is disabled because these project IDs appear more than once in your import: [${projectIds.join(', ')}]`;
                          tooltip.style.display = 'none'; // Hidden by default
                          updateExistingBtn.parentNode.appendChild(tooltip);
                          // Show tooltip on hover
                          updateExistingBtn.addEventListener('mouseenter', () => {
                            tooltip.style.display = 'block';
                          });
                          updateExistingBtn.addEventListener('mouseleave', () => {
                            tooltip.style.display = 'none';
                          });
                        }
                      } else {
                        console.log('Update Existing button not found');
                      }
                    }, 100);
                  }
                });
                
                // Step 4: Store original data before exclusions (we'll apply exclusions only when user chooses to proceed)
                const originalProjectsData = [...updatedProjectsData];
                const originalContactData = [...cleanContactData];
                
                console.log("🔍 [importFile] Original data - Projects:", originalProjectsData.length, "Contacts:", originalContactData.length);
                console.log("🔍 [importFile] Excluded deal names:", window.excludedDealNames || []);
                
                // Step 5: Handle user choice based on individual project actions (only for non-excluded data)
                console.log("🔍 [Collision Resolution] Debug info:");
                console.log("  - window.proceedWithExcluded:", window.proceedWithExcluded);
                console.log("  - result.isDismissed:", result.isDismissed);
                console.log("  - result.isConfirmed:", result.isConfirmed);
                console.log("  - result.isDenied:", result.isDenied);
                
                // Reset the flag first to prevent persistence issues
                const shouldProceedWithExcluded = window.proceedWithExcluded;
                window.proceedWithExcluded = false;
                
                if (shouldProceedWithExcluded) {
                  console.log("✅ User chose to proceed with only non-colliding projects");
                  
                  // Apply exclusions only when user chooses to proceed
                  const { updatedProjectsData: filteredProjectsData, cleanContactData: filteredContactData } = applyExclusionsToImportData(originalProjectsData, originalContactData, window.excludedDealNames || []);
                  updatedProjectsData = filteredProjectsData;
                  cleanContactData = filteredContactData;
                  
                  console.log("🔍 [importFile] After exclusions - Projects:", updatedProjectsData.length, "Contacts:", cleanContactData.length);
                  
                  // Check if all data was excluded
                  if (updatedProjectsData.length === 0 || cleanContactData.length === 0) {
                    await Swal.fire({
                      title: 'Cannot Import',
                      text: 'There are no projects or contacts left to import after exclusions. Please check your file or exclusion settings.',
                      icon: 'error',
                      confirmButtonColor: '#dc2626'
                    });
                    dataLoadingModal.close();
                    setCurrentLoadingModal(null);
                    setLoading(false);
                    return;
                  }
                  
                  // Continue with import for non-colliding projects
                } else if (result.isDismissed) {
                  console.log("❌ User cancelled import (Cancel button clicked)");
                  userCancelledImport = true;
                  dataLoadingModal.close();
                  setCurrentLoadingModal(null);
                  setLoading(false);
                  return;
                } else if (result.isConfirmed || result.isDenied) {
                  // Show loading spinner immediately after collision decision
                  dataLoadingModal = Swal.fire({
                    title: 'Processing Import...',
                    html: `
                      <div class="text-center">
                        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p class="text-gray-600">Processing collision resolution and continuing import...</p>
                        <p class="text-sm text-gray-500 mt-2">This may take a few moments</p>
                        <div class="mt-4">
                          <div class="w-full bg-gray-200 rounded-full h-2.5">
                            <div class="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style="width: ${importProgress}%"></div>
                          </div>
                          <p class="text-xs text-gray-500 mt-2">${progressMessage || 'Initializing...'}</p>
                        </div>
                      </div>
                    `,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                      Swal.showLoading();
                    }
                  });
                  
                  // Store the modal reference for progress updates
                  setCurrentLoadingModal(dataLoadingModal);

                  // If Update Existing was clicked, set all non-excluded collisions to 'update'
                  if (result.isDenied) {
                    console.log("🔄 User clicked Update Existing - setting all non-excluded collisions to 'update'");
                    collisions.forEach(collision => {
                      if (!window.excludedDealNames.includes(collision.projectTitle)) {
                        window.projectActions[collision.projectId] = 'update';
                      }
                    });
                  }

                  // Always apply exclusions to the working data set when user continues via Create New or Update Existing
                  try {
                    const { updatedProjectsData: filteredProjectsData, cleanContactData: filteredContactData } = applyExclusionsToImportData(updatedProjectsData, cleanContactData, window.excludedDealNames || []);
                    updatedProjectsData = filteredProjectsData;
                    cleanContactData = filteredContactData;
                    console.log("✅ Applied exclusions before processing actions - Projects:", updatedProjectsData.length, "Contacts:", cleanContactData.length);
                  } catch (e) {
                    console.warn("⚠️ Failed to apply exclusions before processing actions:", e?.message || e);
                  }
                  console.log("🎯 Processing collision resolution based on individual project actions");
                  console.log("📊 Project actions:", window.projectActions);
                  
                  // Initialize project ID mapping
                  window.projectIdMapping = {};
                  
                  // Process each collision based on its individual action
                  for (const collision of collisions) {
                    const projectId = collision.projectId;
                    let action = window.projectActions[projectId];
                    
                    // Skip excluded collisions entirely (whether using Proceed or not)
                    if (window.excludedDealNames.includes(collision.projectTitle)) {
                      console.log(`⏭️ Skipping excluded collision: ${collision.projectTitle}`);
                      continue;
                    }
                    
                    // For ConstructConnect, if no action is selected, don't process anything (preserve original IDs)
                    if (!action && selectedSource === "ConstructConnect") {
                      console.log(`🔒 ConstructConnect: No action selected for project ${projectId}, preserving original ID`);
                      continue; // Skip processing this collision entirely
                    } else if (!action) {
                      action = 'create'; // Default to create for other sources
                    }
                    
                    console.log(`🔄 Processing project ${projectId} with action: ${action}`);
                    
                    if (action === 'update') {
                      // Update existing deal
                      console.log(`📝 Updating existing deal for project ${projectId}`);
                      // Find the existing deal and use its project ID
                      const existingDeal = collision.existingDeal;
                      if (existingDeal && existingDeal.properties && existingDeal.properties.project_id) {
                        window.projectIdMapping[projectId] = existingDeal.properties.project_id;
                        console.log(`📝 Mapped project ${projectId} to existing deal ID: ${existingDeal.properties.project_id}`);
                      }
                    } else if (action === 'create') {
                      // Create new deal with new ID and incremented deal name
                      console.log(`🆕 Creating new deal for project ${projectId}`);
                      if (selectedSource === "ConstructConnect") {
                        // For ConstructConnect, preserve the original project ID
                        console.log(`🔒 ConstructConnect: Preserving original project ID ${projectId}`);
                        window.projectIdMapping[projectId] = projectId; // Map to itself (no change)
                      } else {
                        // For other sources, generate new incremented deal name and new project ID
                        // Extract the base title (everything except the final _ID)
                        const titleParts = collision.projectTitle.split('_');
                        const baseTitle = titleParts.slice(0, -1).join('_');
                        
                        console.log(`🔍 Checking for existing deals with base title: "${baseTitle}"`);
                        
                        // Get all existing deals for this base title from HubSpot
                        const allExistingDealsResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
                          hubkey,
                          dealNames: [baseTitle]
                        });
                        
                        console.log(`📊 Found ${allExistingDealsResponse.data?.total || 0} existing deals for base title "${baseTitle}"`);
                        const allExistingTitles = (allExistingDealsResponse.data?.results || []).map(
                          d => d.properties?.dealname || ''
                        );
                        console.log(`📋 All existing titles found:`, allExistingTitles);
                        
                        // Find all increments for this base title from HubSpot deals
                        let maxN = 1;
                        const regex = new RegExp(`^${baseTitle.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}(?: \\((\\d+)\\))?(_\\d+)?$`);
                        
                        allExistingTitles.forEach(title => {
                          const match = title.match(regex);
                          if (match) {
                            const n = match[1] ? parseInt(match[1], 10) : 1;
                            if (n >= maxN) maxN = n + 1;
                          }
                        });
                        
                        const nextDealName = maxN === 1 ? baseTitle : `${baseTitle} (${maxN})`;
                        console.log(`✅ Next available name for "${baseTitle}": "${nextDealName}"`);
                        
                        const newProjectId = await generateNumericHashWithCollisionDetection(nextDealName);
                        window.projectIdMapping[projectId] = newProjectId;
                        // Store the new deal name mapping for later update
                        if (!window.projectDealNameMapping) window.projectDealNameMapping = {};
                        window.projectDealNameMapping[projectId] = nextDealName;
                        console.log(`🆕 New deal name for project ${projectId}: ${nextDealName}`);
                      }
                    }
                  }
                  
                  // 🔹 APPLY PROJECT ID MAPPINGS (for both update and create actions)
                  if (window.projectIdMapping && Object.keys(window.projectIdMapping).length > 0) {
                    console.log("🔄 Applying project ID mappings:", window.projectIdMapping);
                    
                    // Update project data with mapped IDs
                    updatedProjectsData = updatedProjectsData.map(project => {
                      const originalProjectId = project["Project ID"];
                      const mappedId = window.projectIdMapping[originalProjectId];
                      const mappedDealName = window.projectDealNameMapping && window.projectDealNameMapping[originalProjectId];
                      if (mappedId) {
                        console.log(`🔄 Updating project data: ${originalProjectId} → ${mappedId}`);
                        return {
                          ...project,
                          "Project ID": mappedId,
                          "Dealname": mappedDealName ? `${mappedDealName}_${mappedId}` : `${project["Dealname"].split('_').slice(0, -1).join('_')}_${mappedId}`
                        };
                      }
                      return project;
                    });
                    
                    // Update contact data with mapped IDs
                    cleanContactData = cleanContactData.map(contact => {
                      const originalProjectId = contact["Project ID"];
                      const mappedId = window.projectIdMapping[originalProjectId];
                      const mappedDealName = window.projectDealNameMapping && window.projectDealNameMapping[originalProjectId];
                      if (mappedId) {
                        console.log(`👥 Updating contact data: ${originalProjectId} → ${mappedId}`);
                        return {
                          ...contact,
                          "Project ID": mappedId,
                          "Project Title": mappedDealName ? `${mappedDealName}_${mappedId}` : `${contact["Project Title"].split('_').slice(0, -1).join('_')}_${mappedId}`
                        };
                      }
                      return contact;
                    });
                    
                    console.log("✅ Project ID mappings applied");
                    console.log("📊 Updated projects data:", updatedProjectsData);
                    console.log("📊 Updated contacts data sample:", cleanContactData.slice(0, 2));
                  }
                  
                  if (result.isConfirmed && selectedSource !== "ConstructConnect") {
                    console.log("📊 Original projects data:", updatedProjectsData);
                    
                    // Only process projects that have 'create' action
                    const projectsToIncrement = updatedProjectsData.filter(project => {
                      const projectId = project["Project ID"];
                      return window.projectActions[projectId] === 'create';
                    });
                    
                    if (projectsToIncrement.length > 0) {
                      console.log(`🔍 Found ${projectsToIncrement.length} projects to increment`);
                      
                      // Collect all unique base titles from the projects that need incrementing
                      const uniqueBaseTitles = [...new Set(projectsToIncrement.map(project => {
                        const titleParts = project["Dealname"].split('_');
                        return titleParts.slice(0, -1).join('_');
                      }))];
                      
                      console.log(`🔍 Collecting unique base titles:`, uniqueBaseTitles);
                      
                      // Single API call to get all existing deals for all base titles
                      const allExistingDealsResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
                        hubkey,
                        dealNames: uniqueBaseTitles
                      });
                      
                      console.log(`📊 Found ${allExistingDealsResponse.data?.total || 0} existing deals for all base titles`);
                      const allExistingTitles = (allExistingDealsResponse.data?.results || []).map(
                        d => d.properties?.dealname || ''
                      );
                      console.log(`📋 All existing titles found:`, allExistingTitles);
                      
                      // Process each project to create new names and IDs
                      for (let i = 0; i < updatedProjectsData.length; i++) {
                        const project = updatedProjectsData[i];
                        const originalProjectId = project["Project ID"];
                        const originalDealName = project["Dealname"];
                        
                        // Only process if this project has 'create' action
                        if (window.projectActions[originalProjectId] !== 'create') {
                          console.log(`⏭️ Skipping project ${originalProjectId} - not marked for 'create'`);
                          continue;
                        }
                        
                        console.log(`🔄 Processing project ${i + 1}:`, { originalProjectId, originalDealName });
                        
                        // Extract the base title (everything except the final _ID)
                        const titleParts = originalDealName.split('_');
                        const baseTitle = titleParts.slice(0, -1).join('_'); // Everything except the last part
                        
                        console.log(`📝 Base title extracted: "${baseTitle}"`);
                        
                        // Find all increments for this base title from the comprehensive results
                        let maxN = 1;
                        const regex = new RegExp(`^${baseTitle.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}(?: \\((\\d+)\\))?(_\\d+)?$`);
                        
                        allExistingTitles.forEach(title => {
                          const match = title.match(regex);
                          if (match) {
                            const n = match[1] ? parseInt(match[1], 10) : 1;
                            if (n >= maxN) maxN = n + 1;
                          }
                        });
                        
                        // Compose the next available name
                        const nextDealName = maxN === 1 ? baseTitle : `${baseTitle} (${maxN})`;
                        console.log(`✅ Next available name for "${baseTitle}": "${nextDealName}"`);
                        
                        // Generate new project ID based on the new deal name
                        const newProjectId = await generateNumericHashWithCollisionDetection(nextDealName);
                        // Now create the final deal name with the new project ID
                        let fallbackTitle = project.originalTitle || "Unknown Project";
                        const newDealName = nextDealName ? `${nextDealName}_${newProjectId}` : `${fallbackTitle}_${newProjectId}`;
                        console.log(`🔄 Renaming: "${originalDealName}" → "${newDealName}"`);
                        console.log(`🆔 New ID: ${originalProjectId} → ${newProjectId}`);
                        
                        // Create mapping for contact updates
                        if (!window.projectIdMapping) window.projectIdMapping = {};
                        window.projectIdMapping[originalProjectId] = newProjectId;
                        console.log(`🗺️ Added to mapping: ${originalProjectId} → ${newProjectId}`);
                        
                        // Update the project data
                        updatedProjectsData[i] = {
                          ...project,
                          "Project ID": newProjectId,
                          "Dealname": newDealName
                        };
                        
                        let contactUpdates = 0;
                        cleanContactData = cleanContactData.map(contact => {
                          if (String(contact["Project ID"]) === String(originalProjectId)) {
                            contactUpdates++;
                            return {
                              ...contact,
                              "Project ID": newProjectId,
                              "Project Title": newDealName
                            };
                          }
                          return contact;
                        });
                        console.log(`👥 Updated ${contactUpdates} contacts for project ${originalProjectId} → ${newProjectId}`);
                      }
                      
                      console.log("✅ 'Create New' processing complete");
                      console.log("📊 Updated projects data:", updatedProjectsData);
                      console.log("📊 Updated contacts data sample:", cleanContactData.slice(0, 2));
                      
                      // Final pass to ensure all contacts have correct project IDs
                      if (window.projectIdMapping && Object.keys(window.projectIdMapping).length > 0) {
                        console.log("🔄 Final contact update with project ID mappings:", window.projectIdMapping);
                        console.log("📊 Contact data before final mapping:", cleanContactData.slice(0, 2));
                        cleanContactData = cleanContactData.map(contact => {
                          const mappedId = window.projectIdMapping[contact["Project ID"]];
                          if (mappedId) {
                            console.log(`📝 Final update: Contact project ID ${contact["Project ID"]} → ${mappedId}`);
                            return {
                              ...contact,
                              "Project ID": mappedId
                            };
                          }
                          return contact;
                        });
                        console.log("📊 Contact data after final mapping:", cleanContactData.slice(0, 2));
                        // Clear the mapping
                        window.projectIdMapping = {};
                      } else {
                        console.log("⚠️ No project ID mappings found - contacts may have mismatched IDs");
                      }
                    } else {
                      console.log("ℹ️ No projects marked for 'create' - skipping increment logic");
                    }
                  }
                  
                  // Continue with import - HubSpot will create new deals
                } else if (result.isDenied) {
                  console.log("🔄 User chose to update existing projects (Update Existing clicked)");
                  // 🔹 UPDATE EXISTING LOGIC - Update Project IDs to match existing deals
                  console.log("📊 Collisions data:", collisions);
                  console.log("📊 Original projects data:", updatedProjectsData);

                  // Create a mapping of new project IDs to existing deal project IDs by searching by deal name
                  const projectIdMapping = {};
                  for (const collision of collisions) {
                    const dealName = collision.projectTitle; // or collision.Dealname, adjust as needed
                    console.log(`[Update Existing] Attempting to update using dealName:`, dealName);
                    try {
                      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
                        hubkey,
                        dealName
                      });
                      const found = (response.data?.results || []).find(
                        d => d.properties?.dealname === dealName
                      );
                      if (found) {
                        projectIdMapping[collision.projectId] = found.properties.project_id;
                        console.log(`[Update Existing] Found existing deal for dealName '${dealName}':`, found.properties.project_id);
                      } else {
                        console.log(`[Update Existing] No existing deal found for dealName '${dealName}'`);
                      }
                    } catch (err) {
                      console.error("Error searching for deal by name:", err);
                    }
                  }

                  console.log("🔄 Project ID mapping:", projectIdMapping);

                  // Update project data to use existing deal IDs
                  updatedProjectsData = updatedProjectsData.map(project => {
                    const originalProjectId = project["Project ID"];
                    const existingDealId = projectIdMapping[originalProjectId];
                    if (existingDealId) {
                      console.log(`🔄 Updating project: ${originalProjectId} → ${existingDealId}`);
                      return {
                        ...project,
                        "Project ID": existingDealId,
                        "Dealname": `${project["Dealname"].split('_').slice(0, -1).join('_')}_${existingDealId}`
                      };
                    }
                    return project;
                  });

                  // Update corresponding contacts to use existing deal IDs
                  cleanContactData = cleanContactData.map(contact => {
                    const originalProjectId = contact["Project ID"];
                    const existingDealId = projectIdMapping[originalProjectId];
                    if (existingDealId) {
                      console.log(`👥 Updating contact project: ${originalProjectId} → ${existingDealId}`);
                      return {
                        ...contact,
                        "Project ID": existingDealId,
                        "Project Title": `${contact["Project Title"].split('_').slice(0, -1).join('_')}_${existingDealId}`
                      };
                    }
                    return contact;
                  });

                  console.log("✅ 'Update Existing' processing complete");
                  console.log("📊 Updated projects data:", updatedProjectsData);
                  console.log("📊 Updated contacts data sample:", cleanContactData.slice(0, 2));
                  // Continue with import - HubSpot will update existing deals
                } else {
                  console.log("❌ User cancelled import");
                  dataLoadingModal.close();
                  setLoading(false);
                  return;
                }
              } else {
                console.log("✅ No collisions found - proceeding with import");
              }
              
            } catch (error) {
              console.error("❌ Error during collision detection:", error);
              console.error("❌ Error response:", error.response?.data);
              
              // Close the loading modal first
              dataLoadingModal.close();
              
              // If collision detection fails after all retries, show error but continue with import
              const continueAnyway = await Swal.fire({
                title: 'Warning - Retries Failed',
                text: `Could not check for existing projects after ${MAX_RETRIES} retry attempts with exponential backoff (1s, 2s, 4s delays). This may be due to network issues, HubSpot API problems, or rate limiting. Proceed with import anyway?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#22c55e', // green-500
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Yes, Continue',
                cancelButtonText: 'Cancel Import'
              });
              
              if (!continueAnyway.isConfirmed) {
                console.log("❌ User cancelled import due to collision detection error");
                userCancelledImport = true;
                return;
              }
            }
            
            // Check if user cancelled during collision detection
            if (userCancelledImport) {
              console.log("❌ Import cancelled by user during collision detection");
              dataLoadingModal.close();
              setCurrentLoadingModal(null);
              setLoading(false);
              return;
            }
  
          // Close the loading modal now that all data fetching is complete
          dataLoadingModal.close();
          setCurrentLoadingModal(null);
  
          // Log final data before import
          console.log("🔍 [importFile] Final data before import - Projects:", updatedProjectsData.length, "Contacts:", cleanContactData.length);
          console.log("Updated Projects Data:", updatedProjectsData);
          console.log("Clean Contact Data:", cleanContactData);

          // 🔹 Use updated projects & companies data in blobs
          const contactColumns = Array.from(new Set([...(desiredColumns || []), 'Location']));
          const contactBlob = createCSVBlob(prepareHubSpotData(cleanContactData), contactColumns);
          // Rebuild companies from the post-exclusion contact set to keep CSVs in sync
          const updatedCompanyDataAfterExclusions = await initializeCompanyData(cleanContactData);
          const companyBlob = createCSVBlob(updatedCompanyDataAfterExclusions, desiredCompanyColumn);
          const contactBlob2 = createCSVBlob(cleanContactData, desiredForImport);
          const projectBlob = createCSVBlob(updatedProjectsData, [...desiredProjectColumns, "HubSpot Owner ID"]);
          console.log("SEND TO SERVER:", fileInfo.name, contactBlob, companyBlob, contactBlob2, projectBlob, toggleModal, setLoading, hubkey, dealStage);

          if (selectedSource === "Lead List") {
            // Use the updated contact data instead of creating new ones
            const contactsWithForcedID = cleanContactData.map(contact => ({
              ...contact,
              hubspotID: user?.hubspotID || null
            }));
            console.log("Hubspot ID: ",user.hubspotID);
            console.log("🔍 Contact data before mapToHubspotFields:", cleanContactData[0]);
            const mappedContacts = mapToHubspotFields(contactsWithForcedID, localNewProjectID, selectedSource);
            console.log("🔍 Contact data after mapToHubspotFields:", mappedContacts[0]);
            const contactBlob = createCSVBlob(mappedContacts, hubspotHeaders);
            const contactBlob2 = createCSVBlob(cleanContactData, desiredForImport);         
            const updatedCompanyDataAfterExclusions = await initializeCompanyData(cleanContactData);
            const companyBlob = createCSVBlob(updatedCompanyDataAfterExclusions, desiredCompanyColumn);
            // Use the updated project data instead of creating new one
            const projectBlob = createCSVBlob(updatedProjectsData, [...desiredProjectColumns, "HubSpot Owner ID"]);
            const presetName = selectedPreset === "New Preset" ? newPresetName.trim() || null : selectedPreset?.trim() || null;
            console.log("presetName", presetName);
            console.log("pipeline in send to server", pipeline);
            result = await sendToServer(fileInfo.name, contactBlob, companyBlob, contactBlob2, projectBlob, toggleModal, setLoading, hubkey, dealStage, presetName, pipeline);
            console.log("Result: ", result);
          } else{
            console.log("pipeline in send to server", pipeline);
            result = await sendToServer(fileInfo.name, contactBlob, companyBlob, contactBlob2, projectBlob, toggleModal, setLoading, hubkey, dealStage, null, pipeline);
            console.log("Result: ", result);
          }
        } else {
          console.log("Undefined Deal Stage or HubKey ID.");
        }

      if(invalidData.length > 0){
        console.log(`Found ${invalidData.length} invalid contacts`);
      }else{
        console.log("There are no invalid contacts");
      }

      setImportInProgress(true);

    } catch (error) {
        console.log(`Error processing files: ${error}`);
        // Close the loading modal if it exists
        if (typeof dataLoadingModal !== 'undefined' && dataLoadingModal) {
          dataLoadingModal.close();
        }
        setProcessingCollisions(false);
    }
  };
  
const initializeProjectsData = async (validContacts, leadProjectID, leadProjectTitle) => {
  const projectIDs = new Set(validContacts.map(contact => contact["Project ID"]));
  const uniqueProjects = {};
  const projectsArray = [];
  const pipeline = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.pipeline : selectedFranchisee?.client_type?.pipeline;

  // For lead lists, collect all unique ZIP codes if zipsColumn is mapped
  let allZips = new Set();
  if (selectedSource === "Lead List" && zipsColumn) {
    validContacts.forEach((contact) => {
      const zipValue = contact[zipsColumn] || contact["ZIP"];
      if (zipValue && zipValue.trim() !== "") {
        allZips.add(zipValue.trim());
      }
    });
  }

  validContacts.forEach((contact) => {
    const dealStage = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.pipelines?.[0]?.hubspot_stage_id : selectedFranchisee?.client_type?.pipelines?.[0]?.hubspot_stage_id;

    const projectID = selectedSource === "Lead List" ? leadProjectID : (contact["Project ID"] || "Unknown ID");

    // ✅ Updated condition: only skip for ConstructConnect or PlanHub if projectID isn't in the list
    if ((selectedSource === "ConstructConnect" || selectedSource === "PlanHub") && !projectIDs.has(projectID)) return;

    const projectTitle = contact["Project Title"] || "Unknown Project";
    const dealName = selectedSource === "Lead List" ? leadProjectTitle : `${projectTitle}_${projectID}`;

    if (!uniqueProjects[dealName]) {
      // For lead lists with ZIP column mapped, use all collected ZIPs; otherwise use individual contact ZIP
      const postalCode = selectedSource === "Lead List" && zipsColumn && allZips.size > 0 
        ? Array.from(allZips).join(", ")
        : (contact["ZIP"] || "");

      uniqueProjects[dealName] = {
        'Project ID': projectID,
        'Dealname': dealName,
        'Pipeline': pipeline,
        'Dealstage': dealStage, // initial deal stage
        'Description':
          selectedSource === "ConstructConnect"
            ? `Construct Connect Link: https://app.constructconnect.com/project/${contact["Project ID"]}/p?sourceType=3\nProject Title: ${contact["Project Title"]}\nProject Types: ${contact["Project Types"]}\nBuilding Uses: ${contact["Building Uses"]}\nProject Category: ${contact["Project Category"]}\nProject Description: ${contact["Project Description"]}\n`
            : `Project Title: ${contact["Project Title"] || leadProjectTitle}\nProject Types: ${contact["Project Types"] || "N/A"}`,
        'HubSpot Owner ID': user?.role === 'agent' ? user?.selectedFranchisee?.UserFranchiseeAssociation?.hubspotID : '69454485',
        'Postal Code': postalCode, 
      };
    }
  });

  for (const key in uniqueProjects) {
    projectsArray.push(uniqueProjects[key]);
  }

  return projectsArray;
};

const initializeCompanyData = async (validContacts) => {
  const companyNames = new Set(validContacts.map(contact => contact.Company));
  const uniqueCompanies = {};
  const companyArray = [];

  validContacts.forEach((data) => {
      const website = data.Website || data["Company URL"] || ""; // 🔹 Fix: Ensure website is taken correctly
      const domain = getDomainName(data.Email, website);
      const companyName = data.Company || "Unknown Company";

      if (!companyNames.has(companyName)) return;

      if (!uniqueCompanies[companyName]) {
          uniqueCompanies[companyName] = {
              Company: companyName,
              Website: website, // 🔹 Ensure Website is correctly assigned
              Domain: domain,
          };
      }
  });

  for (const key in uniqueCompanies) {
      companyArray.push(uniqueCompanies[key]);
  }

  return companyArray; // 🔹 Return filtered companies instead of using setCompanyData
};

  const headerMapping = {
    "Title": "Role",
    "Company URL": "Website",
    "Project Name": "Project Title",
    "Street": "Address",
    "Zip": "ZIP",
    "Project Type": "Project Types",
  };

  const usedHashes = new Set();

  // Simple deterministic hash for UI display only
  const generateSimpleHash = async (str) => {
    console.log("🔍 generateSimpleHash called with input:", str);
    
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    console.log("📝 Encoded data bytes:", Array.from(data));
    
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    console.log("🔐 SHA-256 hash buffer length:", hashBuffer.byteLength);
    
    const hashView = new DataView(hashBuffer);
    const rawBigInt = hashView.getBigUint64(0, false);
    console.log("🔢 Raw BigInt from hash:", rawBigInt.toString());
    
    // Generate a 7-digit number between 1000000 and 9999999
    let hashNum = Number((rawBigInt % 9000000n) + 1000000n);
    console.log("🎯 Calculated 7-digit hash number:", hashNum);
    
    console.log("✅ Final hash result:", hashNum);
    
    return hashNum;
  };

  // Simple hash generation without HubSpot API calls - used during file processing
  const generateNumericHash = async (str) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    
    const hashView = new DataView(hashBuffer);
    // Generate a 7-digit number between 1000000 and 9999999
    let hashNum = Number((hashView.getBigUint64(0, false) % 9000000n) + 1000000n);
    
    // For file processing, use local collision detection only
    if (selectedSource === "Project List" && addProjectIDRef.current) {
      // console.debug("[generateNumericHash] Project List with addProjectID - using local collision detection");
      let candidateId;
      let attempts = 0;
      const maxAttempts = 1000; // Prevent infinite loops
      
      do {
        candidateId = String(hashNum);
        if (!usedHashes.has(candidateId)) {
          usedHashes.add(candidateId);
          // console.debug(`[generateNumericHash] Generated unique ID: ${candidateId}`);
          return candidateId;
        }
        hashNum = (hashNum + 1) % 10000000;
        if (hashNum < 1000000) hashNum = 1000000;
        attempts++;
      } while (attempts < maxAttempts);
      
      // If we can't find a unique ID after max attempts, use timestamp-based fallback
      const timestamp = Date.now() % 9000000 + 1000000;
      const fallbackId = String(timestamp);
      // console.warn(`[generateNumericHash] Using fallback ID after ${maxAttempts} attempts: ${fallbackId}`);
      return fallbackId;
    }
    
    // For other sources during file processing, just return the hash without HubSpot checks
    const finalId = String(hashNum);
    // console.log(`✅ [generateNumericHash] Generated ID for file processing: ${finalId}`);
    return finalId;
  };

  // HubSpot collision detection - ONLY called during import
  const generateNumericHashWithCollisionDetection = async (str) => {
    
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    
    const hashView = new DataView(hashBuffer);
    // Generate a 7-digit number between 1000000 and 9999999
    let hashNum = Number((hashView.getBigUint64(0, false) % 9000000n) + 1000000n);
    
    // Check HubSpot for collisions during import
    try {
      const hubkey = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.hubspot_api_key : selectedFranchisee?.client_type?.hubspot_api_key;
      if (!hubkey) {
        console.error("No HubSpot API key available");
        return String(hashNum);
      }
      // Search for existing deal with same title using getAllDeals
      const searchResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
        hubkey: hubkey,
        dealName: str
      });
      if (searchResponse.data && searchResponse.data.total > 0) {
        // Deal exists - use its ID
        const existingDeal = searchResponse.data.results[0];
        const existingId = existingDeal.properties.project_id;
        if (existingId) {
          console.log("Found existing deal with ID:", existingId);
          return existingId;
        }
      }
      // No existing deal found - generate new unique ID
      let candidateId;
      do {
        candidateId = String(hashNum);
        // Check if ID exists in HubSpot
        const checkResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
          hubkey: hubkey,
          projectIDs: [candidateId]
        });
        if (!checkResponse.data || checkResponse.data.total === 0) {
          // ID is unique - use it
          break;
        }
        // ID exists - try next number
        hashNum = (hashNum + 1) % 10000000;
        if (hashNum < 1000000) hashNum = 1000000;
      } while (true);
      return candidateId;
    } catch (error) {
      console.error("Error checking HubSpot for existing deals:", error);
      // Fallback to local collision detection if HubSpot check fails
      if (usedHashes.has(hashNum)) {
        const altHashNum = Number((hashView.getBigUint64(8, false) % 9000000n) + 1000000n);
        if (!usedHashes.has(altHashNum)) {
          hashNum = altHashNum;
        } else {
          while (usedHashes.has(hashNum)) {
            hashNum = (hashNum + 1) % 10000000;
            if (hashNum < 1000000) hashNum = 1000000;
          }
        }
      }
      usedHashes.add(hashNum);
      return String(hashNum);
    }
  };

  const applyMappings = (data) => {
    return data.map((row, idx) => {
      let adjustedRow = {};
      
      // Always preserve Location that was injected earlier from franchisee selection
      if (row["Location"]) {
        adjustedRow["Location"] = row["Location"];
      } else {
        const fallbackLocation = (() => {
          const name = user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name;
          
          if (typeof name === 'string' && name.trim()) {
            return name.replace(/^TWS\s+/i, '').trim();
          }
            return "Unknown Location";
        })();
        adjustedRow["Location"] = fallbackLocation;
      }

      // First, copy any direct Phone field that has already been normalized
      if (row["Phone"]) {
        adjustedRow["Phone"] = row["Phone"];
      }
      
      Object.entries(columnMappings).forEach(([requiredHeader, detectedHeader]) => {
        // Skip mapping for Phone if we already have normalized it
        if (requiredHeader === "Phone" && adjustedRow["Phone"]) {
          return;
        }
        
        // Skip Mobile Phone and Company Phone for PlanHub and ConstructConnect
        if ((selectedSource === "PlanHub" || selectedSource === "ConstructConnect") && 
            (requiredHeader === "Mobile Phone" || requiredHeader === "Company Phone")) {
          return;
        }
        
        if (detectedHeader === "__blank__" || detectedHeader === "") {
          // 🛠 Safely detect the key that has no name
          const blankKey = Object.keys(row).find(k => !k.trim());
          if (blankKey) {
            adjustedRow[requiredHeader] = row[blankKey];
          } else {
            adjustedRow[requiredHeader] = null;
          }
        } else {
          // Do not overwrite Location from mapping; it's managed above
          if (requiredHeader === "Location") return;
          adjustedRow[requiredHeader] = row[detectedHeader] ?? null;
        }
      });
      return adjustedRow;
    });
  };
  
  
  
  const normalizePhoneNumber = (phone) => {
    // console.log('📞 Normalizing phone number:', phone);
    
    if (!phone) {
      console.log('❌ Empty phone number, returning empty string');
      return '';
    }
    
    // First handle extensions before removing non-digits
    let normalized = String(phone);
    const beforeExtension = normalized;
    // Split on common extension indicators and take only the first part
    normalized = normalized.split(/[xX]|ext|ext\.|est|est\./i)[0];
    if (beforeExtension !== normalized) {
      // console.log('🔪 Removed extension:', beforeExtension, '→', normali zed);
    }
    
    // Now remove all non-digit characters
    normalized = normalized.replace(/\D/g, '');
    // console.log('🔢 After removing non-digits:', normalized);
    
    // Remove leading 1 if number has 11 digits
    if (normalized.length === 11 && normalized.startsWith('1')) {
      normalized = normalized.slice(1);
      // console.log('✂️ Removed leading 1:', normalized);
    }
    
    // console.log('✅ Final normalized number:', normalized);
    return normalized;
  };

  const normalizeMultiplePhoneNumbers = (contact) => {
    // For PlanHub and ConstructConnect, only normalize the primary Phone field
    if (selectedSource === "PlanHub" || selectedSource === "ConstructConnect") {
      if (contact["Phone"]) {
        // console.log(`📞 Normalizing Phone:`, contact["Phone"]);
        const original = contact["Phone"];
        contact["Phone"] = normalizePhoneNumber(original);
        // console.log(`✅ Original: "${original}" → Normalized: "${contact["Phone"]}"`);
      }
      return contact;
    }
    
    // For other sources, normalize all three phone fields
    const phoneFields = ["Phone", "Mobile Phone", "Company Phone"];
    
    phoneFields.forEach(field => {
      if (contact[field]) {
        console.log(`📞 Normalizing ${field}:`, contact[field]);
        const original = contact[field];
        contact[field] = normalizePhoneNumber(original);
        console.log(`✅ Original: "${original}" → Normalized: "${contact[field]}"`);
      }
    });
    
    return contact;
  };

  const filterCSV = async () => {
            // Check if location is unknown or not selected before proceeding
        const currentLocation = user.role === 'agent' 
          ? (typeof user?.selectedFranchisee?.name === 'string' && user.selectedFranchisee.name.trim() ? user.selectedFranchisee.name.replace(/^TWS\s+/i, '').trim() : null)
          : (typeof selectedFranchisee?.name === 'string' && selectedFranchisee.name.trim() ? selectedFranchisee.name.replace(/^TWS\s+/i, '').trim() : null);
        
        if (!currentLocation || currentLocation === "Unknown Location") {
               const modalContent = user.role === 'agent'
           ? `
             <div class="text-left">
               <p class="mb-4">No location assigned. Contact developers for location assignment.</p>
               <p class="mb-4">Continue anyway or wait for assignment?</p>
             </div>
           `
        : `
          <div class="text-left">
            <p class="mb-4">No location has been selected for this import. This may cause issues with data processing.</p>
            <p class="mb-4">Would you like to continue anyway, or select a location first?</p>
          </div>
        `;

      const result = await Swal.fire({
        title: 'Location Not Selected',
        html: modalContent,
        icon: 'warning',
        showCancelButton: user.role !== 'admin',
        confirmButtonText: 'Continue Anyway',
        cancelButtonText: user.role === 'agent' ? 'Wait for Assignment' : 'Cancel',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        ...(user.role === 'admin' && {
          showDenyButton: true,
          denyButtonText: 'Go to Franchisees Page',
          denyButtonColor: '#28a745'
        })
      });

      if (result.isDenied && user.role === 'admin') {
        // Navigate to franchisees page for admins
        window.location.href = '/tws_franchisee';
        return;
      } else if (result.isDismissed || result.isDenied) {
        // User cancelled or denied (non-admin)
        return;
      }
      // If user confirms "Continue Anyway", proceed with the function
    }

    // Clear all existing filter tags to prevent conflicts
    setWhitelistFilters({});
    setBlacklistFilters({});
    
    const processed = new Set();
    const validContacts = [];
    const invalidContacts = [];
    const duplicateEmailContacts = [];

    const maxContacts = 8;
    const columnOrder = selectedSource === "PlanHub"
    ?["Project ID", "Name", "Project Name", "Title", "Company", "Phone", "Email", "Company URL","Project Type", "Street", "City", "State", "Zip", "Location"]
    :["Project ID", "First Name", "Last Name", "Project Name", "Title", "Company", "Phone", "Email", "Company URL","Project Type", "Street", "City", "State", "Zip", "Location"]

    // Determine if a Project List file actually looks like PlanHub based on headers
    const looksPlanHubAsProjectList = (selectedSource === "Project List") && hasPlanHubContactPattern((detectedHeaders || []));
    try {
      console.log('[Filter][Start]', {
        source: selectedSource,
        rows: fileData.length,
        looksPlanHubAsProjectList,
        detectedHeaders: (detectedHeaders || []).slice(0, 20)
      });
    } catch (_) {}

    const filtered = fileData.filter(raw => {
      let isValid = true;

      const zipValue = (selectedSource === "Lead List" || selectedSource === "Project List")
      ? (zipsColumn ? raw[zipsColumn] : raw.ZIP)
      : (selectedSource === "PlanHub" ? raw.Zip : raw.ZIP);
    
        // Comment out zip filtering - will use filter tags instead
      // if (filters.include) {
      //   if (filters.zip) isValid = isValid && filters.zipCodes.includes(zipValue);
      //   if (filters.projectType) isValid = isValid && filters.projectTypes.includes(raw["Project Types"]);
      //   if (filters.buildingUse) isValid = isValid && filters.buildingUses.includes(raw["Building Uses"]);
      // } else {
      //   if (filters.zip) isValid = isValid && !filters.zipCodes.includes(zipValue);
      //   if (filters.projectType) isValid = isValid && !filters.projectTypes.includes(raw["Project Types"]);
      //   if (filters.buildingUse) isValid = isValid && !filters.buildingUses.includes(raw["Building Uses"]);
      // }
      
      // Keep project type and building use filtering
    
      return isValid;
    });

    // Add filter tags for zips that match filter settings
    if (filters.zip && filters.zipCodes.length > 0) {
      // Use the standardized column name from desiredColumns (always "ZIP")
      const zipColumnName = "ZIP";
      
      // Get unique zip values from the filtered data
      const fileZips = [...new Set(filtered.map(row => {
        const zipValue = (selectedSource === "Lead List" || selectedSource === "Project List")
          ? (zipsColumn ? row[zipsColumn] : row.ZIP)
          : (selectedSource === "PlanHub" ? row.Zip : row.ZIP);
        return zipValue;
      }).filter(Boolean))];

      // Find zips that are in both filter settings and the file
      const matchingZips = fileZips.filter(zip => filters.zipCodes.includes(zip));

      if (filters.include) {
        // Include mode
        if (matchingZips.length > 0) {
          // Add matching zips to whitelist
          setWhitelistFilters(prev => ({
            ...prev,
            [zipColumnName]: [...new Set([...(prev[zipColumnName] || []), ...matchingZips])],
          }));
        } else {
          // No matching zips found - add a dummy filter that matches nothing
          setWhitelistFilters(prev => ({
            ...prev,
            [zipColumnName]: ["NO MATCHES FOUND"], // This will match nothing, showing 0 results
          }));
        }
      } else {
        // Exclude mode - only add filter if there are matching zips to exclude
        if (matchingZips.length > 0) {
          setBlacklistFilters(prev => ({
            ...prev,
            [zipColumnName]: [...new Set([...(prev[zipColumnName] || []), ...matchingZips])],
          }));
        }
      }
    }

    let branch = 'unknown';
    filtered.forEach(contact => {
      if (selectedSource == "ConstructConnect") {
        branch = 'ConstructConnect';
        const email = contact.Email;
        const projectId = contact["Project ID"];
        const project = `${contact["Project Title"]}_${projectId}`;
        const uniqueKey = `${email}_${project}`;

        if (!email || !isValidEmail(email)) {
          invalidContacts.push(contact);
        } else if (processed.has(uniqueKey)) {
          duplicateEmailContacts.push(contact);
        } else {
          validContacts.push(contact);
          processed.add(uniqueKey);
        }

      } else if (selectedSource === "PlanHub" || looksPlanHubAsProjectList) {
        branch = looksPlanHubAsProjectList ? 'ProjectList->PlanHub' : 'PlanHub';
        // Debug: log PlanHub contact parsing and inferred location
        console.log('[PlanHub] processing row', {
          projectId: contact["Project ID"],
          projectName: contact["Project Name"],
          locationInRow: contact.Location,
          inferredLocation: (() => {
            const name = user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name;
            if (typeof name === 'string' && name.trim()) {
              return name.replace(/^TWS\s+/i, '').trim();
            }
            return 'Unknown Location';
          })(),
          raw: {
            street: contact["Street"], city: contact["City"], state: contact["State"], zip: contact["Zip"],
          }
        });
        const projectInfo = {
          "Project ID": contact["Project ID"],
          "Project Name": contact["Project Name"],
          "Project Title": contact["Project Name"],
          "Project Type": contact["Project Type"],
          // Synonyms for display tables
          "Project Types": contact["Project Type"],
          "Street": contact["Street"],
          "Address": contact["Street"],
          "City": contact["City"],
          "State": contact["State"],
          "Zip": contact["Zip"],
          "ZIP": contact["Zip"],
          "Location": contact.Location || (() => {
            const name = user?.selectedFranchisee?.name;
            
            if (typeof name === 'string' && name.trim()) {
              return name.replace(/^TWS\s+/i, '').trim();
            }
            return "Unknown Location";
          })()
        };

        for (let i = 1; i <= maxContacts; i++) {
          const name = (contact[`Contact ${i} Name`] || '').toString().trim();
          const email = (contact[`Contact ${i} Email`] || '').toString().trim();
          const phone = (contact[`Contact ${i} Phone`] || '').toString().trim();
          const company = (contact[`Contact ${i} Company`] || '').toString().trim();
          const title = (contact[`Contact ${i} Title`] || '').toString().trim();
          const website = (contact[`Contact ${i} Company URL`] || '').toString().trim();

          // Treat names that equal project-level fields as non-contact data
          const nameLooksLikeProjectField = Boolean(
            name && (name === projectInfo.Location || name === projectInfo["Project Name"] || name === contact["Project Name"])
          );
          const nameForUse = nameLooksLikeProjectField ? "" : name;

          // Debug: verify the source header values for this contact index
          if (i <= 2) {
            try {
              console.log('[PlanHub][ContactFields]', {
                projectId: contact["Project ID"],
                index: i,
                sourceNameKey: `Contact ${i} Name`,
                name,
                email,
                phone,
                company,
                title,
                website,
                equalsLocation: name === projectInfo.Location
              });
            } catch (_) {}
          }

          const hasValidData = Boolean(nameForUse || email || title || company || phone || website);

          // Debug decision for early indices
          if (i <= 3) {
            try {
              console.log('[PlanHub][ContactDecision]', {
                projectId: contact["Project ID"],
                index: i,
                nameRaw: name,
                nameLooksLikeProjectField,
                nameForUse,
                email,
                title,
                company,
                phone,
                website,
                hasValidData,
                decision: (!email && hasValidData) ? 'invalid' : (email ? 'valid' : 'skipped')
              });
            } catch (_) {}
          }

          if (!email && hasValidData) {
            invalidContacts.push({
              ...projectInfo,
              "Name": nameForUse,
              "Role": title,
              "Title": title,
              "Company": company,
              "Phone": phone,
              "Email": "(Missing Email)",
              "Company URL": website,
              "Website": website,
              "Project Title": projectInfo["Project Name"] || contact["Project Name"] || projectInfo["Project Title"] || "",
            });
          } else if (email) {
            const contactRow = {
              ...projectInfo,
              "Name": nameForUse,
              "Role": title,
              "Title": title,
              "Company": company,
              "Phone": phone,
              "Email": email,
              "Company URL": website,
              "Website": website,
              "Project Title": projectInfo["Project Name"] || contact["Project Name"] || projectInfo["Project Title"] || "",
            };

            const uniqueKey = email && contact["Project ID"] ? `${email}_${contact["Project ID"]}` : null;

            if (uniqueKey && processed.has(uniqueKey)) {
              duplicateEmailContacts.push(contactRow);
            } else if (uniqueKey) {
              validContacts.push(contactRow);
              processed.add(uniqueKey);
            }
          }
        }

      } else if ((selectedSource === "Lead List" || selectedSource === "Project List")) {
        // Non-flattened Project List should skip rows without any true contact fields
        const emailRaw = contact[emailColumn] || contact["Contact Email"] || contact["Email"]; 
        const email = (emailRaw || "").toString().trim();
        const nameRaw = (contact["Contact Name"] || contact["Name"] || "").toString().trim();
        const phone = (contact["Contact Phone"] || contact["Phone"] || "").toString().trim();
        const company = (contact["Contact Company"] || contact["Company"] || "").toString().trim();
        const title = (contact["Contact Title"] || contact["Title"] || "").toString().trim();
        const website = (contact["Contact Company URL"] || contact["Company URL"] || contact["Website"] || "").toString().trim();

        const nameLooksLikeProjectField = Boolean(
          nameRaw && (nameRaw === contact["Project Name"] || nameRaw === contact["Project Title"] || nameRaw === contact.Location)
        );
        const nameForUse = nameLooksLikeProjectField ? "" : nameRaw;
        const hasTrueContactField = Boolean(nameForUse || email || phone || company || title || website);

        if (selectedSource === "Project List" && !hasTrueContactField) {
          // Skip rows without any real contact fields
          return;
        }

        const project = `${contact["Project Title"] || contact["Project Name"] || "Unknown"}_${contact["Project ID"] || "Unknown"}`;
        const uniqueKey = `${email}_${project}`;

        if (!email || !isValidEmail(email)) {
          if (selectedSource !== "Project List" || hasTrueContactField) {
          invalidContacts.push(contact);
          }
        } else if (processed.has(uniqueKey)) {
          duplicateEmailContacts.push(contact);
        } else {
          if (selectedSource === "Lead List") {
            validContacts.push({
              ...contact,
              "Project ID": newProjectID,
              "Project Title": newProjectTitle,
              originalTitle: contact["Project Name"] || contact["Name"] || contact["Project Title"] || ""
            });
          } else {
            validContacts.push({
              ...contact,
              originalTitle: contact["Project Name"] || contact["Name"] || contact["Project Title"] || ""
            });
          }          
          processed.add(uniqueKey);
        }
      }
    });

    // Normalize phone numbers after filtering
    console.log("🔄 Starting phone normalization for all phone types:", selectedSource);
    
    validContacts.forEach((contact, index) => {
      // Normalize all phone number fields
      normalizeMultiplePhoneNumbers(contact);
    });

    // Log the contacts with Phone field after normalization
    console.log("🔍 First few contacts after phone normalization:", validContacts.slice(0, 3));
    
    const adjustHeaders = (data) => {
      return data.map(row => {
        let adjustedRow = {};
        Object.keys(row).forEach(key => {
          const newKey = headerMapping[key] || key;
          adjustedRow[newKey] = row[key];
        });
        return adjustedRow;
      });
    };
  
    if (selectedSource === "PlanHub") {
      const orderedValidContacts = validContacts.map(contact =>
        Object.fromEntries(columnOrder.map(key => [key, contact[key] || ""]))
      );
      const adjustedData = adjustHeaders(orderedValidContacts);
      setFilteredData(adjustedData);
  
    } else if (selectedSource === "ConstructConnect" || (selectedSource === "Lead List" || selectedSource === "Project List")) {
      let adjustedData = applyMappings(validContacts);
      
      // For Lead List and Project List, add placeholder Email column as first column only if no email column is selected
      if ((selectedSource === "Lead List" || selectedSource === "Project List") && (!emailColumn || emailColumn === "")) {
        adjustedData = adjustedData.map((row, idx) => {
          // Create new object with Email as first property (placeholder)
          const newRow = { Email: "" };
          // Add all other properties
          Object.keys(row).forEach(key => {
            newRow[key] = row[key];
          });
          return newRow;
        });
        
        // Add Email to detectedHeaders and auto-map it
        if (!detectedHeaders.includes("Email")) {
          setDetectedHeaders(prev => ["Email", ...prev]);
          setColumnMappings(prev => ({
            ...prev,
            "Email": "Email"
          }));
        }
      }
      
      setFilteredData(adjustedData);
    }
  
    setInvalidData(invalidContacts);
    setOriginalInvalidData(invalidContacts); // Preserve original data
    setDuplicateData(duplicateEmailContacts);
    setIsFiltered(true);
    // Debug summary available on demand; suppress by default to avoid console noise
    // console.debug('[Filter][Summary]', { branchUsed: branch, validCount: validContacts.length, invalidCount: invalidContacts.length, duplicateCount: duplicateEmailContacts.length });
    
    setHasFilteredOnce(true);
    setValidContactsOnly(validContacts);

    // After setting filteredData, directly normalize phone numbers
    setTimeout(() => {
      console.log("🔄 Direct phone normalization after setting filteredData");
      setFilteredData(prevData => {
        return prevData.map(contact => {
          const updatedContact = {...contact};
          
          // Always check for a Phone field regardless of source
          if (updatedContact.Phone) {
            const originalPhone = updatedContact.Phone;
            const normalizedPhone = normalizePhoneNumber(originalPhone);
            updatedContact.Phone = normalizedPhone;
          }
          
          return updatedContact;
        });
      });
      
      console.log("✅ Phone normalization complete");
    }, 100);

    await trackEvent(
      user.id,
      'Filter File',
      'Project Upload Filter',
      {}
    );
    
    // Advance to step 2 after filtering
    setCurrentStep(2);
  };
  
  
  const handleModalFilter = () => {
    toggleFilterModal( prev => !prev);
  }

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFileChange = async (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    const fileType = file?.type;

    if (!file) return;
    
    // Reset to step 1 when a new file is uploaded
    setCurrentStep(1);
    
    await trackEvent(
      user.id, 
      "File Upload", 
      "Project File Upload", 
      { fileName: file.name, fileType }
    );

    // Check if the file type is supported
    if (fileType !== 'text/csv' && fileType !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      Swal.fire({
        title: 'Invalid File Type',
        text: 'This is not a valid PlanHub/Construct Connect file. Please upload a CSV or Excel file.',
        icon: 'error',
        confirmButtonText: 'Refresh',
        confirmButtonColor: '#f97316', // hs-orange
        preConfirm: () => {
          window.location.reload();
        }
      });
      return;
    }

    setOriginalFile(file); // ✅ Save the original file
    setFileData([]);
    setFileInfo({ name: '', type: '' });
    setIsFiltered(false);
    setFilteredData([]);
    setDisplayImportSummary(true);
    // Reset the usedHashes Set when uploading a new file
    usedHashes.clear();
    
    if(fileType === 'text/csv'){
      setFileInfo({ name: file.name, type: file.type });
      parseFile(file);
    } else if(fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'){
      convertXLSXtoCSV(file); 
    }
  } 

  const convertXLSXtoCSV = async (file, overrideSheetName = null) => {
    const reader = new FileReader();
  
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
  
      const allSheets = workbook.SheetNames;
      setSheetNames(allSheets);

      // Choose a sheet safely. For ConstructConnect prefer the second sheet if it exists; otherwise use the first.
      const sheetName =
        (overrideSheetName && allSheets.includes(overrideSheetName))
          ? overrideSheetName
          : (selectedSource === "ConstructConnect"
            ? (allSheets.length > 1 ? allSheets[1] : allSheets[0])
            : allSheets[0]);

      // Reflect chosen sheet in state so UI can display if needed
      setSelectedSheet(sheetName);
  
      const sheet = workbook.Sheets[sheetName];
      let jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  
      if (jsonData.length > 0) {
        // Get location based on user role
        const location = (() => {
          const name = user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name;
          
          if (typeof name === 'string' && name.trim()) {
            return name.replace(/^TWS\s+/i, '').trim();
          }
          return "Unknown Location";
        })();

        console.log("🏢 convertXLSXtoCSV - Setting location to:", location, "selectedFranchisee:", user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name);

        // Add Location header
        jsonData[0].push("Location");

        // Add location to each row
        for (let i = 1; i < jsonData.length; i++) {
          if (jsonData[i].length > 0) {
            jsonData[i].push(location);
          } else {
            jsonData[i].push("");
          }
        }
        // Reset column selections when changing sheets
        setEmailColumn("");
        setZipsColumn("");
        setProjectIdColumn("");
        setColumnMappings({});
        setFilteredData([]);
        setIsFiltered(false);

        if (selectedSource === "PlanHub") {
          console.log("✅ Adding Project ID Column for PlanHub");
          jsonData[0].splice(0, 0, "Project ID");
  
          for (let i = 1; i < jsonData.length; i++) {
            if (jsonData[i].length > 0) {
              // For PlanHub, use the project name or a combination of project fields to generate ID
              const projectName = jsonData[i][1] || ""; // Assuming project name is in second column
              const projectType = jsonData[i][2] || ""; // Assuming project type is in third column
              const uniqueString = `${projectName}_${projectType}_Row_${i}`;
              jsonData[i].splice(0, 0, await generateNumericHash(uniqueString));
            } else {
              jsonData[i].splice(0, 0, "");
            }
          }
        } else if (addProjectIDRef.current && (selectedSource === "Project List")) {
          console.log("✅ Adding Project ID Column for Project List");
          jsonData[0].splice(0, 0, "Project ID");
  
          for (let i = 1; i < jsonData.length; i++) {
            if (jsonData[i].length > 0) {
              let columnValue = `Row_${i}`; // Default fallback
              
              if (projectIdColumn) {
                // Find the column index for the selected project column
                const projectColumnIndex = jsonData[0].indexOf(projectIdColumn);
                if (projectColumnIndex >= 0 && jsonData[i][projectColumnIndex]) {
                  columnValue = String(jsonData[i][projectColumnIndex]);
                }
              }
              
              // Always generate a proper hash, even if using fallback value
              const generatedId = await generateNumericHash(columnValue);
              console.log(`🔍 [convertXLSXtoCSV] Generated Project ID for row ${i}: ${generatedId} (from: "${columnValue}")`);
              jsonData[i].splice(0, 0, generatedId);
            } else {
              jsonData[i].splice(0, 0, "");
            }
          }
        }
  
        const csvData = Papa.unparse(jsonData);
        setFileInfo({ name: file.name.replace(/\.[^/.]+$/, ".csv"), type: "text/csv" });
        console.log("🔄 About to call parseFile with csvData length:", csvData.length);
        parseFile(csvData);
      } else {
        console.log("❌ No Data Found in File");
        Swal.fire({
          title: 'Invalid File',
          text: 'This is not a valid PlanHub/Construct Connect file. No data was found in the selected sheet.',
          icon: 'error',
          confirmButtonText: 'Refresh',
          confirmButtonColor: '#f97316', // hs-orange
          preConfirm: () => {
            window.location.reload();
          }
        });
      }
    };
  
    reader.readAsArrayBuffer(file);
  };
  
  
const calculateSimilarity = (str1, str2) => {
  const set1 = new Set(str1.toLowerCase().split(/\s+/));
  const set2 = new Set(str2.toLowerCase().split(/\s+/));

  const intersection = new Set([...set1].filter(x => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  return intersection.size / union.size; // Jaccard score (0 to 1)
};

  const detectMultiContactHeaders = (headers) => {
    const contactGroups = {};
    const cleanHeaders = new Set(); // ✅ Prevent duplicate dropdown options
    console.log("🚨 Raw Headers Before Processing:", headers);

    headers.forEach((header) => {
      const match = header.match(/(.+?)(\d+)\s*(.*)/); // Match "Contact 1 Name", "Contact 2 Email"
      if (match) {
        const baseName = `${match[1].trim()} ${match[3].trim()}`.trim(); // Keeps "Contact Name", "Contact Email"
        if (!contactGroups[baseName]) contactGroups[baseName] = [];
        contactGroups[baseName].push(header);
  
        cleanHeaders.add(baseName); // ✅ Ensure only "Name", "Email", etc. appear
      } else {
        cleanHeaders.add(header); // ✅ Include normal headers
      }
    });
    console.log("🔹 Final Clean Headers:", Array.from(cleanHeaders));
    console.log("📌 Contact Groups Detected:", contactGroups);
    return { contactGroups, cleanHeaders: Array.from(cleanHeaders) };
  };
  
  const flattenMultiContactRows = (data, contactGroups, cleanHeaders) => {
    let flattenedData = [];
  
    // Get location from the first row that has it
    const location = data[0]?.Location || "Unknown Location";
  
    data.forEach((row) => {
      const maxContacts = Math.max(...Object.values(contactGroups).map(arr => arr.length), 1);
  
      for (let i = 0; i < maxContacts; i++) {
        let newRow = {};
  
        // ✅ Copy project-level fields
        cleanHeaders.forEach(header => {
          if (!header.startsWith("Contact ")) {
            newRow[header] = row[header] || "";
          }
        });
  
        // Always preserve the location
        newRow.Location = location;
  
        let isValidContact = false;
  
        // ✅ Extract one contact per row
        Object.entries(contactGroups).forEach(([baseHeader, columnVariants]) => {
          const value = columnVariants[i] ? row[columnVariants[i]] || "" : "";
          newRow[baseHeader] = value;
          if (value.trim() !== "") isValidContact = true;
        });
  
        if (isValidContact) {
          flattenedData.push(newRow);
        }
      }
    });
  
    console.log("✅ Final Flattened Data:", flattenedData);
  
    // 🔹 Now update headers and mappings after flattening
    const newHeaders = Object.keys(flattenedData[0] || {});
    console.log("🛠 New Flattened Headers:", newHeaders);
    detectHeadersAndMap(newHeaders); // ✅ Run mapping AFTER flattening
  
    return flattenedData;
  };
  
  // --- Vendor-aware detection + validation helpers ---
  const normalize = (s) => (s || "").toString().trim().toLowerCase();
  const headerSet = (headers) => new Set(headers.map(h => normalize(h)));
  const hasAny = (set, arr) => arr.some(a => set.has(normalize(a)));
  const countPresent = (set, arr) => arr.reduce((n, a) => n + (set.has(normalize(a)) ? 1 : 0), 0);

  // Detects PlanHub multi-contact headers like "Contact 1 Name", "Contact 2 Email", etc.
  const hasPlanHubContactPattern = (headers) =>
    headers.some(h => /^contact\s*\d+\s*(name|email|phone|title|company|company url)$/i.test(h || ""));

  // Heuristics for vendor-specific fields
  const PLANHUB_PROJECT_HINTS = ["project name","project type","street","city","state","zip","location"];
  const CC_DESCRIPTOR_HINTS = ["project description","building uses","project types","project category"];
  const CORE_REQUIRED = ["email","phone","company"];

  function detectSourceFromHeaders(headers) {
    const set = headerSet(headers);
    const looksPlanHub = hasPlanHubContactPattern(headers) && countPresent(set, PLANHUB_PROJECT_HINTS) >= 3;
    const looksConstructConnect = countPresent(set, CC_DESCRIPTOR_HINTS) >= 2;
    return { looksPlanHub, looksConstructConnect };
  }

  function validateRequiredColumns(headers, selectedSource) {
    const set = headerSet(headers);
    const { looksPlanHub, looksConstructConnect } = detectSourceFromHeaders(headers);

    // Core: require presence of email/phone/company in any form
    const hasCore = CORE_REQUIRED.every(req => [...set].some(h => h.includes(req)));

    if (selectedSource === "PlanHub") {
      const ok = hasCore && looksPlanHub;
      return {
        isValid: ok,
        missingColumns: ok ? [] : [
          "PlanHub file must include project fields (e.g., Project Name/Type/City/State/Zip)",
          "at least one 'Contact N …' column, and Email/Phone/Company."
        ]
      };
    }

    if (selectedSource === "ConstructConnect") {
      const ok = hasCore && looksConstructConnect;
    return {
        isValid: ok,
        missingColumns: ok ? [] : [
          "ConstructConnect file must include descriptors (e.g., Project Description/Building Uses/Project Types/Project Category)",
          "and Email/Phone/Company."
        ]
      };
    }

    return { isValid: true, missingColumns: [] };
  }

  const parseFile = (file) => {
    console.log("📂 Parsing File:", typeof file, file?.length || 'no length');
    Papa.parse(file, {
      header: true, 
      skipEmptyLines: true,
      complete: (results) => {
        if (results.data.length < 1) {
          Swal.fire({
            title: 'Invalid File',
            text: 'This is not a valid PlanHub/Construct Connect file. The file does not contain enough rows!',
            icon: 'error',
            confirmButtonText: 'Refresh',
            confirmButtonColor: '#f97316', // hs-orange
            preConfirm: () => {
              window.location.reload();
            }
          });
          return;
        }
        let headers = results.meta.fields.map(h => h?.trim() || "__blank__");
        console.log("🛠 Detected Headers:", headers);
        
        // Strong vendor-aware validation for ConstructConnect and PlanHub
        if (selectedSource === "ConstructConnect" || selectedSource === "PlanHub") {
          const validation = validateRequiredColumns(headers, selectedSource);
          if (!validation.isValid) {
            Swal.fire({
              title: 'Invalid File',
              html: `
                <p><strong>This file is not a valid ${selectedSource} file.</strong></p>
                <ul style="text-align: left; display: inline-block; max-width: 520px;">
                  ${validation.missingColumns.map(msg => `<li>• ${msg}</li>`).join('')}
                </ul>
                <p class="mt-4">Please upload a valid ${selectedSource} export.</p>
              `,
              icon: 'error',
              confirmButtonText: 'Refresh',
              confirmButtonColor: '#f97316',
              preConfirm: () => { window.location.reload(); }
            });
            return;
          }
        }

        // Get location based on user role
        console.log("👤 User:", user);
        console.log("🏢 Selected Franchisee:", user?.role === 'agent' ? user?.selectedFranchisee : selectedFranchisee);
        
        // Wait for franchisee data to be available
        if (!user || (!user.selectedFranchisee)) {
          console.warn("⚠️ Waiting for franchisee data to be available...");
          // Try to get from localStorage as fallback
          const selectedFranchisee = JSON.parse(localStorage.getItem('selectedFranchisee'));
          if (selectedFranchisee) {
            updateSelectedFranchisee(selectedFranchisee);
          }
        }

        const location = (() => {
          const name = user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name;
          
          if (typeof name === 'string' && name.trim()) {
            return name.replace(/^TWS\s+/i, '').trim();
          }
          return "Unknown Location";
        })();
          
        console.log("📍 Calculated Location:", location);

        let rawData = results.data.map(row => {
          const trimmedRow = {};
          Object.entries(row).forEach(([key, value]) => {
            const normalizedKey = key?.trim() || "__blank__"; // 🔒 Handles blanks & invisible characters
            trimmedRow[normalizedKey] = value;
          });
          // Add location to each row immediately
          trimmedRow.Location = location;
          return trimmedRow;
        });
        
        if ((selectedSource === "Lead List" || selectedSource === "Project List") && shouldFlatten) {
          console.log("🔄 Applying Multi-Contact Flattening for 'Other' Source");
          const { contactGroups, cleanHeaders } = detectMultiContactHeaders(headers);
          rawData = flattenMultiContactRows(rawData, contactGroups, cleanHeaders);
          headers = cleanHeaders;  // ✅ Ensure we update headers to cleaned version
        }

        detectHeadersAndMap(headers);  // ✅ Ensure mapping runs after flattening
        setDetectedHeaders(headers);
        console.log("📂 Setting fileData with", rawData.length, "rows");
        setFileData(rawData);
      },
    });
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (!file) return;
    
    const fileType = file.type;

    // Check if the file type is supported
    if (fileType !== 'text/csv' && fileType !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      Swal.fire({
        title: 'Invalid File Type',
        text: 'Please upload a CSV or Excel file.',
        icon: 'error',
        confirmButtonColor: '#f97316', // hs-orange
      });
      return;
    }

    setFileData([]);
    setFileInfo({ name: '', type: '' });
    setIsFiltered(false);
    setFilteredData([]);
    setDisplayImportSummary(true);
    // Reset the usedHashes Set when dropping a new file
    usedHashes.clear();
    
    if(fileType === 'text/csv'){
      setFileInfo({ name: file.name, type: file.type });
      parseFile(file);
    } else if(fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'){
      convertXLSXtoCSV(file);
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  }


  const getDomainName = (email, website) => {
    const getDomainFromEmail = (email) => {
      return email.substring(email.indexOf("@") + 1);
    };
  
    const getDomainFromWebsite = (website) => {
      const domain = website.match(/^(?:https?:\/\/)?(?:www\.)?([^/]+)/i);
      return domain ? domain[1] : '';
    };
  
    if (email) {
      return getDomainFromEmail(email);
    } else if (website) {
      return getDomainFromWebsite(website);
    } else {
      return ''; 
    }
  };

  // Add this new function near other utility functions
  const ensurePhoneNumbersAreNormalized = (contacts) => {
    // console.log("🔄 Final normalization check on", contacts.length, "contacts");
    let normalizedCount = 0;
    
    const result = contacts.map((contact, index) => {
      const updatedContact = {...contact};
      let wasNormalized = false;
      
      // For PlanHub and ConstructConnect, only normalize the primary Phone field
      if (selectedSource === "PlanHub" || selectedSource === "ConstructConnect") {
        if (updatedContact["Phone"]) {
          const originalPhone = updatedContact["Phone"];
          const normalizedPhone = normalizePhoneNumber(originalPhone);
          updatedContact["Phone"] = normalizedPhone;
          // console.log(`📱 [${index}] Normalized Phone: ${originalPhone} → ${normalizedPhone}`);
          wasNormalized = true;
        }
      } else {
        // For other sources, normalize all three phone fields
        const phoneFields = ["Phone", "Mobile Phone", "Company Phone"];
        
        phoneFields.forEach(field => {
          if (updatedContact[field]) {
            const originalPhone = updatedContact[field];
            const normalizedPhone = normalizePhoneNumber(originalPhone);
            updatedContact[field] = normalizedPhone;
            // console.log(`📱 [${index}] Normalized ${field}: ${originalPhone} → ${normalizedPhone}`);
            wasNormalized = true;
          }
        });
      }
      
      if (wasNormalized) {
        normalizedCount++;
      }
      
      return updatedContact;
    });
    
    console.log(`✅ Normalized ${normalizedCount} phone numbers out of ${contacts.length} contacts`);
    return result;
  };

  // Helper to make a URL/email-safe slug
  const slugify = (value) => {
    return (value || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 16);
  };

  // Smart field detection based on column name patterns
  const detectFieldType = (columnName, row) => {
    if (!columnName || !row) return null;
    
    const col = columnName.toLowerCase().trim();
    const value = row[columnName];
    
    // Name fields
    if (col.includes('first') && col.includes('name')) return { type: 'firstName', value };
    if (col.includes('last') && col.includes('name')) return { type: 'lastName', value };
    if (col === 'name' || col === 'full name' || col === 'fullname') return { type: 'name', value };
    if (col.includes('contact') && col.includes('name')) return { type: 'name', value };
    
    // Company fields
    if (col.includes('company') || col.includes('business') || col.includes('organization')) return { type: 'company', value };
    if (col.includes('firm') || col.includes('corp') || col.includes('inc')) return { type: 'company', value };
    
    // Project fields
    if (col.includes('project') && col.includes('title')) return { type: 'projectTitle', value };
    if (col.includes('project') && col.includes('name')) return { type: 'projectTitle', value };
    if (col.includes('project') && (col.includes('id') || col.includes('number'))) return { type: 'projectId', value };
    if (col === 'project' || col === 'job') return { type: 'projectTitle', value };
    
    // Phone fields
    if (col.includes('phone') || col.includes('tel') || col.includes('mobile') || col.includes('cell')) return { type: 'phone', value };
    if (col.includes('work') && col.includes('phone')) return { type: 'phone', value };
    
    // Address fields
    if (col.includes('address') || col.includes('street')) return { type: 'address', value };
    if (col === 'city' || col.includes('town')) return { type: 'city', value };
    if (col === 'state' || col.includes('province')) return { type: 'state', value };
    if (col === 'zip' || col.includes('postal') || col.includes('code')) return { type: 'zip', value };
    
    // ID fields
    if (col.includes('id') || col.includes('number') || col.includes('ref')) return { type: 'id', value };
    
    return null;
  };

  // Check for name collisions in HubSpot
  const checkNameCollisions = async () => {
    if (!includeTempEmails || (!invalidData || invalidData.length === 0) && (!originalInvalidData || originalInvalidData.length === 0)) {
      return;
    }

    // Check if we have the required selections
    if (useFullName && !selectedNameColumn) {
      return;
    }
    if (!useFullName && (!selectedFirstNameColumn || !selectedLastNameColumn)) {
      return;
    }

    // If we already have collision data, just open the modal
    if (collisionContacts && collisionContacts.length > 0) {
      setShowCollisionModal(true);
      return;
    }

    let dataLoadingModal;
    try {
      setLoading(true);
      
      // Show loading modal for name collision detection
      dataLoadingModal = Swal.fire({
        title: 'Checking Name Collisions...',
        html: `
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Searching for existing contacts in HubSpot...</p>
            <p class="text-sm text-gray-500 mt-2">This may take a few moments</p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      let names = [];
      
      // Use originalInvalidData if invalidData is empty (after exclusions)
      const dataToUse = (invalidData && invalidData.length > 0) ? invalidData : originalInvalidData;
      
      if (useFullName) {
        // Extract unique full names from the selected column
        names = [...new Set(dataToUse
          .map(contact => contact[selectedNameColumn])
          .filter(name => name && name.toString().trim())
          .map(name => name.toString().trim())
        )];
      } else {
        // Extract unique first and last names separately
        const firstNames = [...new Set(dataToUse
          .map(contact => contact[selectedFirstNameColumn])
          .filter(name => name && name.toString().trim())
          .map(name => name.toString().trim())
        )];
        const lastNames = [...new Set(dataToUse
          .map(contact => contact[selectedLastNameColumn])
          .filter(name => name && name.toString().trim())
          .map(name => name.toString().trim())
        )];
        names = { firstNames, lastNames };
      }

      if ((useFullName ? names.length : (names.firstNames.length + names.lastNames.length)) === 0) {
        setLoading(false);
        return;
      }

      // Call backend API to search for contacts by name
      const totalNames = useFullName ? names.length : (names.firstNames.length + names.lastNames.length);
      console.log('🔍 Making request to search-contacts-by-name with', totalNames, 'names');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/search-contacts-by-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          names: names,
          searchType: useFullName ? 'fullname' : 'firstlastname',
          hubspot_api_key: user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.hubspot_api_key : selectedFranchisee?.client_type?.hubspot_api_key
        })
      });
      
      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Convert found names to a simple array for comparison
        const foundNames = data.foundContacts;
        setCollisionContacts(foundNames);
        dataLoadingModal.close();
        
        // Show appropriate modal based on results
        if (foundNames && foundNames.length > 0) {
          setShowCollisionModal(true);
        } else {
          setShowNoCollisionModal(true);
        }
      } else {
        console.error('Error checking collisions:', data.error);
        dataLoadingModal.close();
        alert('Error checking for name collisions: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error checking name collisions:', error);
      dataLoadingModal.close();
      alert('Error checking for name collisions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate unique temporary email for invalid contacts
  const generateTemporaryEmail = (row, index) => {
    // For PlanHub and ConstructConnect, we can create more reliable deterministic emails
    // since they have strict column structures
    if (selectedSource === "PlanHub" || selectedSource === "ConstructConnect") {
      // Use only the most reliable and stable fields for these sources
      const namePart = slugify(row["Name"]);
      const phonePart = slugify(row["Phone"]);
      
      // Create a deterministic hash from just name and phone
      // This avoids unpredictable data from new columns that may be added over time
      const contactData = `${row["Name"]}|${row["Phone"]}`;
      const dataHash = btoa(contactData).replace(/[^a-zA-Z0-9]/g, "").slice(0, 8);
      
      return `noemail+${namePart}_${phonePart}_${dataHash}@temporary.com`;
    } else {
      // For Lead List and Project List, intelligently detect field types from column names
      const detectedFields = {};
      
      // Scan all columns in the row to detect field types
      Object.keys(row).forEach(columnName => {
        const fieldInfo = detectFieldType(columnName, row);
        if (fieldInfo && fieldInfo.value && fieldInfo.value.toString().trim()) {
          detectedFields[fieldInfo.type] = fieldInfo.value;
        }
      });
      
      // Build name part from detected fields
      const namePart = slugify(
        detectedFields.firstName || 
        detectedFields.name || 
        detectedFields.company || 
        detectedFields.projectTitle || 
        "contact"
      );
      
      // Build ID part from detected fields
      const idPart = slugify(
        String(
          detectedFields.projectId || 
          detectedFields.id || 
          "na"
        )
      );
      
      // Create a deterministic unique identifier based on all detected contact data
      const contactData = [
        detectedFields.firstName || "",
        detectedFields.lastName || "",
        detectedFields.name || "",
        detectedFields.company || "",
        detectedFields.projectTitle || "",
        detectedFields.projectId || "",
        detectedFields.id || "",
        detectedFields.phone || "",
        detectedFields.address || "",
        detectedFields.city || "",
        detectedFields.state || "",
        detectedFields.zip || ""
      ].filter(value => value && value.toString().trim() !== "").join("|");
      
      // Create a hash-like string from the contact data for consistency
      const dataHash = btoa(contactData).replace(/[^a-zA-Z0-9]/g, "").slice(0, 8);
      
      return `noemail+${namePart}_${idPart}_${dataHash}@temporary.com`;
    }
  };

  // Normalize strings for filter comparisons (Email only)
  const normalizeForFilter = (s) => (s ?? "").toString().trim().toLowerCase().replace(/[\u200B-\u200D\uFEFF]/g, "");

  // PlanHub helper: pick the first populated "Contact N <Base>" field
  const getFirstContactField = (row, base, max = 8) => {
    for (let i = 1; i <= max; i++) {
      const key = `Contact ${i} ${base}`;
      const val = row[key];
      if (val != null && String(val).trim() !== "") {
        return { key, value: val };
      }
    }
    return { key: null, value: "" };
  };

  const downloadFilteredCSV = () => {

    // Process data once with all transformations
    const processedData = filteredResults.map(contact => {
      const updatedContact = {
        ...contact
      };
      
      // For PlanHub and ConstructConnect, only normalize the primary Phone field
      if (selectedSource === "PlanHub" || selectedSource === "ConstructConnect") {
        if (updatedContact["Phone"]) {
          updatedContact["Phone"] = normalizePhoneNumber(updatedContact["Phone"]);
        }
      } else {
        // For other sources, normalize all three phone fields
        const phoneFields = ["Phone", "Mobile Phone", "Company Phone"];
        
        phoneFields.forEach(field => {
          if (updatedContact[field]) {
            updatedContact[field] = normalizePhoneNumber(updatedContact[field]);
          }
        });
      }
      
      return updatedContact;
    });

    // Generate CSV with location column
    const csvData = Papa.unparse(processedData, { columns: desiredColumns });
    
    // Create and save blob
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `VALID_EMAILS_${fileInfo.name}`);
  };

  const downloadCompanyCSV = async () => {
    const updatedCompanyData = await initializeCompanyData(filteredResults); // ✅ Wait for updated data
    const csvData = Papa.unparse(updatedCompanyData, { columns: desiredCompanyColumn }); // ✅ Use updated data
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `COMPANY_${fileInfo.name}`);
  };


  const downloadProjectCSV = async () => {
    const updatedProjectsData = await initializeProjectsData(filteredResults);
    const csvData = Papa.unparse(updatedProjectsData,{ columns: desiredProjectColumns });
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;'});
    saveAs(blob, `PROJECT_${fileInfo.name}`);
  }

  const downloadInvalidAndDuplicateCSV = () => {
    if (invalidData.length === 0 && duplicateData.length === 0) {
      Swal.fire({
        title: 'Nothing to download',
        text: 'There are no invalid or duplicate email contacts.',
        icon: 'info',
        confirmButtonColor: '#f97316',
      });
      return;
    }
    
    // Helper function to remove the "Location" column from data
    const removeLocationColumn = (data) => {
      return data.map(row => {
        const { Location, ...rowWithoutLocation } = row;
        return rowWithoutLocation;
      });
    };
    
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Add invalid contacts sheet if there are any (excluding Location column)
    if (invalidData.length > 0) {
      const invalidDataWithoutLocation = removeLocationColumn(invalidData);
      const invalidWorksheet = XLSX.utils.json_to_sheet(invalidDataWithoutLocation);
      XLSX.utils.book_append_sheet(workbook, invalidWorksheet, 'Invalid Contacts');
    }
    
    // Add duplicate contacts sheet if there are any (excluding Location column)
    if (duplicateData.length > 0) {
      const duplicateDataWithoutLocation = removeLocationColumn(duplicateData);
      const duplicateWorksheet = XLSX.utils.json_to_sheet(duplicateDataWithoutLocation);
      XLSX.utils.book_append_sheet(workbook, duplicateWorksheet, 'Duplicate Contacts');
    }
    
    // Generate and download the Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `INVALID_AND_DUPLICATE_EMAILS_${fileInfo.name.replace(/\.[^/.]+$/, '')}.xlsx`);
  };

  const desiredCompanyColumn = ["Company", "Website", "Domain"];  
  // Rows used for filter suggestions (unfiltered table + optional temp-email invalids)
  const suggestionRows = useMemo(() => {
    let rows = filteredData;
    if (includeTempEmails && Array.isArray(invalidData) && invalidData.length > 0) {
      try {
        const mappedInvalid = invalidData.map((row, idx) => {
          const shaped = {};
          (desiredColumns || []).forEach(k => {
            let value = row[k];
            if (value == null || String(value).trim() === "") {
              const mappedHeader = (columnMappings && columnMappings[k]) ? columnMappings[k] : null;
              if (mappedHeader && Object.prototype.hasOwnProperty.call(row, mappedHeader)) {
                value = row[mappedHeader];
              }
            }
            if (k === "Project Title" && (value == null || String(value).trim() === "")) {
              value = row["Project Name"] || row["Dealname"] || "";
            }
            shaped[k] = value || "";
          });
          if (!shaped.Email || shaped.Email === "(Missing Email)") {
            const cached = row.__tempEmail || (row.__tempEmail = generateTemporaryEmail(row, idx));
            shaped.Email = String(cached).trim().toLowerCase();
          }
          return shaped;
        });
        rows = [...filteredData, ...mappedInvalid];
      } catch (_) {}
    }
    return rows;
  }, [filteredData, includeTempEmails, invalidData, desiredColumns]);
  const filteredResults = useMemo(() => {
    // Base rows shown in the table. Optionally include invalid rows with temp emails.
    let baseRows = filteredData;
    if (includeTempEmails && Array.isArray(invalidData) && invalidData.length > 0) {
      try {
        const mappedInvalid = invalidData.map((row, idx) => {
          // Conform invalid rows to the same shape as filteredData
          const shaped = {};
          (desiredColumns || []).forEach(k => {
            let value = row[k];
            if (value == null || String(value).trim() === "") {
              const mappedHeader = (columnMappings && columnMappings[k]) ? columnMappings[k] : null;
              if (mappedHeader && Object.prototype.hasOwnProperty.call(row, mappedHeader)) {
                value = row[mappedHeader];
              }
            }
            if (k === "Project Title" && (value == null || String(value).trim() === "")) {
              value = row["Project Name"] || row["Dealname"] || "";
            }
            shaped[k] = value || "";
          });
          if (!shaped.Email || shaped.Email === "(Missing Email)") {
            // Cache a stable temp email per row so it doesn't change across renders
            const cached = row.__tempEmail || (row.__tempEmail = generateTemporaryEmail(row, idx));
            shaped.Email = String(cached).trim().toLowerCase();
          }
          return shaped;
        });
        baseRows = [...filteredData, ...mappedInvalid];
      } catch (_) {}
    }

    // Debug: print comparisons when email whitelist is present and dataset is small
    try {
      const wl = (whitelistFilters?.Email || []).map(v => normalizeForFilter(v));
      if (wl.length && baseRows.length <= 50) {
        baseRows.forEach((r, i) => {
          const raw = r?.Email ?? '';
          const norm = normalizeForFilter(raw);
          console.log('[EmailCompare]', { index: i, rowEmailRaw: raw, rowEmailNorm: norm, filter: wl, match: wl.includes(norm) });
        });
      }
    } catch (_) {}

    const filtered = baseRows.filter(row => {
      const matchesWhitelist = Object.keys(whitelistFilters).every(column => {
        if (!whitelistFilters[column]?.length) return true;
        if (column === 'Email') {
          const cellNorm = normalizeForFilter(row[column] || '');
          return whitelistFilters[column].some(fv => normalizeForFilter(fv) === cellNorm);
        }
        const cellValue = row[column]?.toString().trim().toLowerCase() || "(Blank)";
        return whitelistFilters[column].some(filterValue =>
          cellValue === filterValue.toLowerCase() || (filterValue === "(Blank)" && cellValue === "(Blank)")
        );
      });
  
      const matchesBlacklist = Object.keys(blacklistFilters).every(column => {
        if (!blacklistFilters[column]?.length) return true;
        if (column === 'Email') {
          const cellNorm = normalizeForFilter(row[column] || '');
          return !blacklistFilters[column].some(fv => normalizeForFilter(fv) === cellNorm);
        }
        const cellValue = row[column]?.toString().trim().toLowerCase() || "(Blank)";
        return !blacklistFilters[column].some(filterValue =>
          cellValue === filterValue.toLowerCase() || (filterValue === "(Blank)" && cellValue === "(Blank)")
        );
      });
  
      return matchesWhitelist && matchesBlacklist;
    });
    
    // Phone numbers are normalized when data changes (not on every filter).
    // Avoid re-normalizing on each filter interaction to reduce noise and work.
    return filtered;
  }, [filteredData, whitelistFilters, blacklistFilters, selectedSource, includeTempEmails, invalidData]);
  
  useEffect(() => {
    // console.log("Updated Filters:", whitelistFilters, blacklistFilters);
    // console.log("Filtered Rows:", filteredResults);
  }, [whitelistFilters, blacklistFilters, filteredResults]);
  
  useEffect(() => {
    if (user?.email && rawProjectTitle.trim()) {
      const emailPrefix = user.email.split("@")[0];
      const fullBaseTitle = `${emailPrefix}_${rawProjectTitle.trim()}`;
      // Use simple hash for UI display (deterministic, no HubSpot calls)
      generateSimpleHash(fullBaseTitle).then(projectId => {
        const fullTitle = `${emailPrefix}_${rawProjectTitle.trim()}_${projectId}`;
        setNewProjectTitle(fullTitle);
        setNewProjectID(projectId); // 👈 store project ID
      });
    } else {
      setNewProjectTitle("");
      setNewProjectID(null); // 👈 clear if missing info
    }
  }, [rawProjectTitle, user?.email, selectedSource]);
  

  const addFilter = (column, value, isBlacklist) => {
    if (isBlacklist) {
      setBlacklistFilters(prev => ({
        ...prev,
        [column]: [...new Set([...(prev[column] || []), value])],
      }));
    } else {
      setWhitelistFilters(prev => ({
        ...prev,
        [column]: [...new Set([...(prev[column] || []), value])],
      }));
    }
  };
  

  const columns = desiredColumns
  .filter(column => {
    // Hide Location column from display (but keep data processing intact)
    if (column === "Location") return false;
    
    const mapping = columnMappings[column];
    if (selectedSource === "Lead List") {
      // Hide columns only when mapping is truly not set; keep "__blank__" because it's a real headerless column
      return mapping !== "" && mapping !== undefined && mapping !== null && column !== "Project ID" && column !== "Project Title";
    }
    // For other sources, treat undefined/null/empty string as no match (but allow "__blank__")
    return mapping !== "" && mapping !== undefined && mapping !== null;
  })
  .map(column => {
    // Set smart default widths based on column type
    const getDefaultWidth = (col) => {
      if (col.includes('Description') || col.includes('Title')) return 300;
      if (col.includes('Address')) return 250;
      if (col.includes('Email') || col.includes('Phone')) return 200;
      if (col.includes('Name') || col.includes('Company')) return 180;
      if (col.includes('Types') || col.includes('Uses') || col.includes('Category')) return 160;
      return 120;
    };
    const columnWidth = getDefaultWidth(column);

    // Special handling for all phone columns
    if (column === "Phone" || column === "Mobile Phone" || column === "Company Phone") {
      return {
        name: column,
        selector: row => {
          // If this is a phone column, always normalize the value
          if (row[column]) {
            const normalizedPhone = normalizePhoneNumber(row[column]);
            // Update the actual data too, so exports will have normalized numbers
            row[column] = normalizedPhone;
            return normalizedPhone;
          }
          return "";
        },
        wrap: true,
        sortable: true,
        width: `${columnWidth}px`,
        cell: row => {
          // Always normalize phone numbers when displaying
          if (row[column]) {
            const normalizedPhone = normalizePhoneNumber(row[column]);
            // Update the actual data too
            row[column] = normalizedPhone;
            return <span title={normalizedPhone}>{normalizedPhone}</span>;
          }
          return <span></span>;
        },
      };
    }
    
    // Special handling for Location column
    if (column === "Location") {
      return {
        name: column,
        selector: row => row[column] || "",
        wrap: true,
        sortable: true,
        width: `${columnWidth}px`,
        cell: row => (
          <span title={row[column]}>
            {row[column] || "Unknown Location"}
          </span>
        ),
      };
    }
    
    // Default handling for other columns
    return {
      name: column,
      selector: row => row[column] || "",
      wrap: true,
      sortable: true,
      width: `${columnWidth}px`,
      cell: row => (
        <span title={row[column]}>
          {row[column]?.length > 150 ? `${row[column].slice(0, 100)}...` : row[column]}
        </span>
      ),
    };
  });

  // Add console logs for debugging
  useEffect(() => {
    // console.log("Current columns:", columns);
    // console.log("Current filteredResults:", filteredResults);
    // console.log("Current columnMappings:", columnMappings);
  }, [columns, filteredResults, columnMappings]);

  const toggleFilterMode = () => {
    setIsBlacklistMode(prev => !prev);
  };

  const baseHeaders = ["First Name", "Last Name", "Role", "Company", "Phone", "Mobile Phone", "Company Phone", "Email", "Website", "Address", "City", "State", "ZIP"];

  const predefinedHeaders =
    selectedSource === "Lead List"
      ? baseHeaders // Only contact/company-related columns
      : [
          "Project ID", "First Name", "Last Name", "Project Title", "Role", "Company", 
          "Phone", "Mobile Phone", "Company Phone", "Email", "Website", 
          "Project Description", "Building Uses", "Project Types",
          "Project Category", "Address", "City", "State", "ZIP"
        ];
  
  // Detect duplicate assignments across required headers
  const duplicateAssignments = useMemo(() => {
    const mappingToRequiredHeaders = {};
    try {
      predefinedHeaders.forEach(requiredHeader => {
        const mapped = (columnMappings[requiredHeader] || "").trim();
        if (mapped && mapped !== "__blank__") {
          const key = mapped.toLowerCase();
          if (!mappingToRequiredHeaders[key]) mappingToRequiredHeaders[key] = [];
          mappingToRequiredHeaders[key].push(requiredHeader);
        }
      });
    } catch (_) {
      // noop – renders may occur before all deps are ready
    }
    return mappingToRequiredHeaders;
  }, [columnMappings, predefinedHeaders]);

  // Whether there are conflicting mappings (same detected header used by multiple required fields)
  const hasMappingConflicts = useMemo(() => {
    if (!(selectedSource === "Lead List" || selectedSource === "Project List")) return false;
    return Object.values(duplicateAssignments).some(list => (list?.length || 0) > 1);
  }, [duplicateAssignments, selectedSource]);

  // Compute import disabled state and reason for hover tooltip (removed hasMappingConflicts)
  const isImportDisabled = useMemo(() => {
    return (
      process.env.REACT_APP_DISABLE_IMPORT === 'true' ||
      !isFiltered ||
      // Disable if any contact has Location set to "Unknown Location"
      filteredResults.some(contact => (contact?.Location || '').trim() === 'Unknown Location') ||
      // Disable Leads List imports if more than 1000 rows (HubSpot limit for associated contacts)
      (selectedSource === "Lead List" && filteredResults.length > 1000) ||
      ((selectedSource === "Lead List") && (
        (selectedPreset === "New Preset" && isPresetNameExists(newPresetName.trim())) ||
        !rawProjectTitle.trim()
      )) ||
      ((selectedSource === "Project List") && (selectedPreset === "New Preset") && isPresetNameExists(newPresetName.trim()))
    );
  }, [isFiltered, filteredResults, selectedSource, selectedPreset, newPresetName, rawProjectTitle]);

  const importDisableReason = useMemo(() => {
    if (process.env.REACT_APP_DISABLE_IMPORT === 'true') return 'Import functionality is disabled by admin.';
    if (!isFiltered) return 'Please click "Filter File" first to prepare data.';
    if (filteredResults.some(contact => (contact?.Location || '').trim() === 'Unknown Location')) return 'Cannot Import: Location Unknown.';
    if (selectedSource === 'Lead List') {
      if (filteredResults.length > 1000) return 'Cannot Import: Leads List limited to 1000 contacts (HubSpot limit for associated contacts).';
      if (selectedPreset === 'New Preset' && isPresetNameExists(newPresetName.trim())) return 'Preset name already exists.';
      if (!rawProjectTitle.trim()) return 'Cannot Import: Project Title is empty.';
    }
    if (selectedSource === 'Project List') {
      if (selectedPreset === 'New Preset' && isPresetNameExists(newPresetName.trim())) return 'Preset name already exists.';
    }
    return '';
  }, [isFiltered, filteredResults, selectedSource, selectedPreset, newPresetName, rawProjectTitle]);

  const handleMappingChange = (requiredHeader, selectedValue) => {
    const mappedValue = selectedValue === "__blank__" ? "__blank__" : selectedValue;
    setColumnMappings(prev => ({
      ...prev,
      [requiredHeader]: mappedValue,
    }));
    
    // If this is any phone field mapping changing, normalize phone numbers immediately
    const phoneFields = ["Phone", "Mobile Phone", "Company Phone"];
    if (phoneFields.includes(requiredHeader) && mappedValue !== "__blank__" && mappedValue !== "" && filteredData.length > 0) {
      console.log(`🔄 ${requiredHeader} mapping changed to "${mappedValue}" - normalizing phone numbers immediately`);
      
      setTimeout(() => {
        setFilteredData(prevData => {
          return prevData.map(contact => {
            const updatedContact = {...contact};
            
            // If we have data in the newly mapped column, normalize it
            if (updatedContact[mappedValue]) {
              const originalPhone = updatedContact[mappedValue];
              const normalizedPhone = normalizePhoneNumber(originalPhone);
              
              // Update both the specific phone field and the mapped field
              updatedContact[requiredHeader] = normalizedPhone;
              updatedContact[mappedValue] = normalizedPhone;
              
              console.log(`📱 Normalized ${requiredHeader} after mapping change: ${originalPhone} → ${normalizedPhone}`);
            }
            
            return updatedContact;
          });
        });
        
        console.log(`✅ ${requiredHeader} normalization after mapping change complete`);
      }, 100);
    }
  };
  
  const hubspotHeaders = [
    "First Name",
    "Last Name",
    "Contact Title",
    "Phone Number",
    "Mobile Phone Number",
    "Company Phone Number",
    "Email",
    "Website URL",
    "Street Address",
    "City",
    "State/Region",
    "Postal Code",
    "Project ID",
    "Project Title",
  ];
  
  const mapToHubspotFields = (contacts, projectID, source) => {
    return contacts.map(contact => {
      // Base fields for all sources
      const baseFields = {
        "First Name": selectedSource === "PlanHub" || selectedSource === "ConstructConnect"
          ? contact["Name"]?.trim() || ""
          : contact["First Name"]?.trim() || "",
        "Last Name": selectedSource === "PlanHub" || selectedSource === "ConstructConnect"
          ? ""
          : contact["Last Name"]?.trim() || "",
        "Contact Title": contact["Contact Title"] || contact["Title"] || contact["Role"] ||"",
        "Phone Number": contact["Phone"] || "",
        "Email": contact["Contact Email"] || contact["Email"] || "",
        "Website URL": contact["Website"] || "",
        "Street Address": contact["Address"] || "",
        "City": contact["City"] || "",
        "State/Region": contact["State"] || "",
        "Postal Code": contact["Zip"] || contact["ZIP"] || "",
        "Project ID": contact["Project ID"] || projectID || "",
        "Project Title": contact["Project Title"] || "",
      };
      console.log("🔹 Base Fields:", baseFields);
      // Only add mobile and company phone fields for Lead List and Project List sources
      if (selectedSource !== "PlanHub" && selectedSource !== "ConstructConnect") {
        baseFields["Mobile Phone Number"] = contact["Mobile Phone"] || "";
        baseFields["Company Phone Number"] = contact["Company Phone"] || "";
      }
      
      return baseFields;
    });
  };    

  const synonymList = {
    "First Name": ["First Name", "Contact Name", "Full Name", "First"],
    "Last Name": ["Last Name", "Family Name", "Last", "Surname"],
    "Name": ["Contact Name", "Full Name"],
    "Role": ["Title", "Job Title", "Position", "Job Role"],
    "Email": ["Contact Email", "E-mail", "Email Address", "Primary Email"],
    "Phone": ["Phone Number", "Primary Phone", "Work Phone", "Office Phone", "Business Phone", "Main Phone", "Contact Phone"],
    "Mobile Phone": ["Mobile", "Cell", "Mobile Number", "Cell Phone", "Cell Number", "Cellular"],
    "Company Phone": ["Company Phone", "HQ Phone", "Business Phone", "Company Number", "Fax", "Fax Number", "Office Phone Number"],
    "Website": ["Company URL", "Web", "Site", "URL", "Web Address"],
    "Address": ["Street", "Street Address", "Mailing Address", "Business Address", "Company Address"],
    "ZIP": ["Zip", "Postal Code", "Zipcode", "ZIP Code"],
    "State": ["Region", "Province", "State/Region"],
    "City": ["Town", "Municipality"],
    "Company": ["Business", "Organization", "Employer", "Company Name"],
    "Project Title": ["Project Name"],
    "Project Types": ["Project Type"],
  };
  
  const detectHeadersAndMap = (headers) => {
    let newMappings = {};
  
    console.log("🟠 Running detectHeadersAndMap()...");
    console.log("🔹 Expected Desired Columns:", desiredColumns);
    console.log("🔹 Received Detected Headers:", headers);
  
    desiredColumns.forEach(requiredHeader => {
      // For Lead List, we want to preserve Project ID and Title
      // Skip Mobile Phone and Company Phone for PlanHub and ConstructConnect
      if ((selectedSource === "PlanHub" || selectedSource === "ConstructConnect") && 
          (requiredHeader === "Mobile Phone" || requiredHeader === "Company Phone")) {
        return;
      }
      
      if (selectedSource === "Lead List" && (requiredHeader === "Project Title" || requiredHeader === "Project ID")) {
        newMappings[requiredHeader] = requiredHeader; // Map to itself to preserve the value
        return;
      }

      let bestMatch = "";
      let bestScore = 0;
  
      const synonyms = synonymList[requiredHeader] || [];

      // Special handling for phone fields - look for all three types
      if (requiredHeader === "Phone" || requiredHeader === "Mobile Phone" || requiredHeader === "Company Phone") {
        for (let detectedHeader of headers) {
          const lowerDetected = detectedHeader.toLowerCase();
          
          // Prevent cross-matching between phone types
          if (requiredHeader === "Phone" && (lowerDetected.includes("mobile") || lowerDetected.includes("cell") || 
              lowerDetected.includes("fax") || lowerDetected.includes("company phone"))) {
            continue;
          }
          
          if (requiredHeader === "Mobile Phone" && (lowerDetected === "phone" || lowerDetected.includes("work") || 
              lowerDetected.includes("fax") || lowerDetected.includes("company phone"))) {
            continue;
          }
          
          if (requiredHeader === "Company Phone" && (lowerDetected === "phone" || lowerDetected.includes("mobile") || 
              lowerDetected.includes("cell"))) {
            continue;
          }
          
          // Match phone field types
          let similarity = calculateSimilarity(requiredHeader.trim(), detectedHeader.trim());
          
          if (lowerDetected === requiredHeader.toLowerCase()) {
            similarity += 0.3;
          }
          
          if (synonyms.some(syn => lowerDetected.includes(syn.toLowerCase()))) {
            similarity += 0.15;
          }
          
          if (similarity > bestScore) {
            bestMatch = detectedHeader;
            bestScore = similarity;
          }
        }
      } else {
        // Original logic for non-phone fields
        for (let detectedHeader of headers) {
          const lowerDetected = detectedHeader.toLowerCase();
        
          // 🔒 Prevent Address → Email Address
          if (requiredHeader === "Address" && lowerDetected.includes("email")) continue;
        
          // ✅ If exact synonym match, lock it in immediately
          if (synonyms.some(syn => lowerDetected === syn.toLowerCase())) {
            bestMatch = detectedHeader;
            break;
          }
        
          // ✅ Otherwise, use similarity scoring
          let similarity = calculateSimilarity(requiredHeader.trim(), detectedHeader.trim());
        
          if (lowerDetected === requiredHeader.toLowerCase()) {
            similarity += 0.3;
          }
        
          if (synonyms.some(syn => lowerDetected.includes(syn.toLowerCase()))) {
            similarity += 0.15;
          }
        
          if (similarity > bestScore) {
            bestMatch = detectedHeader;
            bestScore = similarity;
          }
        }
      }
      newMappings[requiredHeader] = bestMatch || "";
    });
  
    console.log("🟢 Updated Column Mappings Before SetState:", newMappings);
    setColumnMappings(newMappings);
    setInitialDetectedHeaders(newMappings);
  };
    
  const resetImportState = () => {
    setFileData(null);
    setFileInfo({ name: '', type: '' });
    setFilteredData([]);
    setInvalidData([]);
    setDuplicateData([]);
    setDetectedHeaders([]);
    setColumnMappings({});
    setSelectedPreset("");
    setNewPresetName("");
    setEmailColumn("");
    setZipsColumn("");
    setProjectIdColumn("");
    setAddProjectID(false);
    setRawProjectTitle("");
    setNewProjectTitle("");
    setNewProjectID(null);
    setSheetNames([]);
    setSelectedSheet("");
    setOriginalFile(null);
    setInitialDetectedHeaders([]);
    setIsFiltered(false);
    setShouldFlatten(false);
    setWhitelistFilters({});
    setBlacklistFilters({});
    // Reset the usedHashes Set to prevent collision issues
    usedHashes.clear();
  };
    
  // Add this useEffect to handle phone normalization when column mappings change
  useEffect(() => {
    // Only run if we have filtered data and any phone mapping changes
    if (filteredData.length > 0) {
      console.log("🔄 Column mappings changed - checking if phone normalization is needed");
      
      // Check all three phone field types
      const phoneFields = ["Phone", "Mobile Phone", "Company Phone"];
      
      phoneFields.forEach(field => {
        const phoneMapping = columnMappings[field];
        if (phoneMapping && phoneMapping !== "__blank__" && phoneMapping !== "") {
          console.log(`📱 ${field} is mapped to "${phoneMapping}" - normalizing phone numbers`);
          
          // Normalize phone numbers in filteredData
          setFilteredData(prevData => {
            return prevData.map(contact => {
              const updatedContact = {...contact};
              
              // If we have data in the mapped column, normalize it
              if (updatedContact[phoneMapping]) {
                const originalPhone = updatedContact[phoneMapping];
                const normalizedPhone = normalizePhoneNumber(originalPhone);
                
                // Update the specific phone field
                updatedContact[field] = normalizedPhone;
                
                // console.log(`📱 Normalized ${field} from mapping: ${originalPhone} → ${normalizedPhone}`);
              }
              
              return updatedContact;
            });
          });
        }
      });
    }
  }, [columnMappings, filteredData.length]);
  
  useEffect(() => {
    // console.log("Updated Filters:", whitelistFilters, blacklistFilters);
    // console.log("Filtered Rows:", filteredResults);
  }, [whitelistFilters, blacklistFilters, filteredResults]);

  const extractSynonymsFromPresets = (presets) => {
    const mergedSynonyms = { ...synonymList };
    
    presets.forEach(preset => {
      if (preset.mappings) {
        Object.entries(preset.mappings).forEach(([requiredHeader, detectedHeader]) => {
          if (!mergedSynonyms[requiredHeader]) {
            mergedSynonyms[requiredHeader] = [];
          }
          if (detectedHeader && !mergedSynonyms[requiredHeader].includes(detectedHeader)) {
            mergedSynonyms[requiredHeader].push(detectedHeader);
          }
        });
      }
    });
    
    return mergedSynonyms;
  };

  const handleCollisionResolution = async (resolution) => {
    try {
      console.log("🔍 handleCollisionResolution called with:", resolution);
      
      if (resolution.action === 'update') {
        console.log("📝 Using existing deal ID:", resolution.dealId);
        // Return the existing deal ID to use
        return resolution.dealId;
      } else if (resolution.action === 'create_new') {
        console.log("🆕 Creating new project with incremented title");
        
        try {
        // Smart incrementing logic for new project title
        const api = createApiInstance();
        const hubkey = user?.role === 'agent' ? user?.selectedFranchisee?.client_type?.hubspot_api_key : selectedFranchisee?.client_type?.hubspot_api_key;
        
        console.log("🔑 HubSpot API key available:", hubkey ? 'Yes' : 'No');
        console.log("👤 User email:", user?.email);
        console.log("📋 Raw project title:", rawProjectTitle);
        
        if (!hubkey) throw new Error('No HubSpot API key available');

        // Use the clean project title and user email prefix from state
        const userPrefix = user?.email ? user.email.split('@')[0] : '';
        const cleanProjectTitle = rawProjectTitle.trim();
        
        console.log("🏷️ User prefix:", userPrefix);
        console.log("🎯 Clean project title:", cleanProjectTitle);
        
        // Construct the base title in the correct format: prefix_projectTitle
        const baseTitle = userPrefix ? `${userPrefix}_${cleanProjectTitle}` : cleanProjectTitle;
        
        console.log("🎪 Base title for search:", baseTitle);

        // Search for all deals with similar titles
        console.log("🔍 Searching for existing deals...");
        const searchResponse = await api.post('/searchDeal', {
          title: baseTitle,
          hubkey: hubkey,
          operator: 'CONTAINS_TOKEN',
          limit: 100
        });
        
        console.log("📊 Search response:", searchResponse.data);
        
        const existingTitles = (searchResponse.data?.results || []).map(
          d => d.properties?.dealname || ''
        );
        
        console.log("📋 Existing titles found:", existingTitles);

        // Find all titles matching the pattern: baseTitle, baseTitle (2), baseTitle (3), ...
        let maxN = 1;
        const regex = new RegExp(`^${baseTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?: \\((\\d+)\\))?(_\\d+)?$`);
        existingTitles.forEach(title => {
          const match = title.match(regex);
          if (match) {
            const n = match[1] ? parseInt(match[1], 10) : 1;
            if (n > maxN) maxN = n;
          }
        });
        const nextN = maxN + 1;
        const newTitle = nextN === 2 ? `${baseTitle} (2)` : `${baseTitle} (${nextN})`;
        
        console.log("🔢 Next increment number:", nextN);
        console.log("📝 New title:", newTitle);
        
        // Generate new project ID based on the new title
        console.log("🎲 Generating new project ID...");
        const newId = await generateNumericHash(newTitle);
        
        console.log("🆔 Generated new ID:", newId);
        console.log("✅ Returning collision resolution result:", { newId, newTitle });
        
        // Return both new title and new ID
        return { newId, newTitle };
        } catch (error) {
          console.error('❌ Error creating new project:', error);
          throw error; // Re-throw to be caught by the outer catch
        }
      } else if (resolution.action === 'change_deal_name') {
        console.log("✏️ User chose to change deal name - cancelling import to allow modification");
        // Return null to indicate the import should be cancelled
        return null;
                  }
                } catch (error) {
      console.error('❌ Error handling collision resolution:', error);
      console.error('❌ Error stack:', error.stack);
      throw error; // Re-throw to be caught by the promise handler
    }
  };

  useEffect(() => {
    console.log("Modal visibility changed:", showCollisionModal);
    console.log("Current collision data:", collisionData);
  }, [showCollisionModal, collisionData]);

  const applyExclusionsToImportData = (updatedProjectsData, cleanContactData, excludedDealNames = []) => {
    console.log("🔍 [applyExclusionsToImportData] Excluded deal names:", excludedDealNames);
    console.log("🔍 [applyExclusionsToImportData] Projects before filtering:", updatedProjectsData.length);
    console.log("🔍 [applyExclusionsToImportData] Contacts before filtering:", cleanContactData.length);
    
    if (excludedDealNames.length > 0) {
      // Extract original project IDs from excluded deal names
      // Deal names format: projectTitle_projectID
      const excludedProjectIds = new Set();
      excludedDealNames.forEach(dealName => {
        const parts = dealName.split('_');
        if (parts.length > 1) {
          const projectId = parts[parts.length - 1]; // Last part is the project ID
          excludedProjectIds.add(projectId);
          console.log(`🔍 [applyExclusionsToImportData] Extracted project ID from excluded deal "${dealName}": ${projectId}`);
        }
      });
      
      console.log("🔍 [applyExclusionsToImportData] Excluded project IDs:", Array.from(excludedProjectIds));
      
      // Filter projects by original project ID
      updatedProjectsData = updatedProjectsData.filter(project => {
        const projectId = project['Project ID'];
        const isExcluded = excludedProjectIds.has(projectId);
        if (isExcluded) {
          console.log(`🔍 [applyExclusionsToImportData] Excluding project with ID: ${projectId}`);
        }
        return !isExcluded;
      });
      
      // Filter contacts by original project ID
      cleanContactData = cleanContactData.filter(contact => {
        const projectId = contact['Project ID'];
        const isExcluded = excludedProjectIds.has(projectId);
        if (isExcluded) {
          console.log(`🔍 [applyExclusionsToImportData] Excluding contact with project ID: ${projectId}`);
        }
        return !isExcluded;
      });
    }
    
    console.log("🔍 [applyExclusionsToImportData] Projects after filtering:", updatedProjectsData.length);
    console.log("🔍 [applyExclusionsToImportData] Contacts after filtering:", cleanContactData.length);
    
    return { updatedProjectsData, cleanContactData };
  };

  // Helper to find the next available incremented deal name
  const getNextAvailableDealName = async (baseTitle, hubkey) => {
    console.log(`🔍 [getNextAvailableDealName] Searching for base title: "${baseTitle}"`);
    
    const searchResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getAllDeals`, {
      hubkey,
      dealName: baseTitle
    });
    
    console.log(`📊 [getNextAvailableDealName] API response:`, searchResponse.data);
    
    const existingTitles = (searchResponse.data?.results || []).map(
      d => d.properties?.dealname || ''
    );
    
    console.log(`📋 [getNextAvailableDealName] Found existing titles:`, existingTitles);

    // Find all increments
    let maxN = 1;
    const regex = new RegExp(`^${baseTitle.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}(?: \\((\\d+)\\))?(_\\d+)?$`);
    console.log(`🔍 [getNextAvailableDealName] Using regex:`, regex);
    
    existingTitles.forEach(title => {
      const match = title.match(regex);
      console.log(`🔍 [getNextAvailableDealName] Checking title "${title}" - match:`, match);
      if (match) {
        const n = match[1] ? parseInt(match[1], 10) : 1;
        console.log(`🔢 [getNextAvailableDealName] Found increment: ${n}`);
        if (n >= maxN) maxN = n + 1;
      }
    });
    
    console.log(`🔢 [getNextAvailableDealName] Max increment found: ${maxN - 1}, next increment: ${maxN}`);

    // Compose the next available name
    const nextName = maxN === 1 ? baseTitle : `${baseTitle} (${maxN})`;
    console.log(`✅ [getNextAvailableDealName] Returning next name: "${nextName}"`);
    return nextName;
  };

  // Derived styles/text for import type badge in the header
  const importTypeBg = selectedSource === "ConstructConnect"
    ? "bg-green-600"
    : selectedSource === "PlanHub"
    ? "bg-yellow-600"
    : selectedSource === "Project List"
    ? "bg-blue-600"
    : selectedSource === "Lead List"
    ? "bg-blue-500"
    : "bg-gray-600";

  const importTypeText = selectedSource === "ConstructConnect" ? "Construct Connect" : (selectedSource || "");

  return (
    <div className='bg-gray-900'>
      <div className={`${currentStep === 1 ? "max-w-7xl" : ""} mx-auto mt-6`}>
        <div className="flex flex-col justify-center items-center"> 
          <h4 className='text-white text-sm sm:text-sm md:text-sm lg:text-4xl uppercase'>{user?.role === 'agent' ? user?.selectedFranchisee?.name : selectedFranchisee?.name}</h4>
            <h4 className='text-hs-orange pt-1 text-sm sm:text-sm md:text-sm lg:text-xl flex items-center gap-2'>
              <span>Import</span>
              {importTypeText && (
                <span className={`px-2 py-0.5 rounded text-white ${importTypeBg}`}>
                  {importTypeText}
                </span>
              )}
              <span>File to HubSpot</span>
          </h4>
        </div>
        {!(currentStep === 2 || currentStep === 3) && (
          <div className="flex flex-row items-center justify-center space-x-4 mt-4 mb-6">
            <div className="bg-gray-800 p-2 rounded-lg flex gap-[8px] items-center">
              {["PlanHub", "ConstructConnect", "Project List", "Lead List"].map((source) => (
                <button
                  key={source}
                  onClick={() => {
                    if (selectedSource !== source && typeof onChangeSource === 'function') {
                      onChangeSource(source);
                    }
                  }}
                  className={`px-4 py-2 rounded-md text-white transition ${
                    selectedSource === source 
                      ? source === "ConstructConnect" 
                        ? "bg-green-600" 
                        : source === "PlanHub" 
                        ? "bg-yellow-600" 
                        : source === "Project List" 
                        ? "bg-blue-600"
                        : "bg-blue-500"
                      : "bg-gray-600 hover:bg-gray-500"
                  }`}
                >
                  {source}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setIsImportTypesHelpOpen(true)}
                className="ml-2 h-8 w-8 flex items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-600 focus:outline-none"
                aria-label="What do these import types do?"
                title="What do these import types do?"
              >
                ?
              </button>
            </div>
          </div>
        )}
        {/* Debug: Log selectedFranchisee structure */}
        {/* {console.log("🔍 Debug - selectedFranchisee:", selectedFranchisee)}
        {console.log("🔍 Debug - selectedFranchisee?.client_type:", selectedFranchisee?.client_type)}
        {console.log("🔍 Debug - selectedFranchisee?.name:", selectedFranchisee?.name)}
        {console.log("🔍 Debug - selectedFranchisee keys:", Object.keys(selectedFranchisee || {}))}
        {console.log("🔍 Debug - selectedFranchisee?.assoc_client_type:", selectedFranchisee?.assoc_client_type)}
        {console.log("🔍 Debug - selectedFranchisee?.hubspot_api_key:", selectedFranchisee?.hubspot_api_key)} */}
        {showCollisionModal && collisionData && (
          <CollisionModal
            collision={collisionData}
            hubkey={user?.selectedFranchisee?.client_type?.hubspot_api_key || null}
            onResolve={async (resolution) => {
              console.log("Modal resolution callback triggered:", resolution);
              
              if (resolution.action === 'create_new') {
                console.log("🆕 Creating new project from CollisionModal");
                
                try {
                  // Call the collision resolution logic
                  const result = await handleCollisionResolution(resolution);
                  
                  // Keep loading spinner active - will close when entire import completes
                  return result;
                } catch (error) {
                  // Close the loading modal on error
                  Swal.close();
                  setCurrentLoadingModal(null);
                  console.error('❌ Error creating new project:', error);
                  throw error;
                }
              } else if (resolution.action === 'update') {
                console.log("📝 Updating existing project from CollisionModal");
                
                try {
                  // Call the collision resolution logic
                  const result = await handleCollisionResolution(resolution);
                  
                  // Keep loading spinner active - will close when entire import completes
                  return result;
                } catch (error) {
                  // Close the loading modal on error
                  Swal.close();
                  setCurrentLoadingModal(null);
                  console.error('❌ Error updating project:', error);
                  throw error;
                }
              }
              
              // Handle other actions if needed
              if (onResolve.current) {
                onResolve.current(resolution);
              }
            }}
            onClose={() => {
              console.log("Modal close triggered");
              setShowCollisionModal(false);
            }}
          />
        )}
        {/* Step 1: File Upload */}
        {currentStep === 1 && (
          <>
        {/* file upload component  */}
        <div className="mx-16 my-2" onDrop={handleDrop} onDragOver={handleDragOver}>
          <label
              className="flex justify-center w-full h-[300px] transition bg-hs-dark-gray border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-hs-background focus:outline-none hover:bg-hs-light-gray">
              <div className="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round"
                              d="M7 16a4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div className="font-medium text-white">
                      Drop files to upload, or
                      <span className="text-blue-600 underline"> browse</span>
                    <p className='text-hs-background'>{fileInfo.name || 'No file selected'}</p>
                                                         {selectedSource === "Project List" && (
                               <p className='text-sm text-yellow-300 absolute left-1/2 transform -translate-x-1/2'>Remember to turn on/off the buttons below before uploading</p>
                             )}
                  </div>
                  <div>
                  </div>
              </div>
              <input type="file" name="file_upload" accept=".csv,.xlsx" className="hidden" onChange={handleFileChange} />
          </label>
        </div>
          </>
        )}
        {/* Column Selection Dropdowns - Step 1 */}
        {currentStep === 1 && (selectedSource === "Lead List" || selectedSource === "Project List") && fileData?.length > 0 && (
          <div className="flex flex-col items-center justify-center mt-4">
            <div className="flex gap-4 items-start">
              {sheetNames.length > 1 && (
                <div className="flex flex-col">
                  <label className="text-white text-sm mb-1">Select Sheet</label>
                  <select
                    value={selectedSheet}
                    onChange={(e) => {
                      const newSheet = e.target.value;
                      setSelectedSheet(newSheet);
                      // Reset all column selections
                      setEmailColumn("");
                      setZipsColumn("");
                      setProjectIdColumn("");
                      setColumnMappings({});
                      setFilteredData([]);
                      setIsFiltered(false);
                      convertXLSXtoCSV(originalFile, newSheet);
                    }}
                    className="w-64 p-2 rounded bg-gray-700 text-white border border-gray-600"
                  >
                    <option value="">-- Select Sheet --</option>
                    {sheetNames.map((sheet, idx) => (
                      <option key={idx} value={sheet}>
                        {sheet}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-col">
                <label className="text-white text-sm mb-1">Select Email Column (Optional)</label>
                <select
                  value={emailColumn}
                  onChange={(e) => setEmailColumn(e.target.value)}
                  className="w-64 p-2 rounded bg-gray-700 text-white border border-gray-600"
                >
                  <option value="">-- Select Email Column --</option>
                  {detectedHeaders.map((header) => {
                    const mergedSynonyms = extractSynonymsFromPresets(presets);
                    const emailSynonyms = mergedSynonyms["Email"] || [];
                    const isEmailMatch = header.toLowerCase().includes("email") || 
                      emailSynonyms.some(syn => header.toLowerCase().includes(syn.toLowerCase()));
                    
                    return (
                      <option 
                        key={header} 
                        value={header}
                        className={isEmailMatch ? "text-green-400" : ""}
                      >
                        {header} {isEmailMatch ? "✓" : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-white text-sm mb-1">Select Zips Column (Optional)</label>
                <select
                  value={zipsColumn}
                  onChange={(e) => setZipsColumn(e.target.value)}
                  className="w-64 p-2 rounded bg-gray-700 text-white border border-gray-600"
                >
                  <option value="">-- Select Zip Column --</option>
                  {detectedHeaders.map((header) => {
                    const mergedSynonyms = extractSynonymsFromPresets(presets);
                    const zipSynonyms = mergedSynonyms["ZIP"] || [];
                    const isZipMatch = header.toLowerCase().includes("zip") || 
                      header.toLowerCase().includes("postal") ||
                      zipSynonyms.some(syn => header.toLowerCase().includes(syn.toLowerCase()));
                    
                    return (
                      <option 
                        key={header} 
                        value={header}
                        className={isZipMatch ? "text-green-400" : ""}
                      >
                        {header} {isZipMatch ? "✓" : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              {addProjectID && selectedSource === "Project List" && (
                <div className="flex flex-col">
                  <label className="text-white text-sm mb-1">Select Project Title Column (Required)</label>
                  <select
                    value={projectIdColumn}
                    onChange={(e) => setProjectIdColumn(e.target.value)}
                    className="w-64 p-2 rounded bg-gray-700 text-white border border-gray-600"
                  >
                    <option value="">-- Select Column --</option>
                    {detectedHeaders.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsDropdownsHelpOpen(true)}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-600 mt-[1.7rem]"
                aria-label="How to choose these fields"
                title="How to choose these fields"
              >
                ?
              </button>
            </div>
          </div>
        )}

        {isImportTypesHelpOpen && (
          <GuideModal
            isOpen={isImportTypesHelpOpen}
            onClose={() => setIsImportTypesHelpOpen(false)}
            footerExtras={(
              <>
                <a
                  href="https://www.loom.com/share/a1c011e5a965413aa72904dc23ba63fa?sid=3e74e973-10b1-4aef-9959-bc4d404772b6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-900 text-sm"
                >
                  Watch PlanHub Demo
                </a>
                <a
                  href="https://www.loom.com/share/9d132014778949399243dafe118616aa?sid=aae32e65-6415-48cd-936b-4ba599b50af1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-900 text-sm"
                >
                  Watch ConstructConnect Demo
                </a>
                <a
                  href="https://www.loom.com/share/fcf6d9b68a8e4e0ea7dc0298c10517d9?sid=1a2ddfc2-5d0d-40bd-9d2f-fc3f67d72aaa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-900 text-sm"
                >
                  Watch Project List Demo
                </a>
                <a
                  href="https://www.loom.com/share/c41cd7bd7e384a91bf22fdff40a7eb45?sid=5ce17c00-114e-4103-81e1-2b4c27471b55"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-900 text-sm"
                >
                  Watch Lead List Demo
                </a>
              </>
            )}
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 shadow-lg">
              <div className="text-gray-800">
                <h3 className="text-xl font-bold mb-4 text-center text-gray-900">Import Types Overview</h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <span className="font-semibold text-gray-900">PlanHub</span>
                        <span className="text-gray-700">: CSV exported from <u>PlanHub</u>.</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <span className="font-semibold text-gray-900">ConstructConnect</span>
                        <span className="text-gray-700">: CSV exported from <u>ConstructConnect</u>.</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <span className="font-semibold text-gray-900">Project List</span>
                        <span className="text-gray-700">: Use for sources <u>outside PlanHub/ConstructConnect</u> where each contact is associated to a specific project (rows include a Project ID and project fields).</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                      <div>
                        <span className="font-semibold text-gray-900">Lead List</span>
                        <span className="text-gray-700">: Use when you <u>only have contacts/leads (no Projects)</u>. All leads in this upload will be <i>grouped under a single HubSpot deal that you name</i>.</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 font-medium">
                    💡 <strong>Tip:</strong> After selecting an import type, upload your file and configure any pre-filters before clicking "Filter File".
                  </p>
                </div>
              </div>
            </div>
          </GuideModal>
        )}

        {/* Step 1: Filter Settings and Filter File Button */}
        {currentStep === 1 && (
        <div className='flex flex-row justify-center items-center gap-2 m-4'>

          {(selectedSource === "Project List") && (
            <>
              <div className='flex items-center gap-2'>
                <button 
                  onClick={() => setShouldFlatten(prev => !prev)}
                  className={`transition-all ease-in-out p-4 rounded-md text-hs-background ${
                    shouldFlatten ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-800'
                  } text-white`}
                >
                  {shouldFlatten ? "✅ Flatten Contacts: ON" : "➕ Flatten Contacts: OFF"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsFlattenHelpOpen(true)}
                  className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-600"
                  aria-label="What does Flatten Contacts do?"
                  title="What does Flatten Contacts do?"
                >
                  ?
                </button>
              </div>
              {(
                <div className='flex items-center gap-2'>
                  <button 
                    onClick={() => setAddProjectID(prev => !prev)}
                    className={`transition p-4 rounded-md text-white ${addProjectID ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-800'}`}
                  >
                    {addProjectID ? "✅ Add Project ID: ON" : "➕ Add Project ID: OFF"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAddProjectIdHelpOpen(true)}
                    className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-600"
                    aria-label="What does Add Project ID do?"
                    title="What does Add Project ID do?"
                  >
                    ?
                  </button>
                </div>
              )}
            </>
          )}

          {/* Step 3: Filter Settings */}
          <button 
            onClick={handleModalFilter}
            className={`transition-all ease-in-out flex flex-row justify-center items-center gap-2 bg-hs-blue p-4 rounded-md text-hs-background hover:bg-hs-dark-blue `}
          >
            <AdjustmentsHorizontalIcon className='h-6 w-6'/>
            <p className='font-hs-font'>Filter Settings</p>
          </button>

          {/* Step 4: Filter File */}
          <button 
            onClick={filterCSV}
            className={`transition-all ease-in-out p-4 bg-hs-light-gray rounded-md text-hs-background hover:bg-[#252535ec] 
              ${!fileData ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!fileData}
          >
            <p className='font-hs-font'>Filter File</p>
          </button>
        </div>
        )}
        {/* Forward nav from Step 1 to Step 2 when summary is ready */}
        {currentStep === 1 && isFiltered && (
          <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-50">
            <button
              onClick={() => setCurrentStep(2)}
              className="bg-hs-orange/60 hover:bg-hs-orange-dark/80 text-white w-16 h-16 rounded-full font-extrabold text-2xl transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-110 flex items-center justify-center"
              title="Next: Summary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Step 2: File Summary and Download Buttons */}
        {currentStep === 2 && isFiltered && (
          <div className="w-full min-h-[60vh] grid place-items-center">
            <div className="w-full max-w-screen-2xl flex flex-col items-center justify-center gap-6">
            {/* Filter Summary */}
            {(() => { window.setIsSummaryHelpOpen = setIsSummaryHelpOpen; return null; })()}
            <DisplayContactCounts 
              validContacts={filteredResults}
              invalidContacts={includeTempEmails ? [] : invalidData}
              duplicateEmailContacts={duplicateData}
            />
          
            <div className='flex flex-col bg-gray-900'>
              <div className='flex flex-row gap-2 justify-center'>
                <button onClick={downloadCompanyCSV} className='flex items-center bg-green-100 text-green-900 p-2 rounded-lg border-green-700 border-2 hover:bg-green-700 hover:text-white transition ease-in-out'>
                  <img src="/gsheets_logo.png" height={15} width={20} alt='' className='mr-2' /> Download Companies CSV
                </button>
                <button onClick={downloadFilteredCSV} className='flex items-center bg-green-100 text-green-900 p-2 rounded-lg border-green-700 border-2 hover:bg-green-700 hover:text-white transition ease-in-out'>
                  <img src="/gsheets_logo.png" height={15} width={20} alt='' className='mr-2' /> Download Contacts with Emails CSV
                </button>
                {selectedSource !== "Lead List" && (
                <button onClick={downloadProjectCSV} className='flex items-center bg-green-100 text-green-900 p-2 rounded-lg border-green-700 border-2 hover:bg-green-700 hover:text-white transition ease-in-out'>
                  <img src="/gsheets_logo.png" height={15} width={20} alt='' className='mr-2' /> Download Projects CSV
                  </button>
                )}
                <button onClick={downloadInvalidAndDuplicateCSV} className='flex items-center bg-red-100 text-red-900 p-2 rounded-lg border-red-800 border-2 hover:bg-red-800 hover:text-white transition ease-in-out'>
                  <img src="/gsheets_logo.png" height={15} width={20} alt='' className='mr-2' /> Download Invalid + Duplicate Emails CSV
                </button>
                
              </div >
              {/* Big, prominent toggle below buttons */}
              <div className='mt-6 flex justify-center relative'>
                <div className='flex items-center gap-4 px-5 py-4 rounded-2xl bg-gray-800 text-white border-2 border-green-400 shadow-2xl'>
                  <span className='text-xl font-bold tracking-wide'>Generate temp emails (Invalid Contacts)</span>
                  <button
                    type='button'
                    onClick={() => setIncludeTempEmails(prev => !prev)}
                    className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors duration-200 ${includeTempEmails ? 'bg-green-500' : 'bg-gray-600'}`}
                    role='switch'
                    aria-checked={includeTempEmails}
                    aria-label='Generate temp emails (Invalid Contacts)'
                    title='Generate temp emails (Invalid Contacts)'
                  >
                    <span className={`inline-block h-10 w-10 transform bg-white rounded-full shadow-md transition-transform duration-200 ${includeTempEmails ? 'translate-x-12' : 'translate-x-2'}`}></span>
                  </button>
            </div>
            </div>
            </div>
            </div>
            {/* Collision Detection Section */}
              {includeTempEmails && (invalidData && invalidData.length > 0 || collisionContacts && collisionContacts.length > 0) && (
                <div className='mt-4 space-y-4 bg-gray-100 p-4 rounded-lg'>
                  {/* Full Name Toggle */}
                  <div className='flex flex-col items-center space-y-2'>
                    <label className='flex items-center space-x-2'>
                      <input
                        type="checkbox"
                        checked={useFullName}
                        onChange={(e) => {
                          setUseFullName(e.target.checked);
                          if (e.target.checked) {
                            // Clear separate name selections when switching to full name
                            setSelectedFirstNameColumn('');
                            setSelectedLastNameColumn('');
                          } else {
                            // Clear full name selection when switching to separate names
                            setSelectedNameColumn('');
                          }
                        }}
                        className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                      />
                      <span className='text-sm font-medium text-gray-700'>
                        Use Full Name (single column)
                      </span>
                    </label>
                  </div>

                  {/* Name Column Selection */}
                  <div className='flex flex-col items-center space-y-2'>
                    {useFullName ? (
                      <>
                        <label className='text-sm font-medium text-gray-700'>
                          Full Name Column:
                        </label>
                        <select
                          value={selectedNameColumn}
                          onChange={(e) => setSelectedNameColumn(e.target.value)}
                          className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]'
                        >
                          <option value=''>Select</option>
                          {originalInvalidData && originalInvalidData.length > 0 && Object.keys(originalInvalidData[0]).map((column) => (
                            <option key={column} value={column}>
                              {column}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <div className='flex flex-row w-full max-w-md gap-4'>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium text-gray-700'>
                            First Name Column:
                          </label>
                          <select
                            value={selectedFirstNameColumn}
                            onChange={(e) => setSelectedFirstNameColumn(e.target.value)}
                            className='mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                          >
                            <option value=''>Select</option>
                            {originalInvalidData && originalInvalidData.length > 0 && Object.keys(originalInvalidData[0]).map((column) => (
                              <option key={column} value={column}>
                                {column}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium text-gray-700'>
                            Last Name Column:
                          </label>
                          <select
                            value={selectedLastNameColumn}
                            onChange={(e) => setSelectedLastNameColumn(e.target.value)}
                            className='mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                          >
                            <option value=''>Select</option>
                            {originalInvalidData && originalInvalidData.length > 0 && Object.keys(originalInvalidData[0]).map((column) => (
                              <option key={column} value={column}>
                                {column}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Collision Detection Button */}
                  <div className='flex flex-col items-center gap-2'>
                    <button
                      onClick={checkNameCollisions}
                      disabled={useFullName ? !selectedNameColumn : (!selectedFirstNameColumn || !selectedLastNameColumn)}
                      className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2 ${
                        (useFullName ? selectedNameColumn : (selectedFirstNameColumn && selectedLastNameColumn))
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {collisionContacts && collisionContacts.length > 0 
                        ? 'Review Name Collisions' 
                        : 'Check for Name Collisions'
                      }
                    </button>
                    <a
                      href="https://www.loom.com/share/e983d6a7289645b6b0593ce317b198d7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      📹 Watch Tutorial
                    </a>
                  </div>
                </div>
              )}
        {/* Navigation buttons */}
            <div className="fixed left-8 top-1/2 transform -translate-y-1/2 z-50">
              <button
                onClick={() => setCurrentStep(1)}
                className="bg-gray-600/60 hover:bg-gray-700/80 text-white w-16 h-16 rounded-full font-extrabold text-2xl transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-110 flex items-center justify-center"
                title="Back to Upload"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5" />
                  <path d="M11 19l-7-7 7-7" />
                </svg>
              </button>
            </div>
            {(filteredData.length > 0 || (includeTempEmails && invalidData.length > 0)) && (
              <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-50">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="bg-hs-orange/60 hover:bg-hs-orange-dark/80 text-white w-16 h-16 rounded-full font-extrabold text-2xl transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-110 flex items-center justify-center"
                  title="Next: View Data & Import"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                    <path d="M13 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
        {/* Step 3: DataTable, Filters, and Import */}
        {currentStep === 3 && (filteredData.length > 0 || (includeTempEmails && invalidData.length > 0)) && (
                <div className="flex flex-col justify-center items-center">
              <div className="w-full overflow-x-auto mb-6 rounded-lg">
                    <div className="mx-auto w-full max-w-screen-2xl mb-4 flex items-end gap-2 mt-8 px-4">
                      <select
                        ref={columnSelectRef}
                        value={selectedColumn}
                        onChange={(e) => {
                          setSelectedColumn(e.target.value);
                          setSearchText("");
                          if (e.target.value === "All Columns") {
                            // Precompute suggestions across all mapped columns
                            console.log('[FilterUI] building suggestions for All Columns');
                            const allValuesSet = new Set();
                            desiredColumns.forEach(col => {
                              const mapping = columnMappings[col];
                              if (mapping && mapping !== "" && mapping !== "__blank__") {
                                suggestionRows.forEach(row => {
                                  if (row[col]) {
                                    allValuesSet.add(`${row[col]} (${col})`);
                                  }
                                });
                              }
                            });
                            const allValues = Array.from(allValuesSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                            setFilteredSuggestions(allValues);
                            setAllColumnsBase(allValues);
                          } else {
                          setFilteredSuggestions(
                            [...new Set(suggestionRows.map(row => row[e.target.value]).filter(Boolean))]
                              .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                          );
                            // Clear base when not in All Columns mode
                            setAllColumnsBase([]);
                          }
                          // Focus the search input after selecting a column to immediately show suggestions
                          setTimeout(() => {
                            if (searchInputRef.current) {
                              console.log('[FilterUI] focusing search input after column change');
                              searchInputRef.current.focus();
                            }
                          }, 0);
                        }}
                        className="w-full max-w-[23rem] p-2 rounded bg-gray-700 text-white border border-gray-600 text-center"
                      >
                        <option value="Select Column to Filter" className="text-center" style={{ textAlign: 'center' }}>
                          Select Column to Filter
                        </option>
                        <option value="All Columns" className="text-center" style={{ textAlign: 'center' }}>
                          All Columns
                        </option>
                        {desiredColumns
                          .filter(col => {
                            const mapping = columnMappings[col];
                            // Hide if explicitly not matched or mapped to __blank__
                            if (mapping === "" || mapping === "__blank__" || mapping === undefined || mapping === null) return false;
                            // Hide Project ID in Lead List
                            if (selectedSource === "Lead List" && (col === "Project ID" || col === "Project Title")) return false;
                            // Hide Location for all import types
                            if (col === "Location") return false;
                            return true;
                          })
                          .map(col => {
                            const mapping = columnMappings[col];
                            return (
                              <option key={col} value={col} className="text-center" style={{ textAlign: 'center' }}>
                                {col}
                              </option>
                            );
                          })}
                      </select>
                      <div ref={dropdownRef} className="relative w-full flex-1">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder={selectedColumn === "Select Column to Filter" ? "Click to select a column first" : `Search in ${selectedColumn === "All Columns" ? "All" : selectedColumn}`}
                            value={searchText}
                            data-filter-search={true}
                            ref={searchInputRef}
                            onMouseDown={(e) => {
                              console.log('[FilterUI] onMouseDown input', { selectedColumn });
                              if (selectedColumn === 'Select Column to Filter' && columnSelectRef.current) {
                                try {
                                  if (typeof columnSelectRef.current.showPicker === 'function') {
                                    console.log('[FilterUI] onMouseDown using showPicker');
                                    columnSelectRef.current.showPicker();
                                  } else {
                                    console.log('[FilterUI] onMouseDown focus+click fallback');
                                    columnSelectRef.current.focus();
                                    setTimeout(() => columnSelectRef.current.click(), 0);
                                  }
                                } catch (err) {
                                  console.log('[FilterUI] onMouseDown fallback failed', err);
                                }
                              }
                            }}
                            onFocus={(e) => {
                              console.log('[FilterUI] onFocus input', { selectedColumn });
                              if (selectedColumn === 'Select Column to Filter' && columnSelectRef.current) {
                                try {
                                  if (typeof columnSelectRef.current.showPicker === 'function') {
                                    console.log('[FilterUI] onFocus using showPicker');
                                    columnSelectRef.current.showPicker();
                                  } else {
                                    console.log('[FilterUI] onFocus focus+click fallback');
                                    columnSelectRef.current.focus();
                                    setTimeout(() => columnSelectRef.current.click(), 0);
                                  }
                                } catch (err) {
                                  console.log('[FilterUI] onFocus fallback failed', err);
                                }
                              }
                              if (selectedColumn === "Select Column to Filter") {
                                // Let the useEffect handle the focus redirection
                                return;
                              }
                              if (selectedColumn === "All Columns") {
                                console.log('[FilterUI] onFocus for All Columns');
                                const allValuesMap = new Map();
                                desiredColumns.forEach(col => {
                                  const mapping = columnMappings[col];
                                  if (mapping && mapping !== "" && mapping !== "__blank__") {
                                    suggestionRows.forEach(row => {
                                      if (row[mapping]) { // Use mapped column name to access data
                                        const key = `${row[mapping]} (${col})`; // Display desired column name
                                        allValuesMap.set(key, (allValuesMap.get(key) || 0) + 1);
                                      }
                                    });
                                  }
                                });
                                const allValues = Array.from(allValuesMap.entries())
                                  .map(([value, count]) => `${value} (${count})`)
                                  .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                                setFilteredSuggestions(allValues);
                              } else {
                              // suggestionRows already contains mapped data, so use selectedColumn directly
                              console.log('[FilterUI] Single column filter onFocus', { selectedColumn, sampleData: suggestionRows[0] });
                              
                              const valueCounts = new Map();
                              console.log('[FilterUI] Processing rows:', { 
                                rowCount: suggestionRows.length, 
                                firstRow: suggestionRows[0], 
                                selectedColumn,
                                firstRowValue: suggestionRows[0]?.[selectedColumn]
                              });
                              suggestionRows.forEach(row => {
                                const value = row[selectedColumn] || "(Blank)"; // Use selectedColumn directly since data is already mapped
                                valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
                              });
                              console.log('[FilterUI] Value counts:', Object.fromEntries(valueCounts));
                              const allValues = Array.from(valueCounts.entries())
                                .map(([value, count]) => `${value} (${count})`)
                                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                              const filtered = searchText
                                ? allValues.filter(value => value.toLowerCase().includes(searchText.toLowerCase()))
                                : allValues;
                              setFilteredSuggestions(filtered);
                              }
                            }}
                            
                            onChange={(e) => {
                              const query = e.target.value; // Remove trim() to preserve spaces
                              setSearchText(query);

                              if (selectedColumn === "Select Column to Filter") {
                                return; // Don't do anything if no column is selected
                              }
                              if (selectedColumn === "All Columns") {
                                const allValuesMap = new Map();
                                desiredColumns.forEach(col => {
                                  const mapping = columnMappings[col];
                                  if (mapping && mapping !== "" && mapping !== "__blank__") {
                                    suggestionRows.forEach(row => {
                                      if (row[mapping]) { // Use mapped column name to access data
                                        const key = `${row[mapping]} (${col})`; // Display desired column name
                                        allValuesMap.set(key, (allValuesMap.get(key) || 0) + 1);
                                      }
                                    });
                                  }
                                });
                                const allValues = Array.from(allValuesMap.entries())
                                  .map(([value, count]) => `${value} (${count})`)
                                  .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                                const filtered = query
                                  ? allValues.filter(value => value.toLowerCase().includes(query.toLowerCase()))
                                  : allValues;
                                setFilteredSuggestions(filtered);
                              } else {
                              // suggestionRows already contains mapped data, so use selectedColumn directly
                              const valueCounts = new Map();
                              suggestionRows.forEach(row => {
                                const value = row[selectedColumn] || "(Blank)"; // Use selectedColumn directly since data is already mapped
                                valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
                              });
                              const allValues = Array.from(valueCounts.entries())
                                .map(([value, count]) => `${value} (${count})`)
                                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                              const filtered = query
                                ? allValues.filter(value => value.toLowerCase().includes(query.toLowerCase()))
                                : allValues;
                              setFilteredSuggestions(filtered);
                              }
                            }}
                            className="w-full p-2 pr-10 rounded bg-gray-700 text-white border border-gray-600"
                          />

                          {filteredSuggestions.length > 0 && (
                            <div
                              className="absolute inset-y-0 right-2 flex items-center"
                              onMouseDown={(e) => e.preventDefault()} // Prevent input blur on click
                            >
                              <label className="flex items-center text-xs text-white pr-2 cursor-pointer select-none space-x-1">
                                <span className="text-xs text-white pr-1">All </span>
                                <input
                                  type="checkbox"
                                  title="Select/Deselect all"
                                    checked={filteredSuggestions.every(value => {
                                      if (selectedColumn === "All Columns") {
                                        // Parse "Value (DesiredColumn) (Count)" format
                                        const lastParenIndex = value.lastIndexOf(' (');
                                        const secondLastParenIndex = value.lastIndexOf(' (', lastParenIndex - 1);
                                        if (secondLastParenIndex !== -1 && lastParenIndex !== -1 && value.endsWith(')')) {
                                          const actualValue = value.substring(0, secondLastParenIndex);
                                          const desiredColumn = value.substring(secondLastParenIndex + 2, lastParenIndex);
                                          return (isBlacklistMode ? blacklistFilters : whitelistFilters)[desiredColumn]?.includes(actualValue);
                                        }
                                        return false;
                                      } else {
                                        // Parse "Value (Count)" format for single column
                                        const parenIndex = value.lastIndexOf(' (');
                                        if (parenIndex !== -1 && value.endsWith(')')) {
                                          const actualValue = value.substring(0, parenIndex);
                                          return (isBlacklistMode ? blacklistFilters : whitelistFilters)[selectedColumn]?.includes(actualValue);
                                        }
                                        return false;
                                      }
                                    })}
                                  onChange={() => {
                                    if (selectedColumn === "All Columns") {
                                      // Handle "All Columns" case
                                      const targetFilter = isBlacklistMode ? blacklistFilters : whitelistFilters;
                                      const setTargetFilter = isBlacklistMode ? setBlacklistFilters : setWhitelistFilters;
                                      const setOpposite = isBlacklistMode ? setWhitelistFilters : setBlacklistFilters;

                                        const allSelected = filteredSuggestions.every(value => {
                                         // Parse "Value (DesiredColumn) (Count)" format
                                         const lastParenIndex = value.lastIndexOf(' (');
                                         const secondLastParenIndex = value.lastIndexOf(' (', lastParenIndex - 1);
                                         if (secondLastParenIndex !== -1 && lastParenIndex !== -1 && value.endsWith(')')) {
                                           const actualValue = value.substring(0, secondLastParenIndex);
                                           const desiredColumn = value.substring(secondLastParenIndex + 2, lastParenIndex);
                                           return targetFilter[desiredColumn]?.includes(actualValue);
                                         }
                                         return false;
                                       });

                                      if (allSelected) {
                                        // Deselect all
                                        const newTargetFilter = { ...targetFilter };
                                        const newOppositeFilter = { ...(isBlacklistMode ? whitelistFilters : blacklistFilters) };
                                        
                                        filteredSuggestions.forEach(value => {
                                          // Parse "Value (DesiredColumn) (Count)" format
                                          const lastParenIndex = value.lastIndexOf(' (');
                                          const secondLastParenIndex = value.lastIndexOf(' (', lastParenIndex - 1);
                                          if (secondLastParenIndex !== -1 && lastParenIndex !== -1 && value.endsWith(')')) {
                                            const actualValue = value.substring(0, secondLastParenIndex);
                                            const desiredColumn = value.substring(secondLastParenIndex + 2, lastParenIndex);
                                            
                                            if (newTargetFilter[desiredColumn]) {
                                              newTargetFilter[desiredColumn] = newTargetFilter[desiredColumn].filter(v => v !== actualValue);
                                            }
                                            if (newOppositeFilter[desiredColumn]) {
                                              newOppositeFilter[desiredColumn] = newOppositeFilter[desiredColumn].filter(v => v !== actualValue);
                                            }
                                          }
                                        });
                                        
                                        setTargetFilter(newTargetFilter);
                                        setOpposite(newOppositeFilter);
                                      } else {
                                        // Select all
                                        const newTargetFilter = { ...targetFilter };
                                        const newOppositeFilter = { ...(isBlacklistMode ? whitelistFilters : blacklistFilters) };
                                        
                                        filteredSuggestions.forEach(value => {
                                          // Parse "Value (DesiredColumn) (Count)" format
                                          const lastParenIndex = value.lastIndexOf(' (');
                                          const secondLastParenIndex = value.lastIndexOf(' (', lastParenIndex - 1);
                                          if (secondLastParenIndex !== -1 && lastParenIndex !== -1 && value.endsWith(')')) {
                                            const actualValue = value.substring(0, secondLastParenIndex);
                                            const desiredColumn = value.substring(secondLastParenIndex + 2, lastParenIndex);
                                            
                                            if (!newTargetFilter[desiredColumn]) newTargetFilter[desiredColumn] = [];
                                            if (!newOppositeFilter[desiredColumn]) newOppositeFilter[desiredColumn] = [];
                                            
                                            if (!newTargetFilter[desiredColumn].includes(actualValue)) {
                                              newTargetFilter[desiredColumn].push(actualValue);
                                            }
                                            newOppositeFilter[desiredColumn] = newOppositeFilter[desiredColumn].filter(v => v !== actualValue);
                                          }
                                        });
                                        
                                        setTargetFilter(newTargetFilter);
                                        setOpposite(newOppositeFilter);
                                      }
                                    } else {
                                      // Handle single column case
                                    const targetFilter = isBlacklistMode ? blacklistFilters : whitelistFilters;
                                    const setTargetFilter = isBlacklistMode ? setBlacklistFilters : setWhitelistFilters;
                                    const currentSelection = targetFilter[selectedColumn] || [];

                                    const allSelected = filteredSuggestions.every(value => {
                                      // Parse "Value (Count)" format for single column
                                      const parenIndex = value.lastIndexOf(' (');
                                      if (parenIndex !== -1 && value.endsWith(')')) {
                                        const actualValue = value.substring(0, parenIndex);
                                        return currentSelection.includes(actualValue);
                                      }
                                      return false;
                                    });

                                    if (allSelected) {
                                      // Parse values and remove from selection
                                      const valuesToRemove = filteredSuggestions.map(value => {
                                        const parenIndex = value.lastIndexOf(' (');
                                        if (parenIndex !== -1 && value.endsWith(')')) {
                                          return value.substring(0, parenIndex);
                                        }
                                        return value;
                                      });
                                      
                                      setTargetFilter(prev => ({
                                        ...prev,
                                        [selectedColumn]: currentSelection.filter(v => !valuesToRemove.includes(v)),
                                      }));
                                    } else {
                                      // Parse values and add to selection
                                      const valuesToAdd = filteredSuggestions.map(value => {
                                        const parenIndex = value.lastIndexOf(' (');
                                        if (parenIndex !== -1 && value.endsWith(')')) {
                                          return value.substring(0, parenIndex);
                                        }
                                        return value;
                                      });
                                      
                                      const updated = [...new Set([...currentSelection, ...valuesToAdd])];
                                      setTargetFilter(prev => ({
                                        ...prev,
                                        [selectedColumn]: updated,
                                      }));

                                      const setOpposite = isBlacklistMode ? setWhitelistFilters : setBlacklistFilters;
                                      setOpposite(prev => ({
                                        ...prev,
                                        [selectedColumn]: (prev[selectedColumn] || []).filter(v => !valuesToAdd.includes(v)),
                                      }));
                                      }
                                    }
                                  }}
                                  className="accent-blue-500 cursor-pointer"
                                />
              </label>
                            </div>

                          )}
                        </div>

                        {/* 🔽 Dropdown options */}
                        {filteredSuggestions.length > 0 && (
                          <ul className="absolute bg-gray-800 text-white w-full mt-1 rounded shadow-lg z-50 max-h-60 overflow-y-auto">
                            {filteredSuggestions.map((value) => {
                              const isSelected = selectedColumn === "All Columns" 
                                ? (() => {
                                    // Parse "Value (DesiredColumn) (Count)" format
                                    const lastParenIndex = value.lastIndexOf(' (');
                                    const secondLastParenIndex = value.lastIndexOf(' (', lastParenIndex - 1);
                                    if (secondLastParenIndex !== -1 && lastParenIndex !== -1 && value.endsWith(')')) {
                                      const actualValue = value.substring(0, secondLastParenIndex);
                                      const desiredColumn = value.substring(secondLastParenIndex + 2, lastParenIndex);
                                      return (isBlacklistMode ? blacklistFilters : whitelistFilters)[desiredColumn]?.includes(actualValue);
                                    }
                                    return false;
                                  })()
                                : (() => {
                                    // Parse "Value (Count)" format for single column
                                    const parenIndex = value.lastIndexOf(' (');
                                    if (parenIndex !== -1 && value.endsWith(')')) {
                                      const actualValue = value.substring(0, parenIndex);
                                      return (isBlacklistMode ? blacklistFilters : whitelistFilters)[selectedColumn]?.includes(actualValue);
                                    }
                                    return false;
                                  })();
                              return (
                                <li
                                  key={value}
                                  className={`p-2 cursor-pointer ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`}
                                  onMouseDown={(e) => e.preventDefault()} // 🛑 don't blur input
                                  onClick={() => {
                                    if (selectedColumn === "All Columns") {
                                      // Parse "Value (DesiredColumn) (Count)" format
                                      // Use regex to properly parse the format
                                      const match = value.match(/^(.+?) \((\w+)\) \(\d+\)$/);
                                      console.log('[FilterUI] Parsing All Columns value:', { value, match });
                                      if (match) {
                                        const actualValue = match[1];
                                        const desiredColumn = match[2];
                                        console.log('[FilterUI] Parsed values:', { actualValue, desiredColumn });
                                        
                                        const targetFilter = isBlacklistMode ? blacklistFilters : whitelistFilters;
                                        const setTargetFilter = isBlacklistMode ? setBlacklistFilters : setWhitelistFilters;

                                        const currentlySelected = targetFilter[desiredColumn] || [];
                                        const newSelection = currentlySelected.includes(actualValue)
                                          ? currentlySelected.filter(v => v !== actualValue)
                                          : [...new Set([...currentlySelected, actualValue])];

                                        setTargetFilter(prev => ({
                                          ...prev,
                                          [desiredColumn]: newSelection,
                                        }));

                                        const setOpposite = isBlacklistMode ? setWhitelistFilters : setBlacklistFilters;
                                        setOpposite(prev => {
                                          const previousValues = prev[desiredColumn] || [];
                                          const updatedValues = previousValues.filter(v => v !== actualValue);
                                          if (updatedValues.length === 0) {
                                            const { [desiredColumn]: _omit, ...rest } = prev;
                                            return rest;
                                          }
                                          return { ...prev, [desiredColumn]: updatedValues };
                                        });
                                      }
                                    } else {
                                    // Parse "Value (Count)" format for single column
                                    const parenIndex = value.lastIndexOf(' (');
                                    if (parenIndex !== -1 && value.endsWith(')')) {
                                      const actualValue = value.substring(0, parenIndex);
                                      
                                      const targetFilter = isBlacklistMode ? blacklistFilters : whitelistFilters;
                                      const setTargetFilter = isBlacklistMode ? setBlacklistFilters : setWhitelistFilters;

                                      const currentlySelected = targetFilter[selectedColumn] || [];
                                      const newSelection = currentlySelected.includes(actualValue)
                                        ? currentlySelected.filter(v => v !== actualValue)
                                        : [...new Set([...currentlySelected, actualValue])];

                                    setTargetFilter(prev => ({
                                      ...prev,
                                      [selectedColumn]: newSelection,
                                    }));

                                      const setOpposite = isBlacklistMode ? setWhitelistFilters : setBlacklistFilters;
                                      setOpposite(prev => {
                                        const previousValues = prev[selectedColumn] || [];
                                        const updatedValues = previousValues.filter(v => v !== actualValue);
                                        if (updatedValues.length === 0) {
                                          const { [selectedColumn]: _omit, ...rest } = prev;
                                          return rest;
                                        }
                                        return { ...prev, [selectedColumn]: updatedValues };
                                      });
                                    }
                                    }
                                  }}
                                >
                                                                      {value === "(Blank)" ? <i>(Blank)</i> :
                                      value.length > 100 ? `${value.substring(0, 100)}...` : value}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                      <button 
                        onClick={toggleFilterMode}
                        className={`px-4 py-2 rounded-lg transition ${isBlacklistMode ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white whitespace-nowrap`}
                        title={isBlacklistMode ? 'Blacklist Mode (Excluding Matches)' : 'Whitelist Mode (Including Matches)'}
                      >
                        {isBlacklistMode ? "Exclude" : "Include"}
                      </button>

                      {isFiltered && (
                        <div className="relative group">
                          <button
                            className={`px-4 py-2 rounded-md transition bg-hs-orange-light hover:bg-hs-orange text-hs-background whitespace-nowrap ${
                              isImportDisabled ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={async () => {
                              // Check for mapping conflicts first
                              if (hasMappingConflicts) {
                                const conflictDetails = Object.entries(duplicateAssignments)
                                  .filter(([, list]) => list.length > 1)
                                  .map(([column, fields]) => `"${column}" → ${fields.join(', ')}`)
                                  .join('\n');
                                
                                const conflictResult = await Swal.fire({
                                  title: 'Mapping Conflicts Detected',
                                  html: `
                                    <div class="text-left">
                                      <p class="mb-3">The following columns are mapped to multiple HubSpot fields:</p>
                                      <div class="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                                        <pre class="text-sm text-gray-700 whitespace-pre-wrap">${conflictDetails}</pre>
                                      </div>
                                      <p class="text-sm text-gray-600">This may cause data to be duplicated across fields. Do you want to proceed anyway?</p>
                                    </div>
                                  `,
                                  icon: 'warning',
                                  showCancelButton: true,
                                  confirmButtonColor: '#f59e0b',
                                  cancelButtonColor: '#6b7280',
                                  confirmButtonText: 'Yes, Import Anyway',
                                  cancelButtonText: 'Cancel'
                                });
                                
                                if (!conflictResult.isConfirmed) {
                                  return; // User cancelled due to conflicts
                                }
                              }
                              
                              // Show normal confirmation dialog
                              const result = await Swal.fire({
                                title: 'Confirm Import',
                                text: `Are you sure you want to import ${filteredResults.length} contacts to HubSpot?`,
                                icon: 'question',
                                showCancelButton: true,
                                confirmButtonColor: '#22c55e',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, Import!',
                                cancelButtonText: 'Cancel'
                              });
                              if (result.isConfirmed) {
                                importFile();
                              }
                            }}
                            disabled={isImportDisabled}
                          >
                            {process.env.REACT_APP_DISABLE_IMPORT === 'true' ? 'Import Disabled' : 'Import File to TWS Hubspot'}
                          </button>
                          {isImportDisabled && (
                            <div className="absolute inset-0" aria-hidden="true"></div>
                          )}
                          <div className={`absolute bottom-full mb-2 right-0 ${isImportDisabled ? 'bg-red-500' : 'bg-green-600'} text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-[9999]`}>
                            {isImportDisabled ? (importDisableReason || 'Import prerequisites not met') : `Ready to import ${filteredResults.length} ${filteredResults.length === 1 ? 'contact' : 'contacts'}`}
                          </div>
                        </div>
                      )}
                    </div>

                      <div className="flex flex-wrap gap-2 mb-4 px-[15px] items-center justify-center">
                        {Object.entries(whitelistFilters).filter(([, values]) => (values || []).length > 0).map(([column, values]) => (
                          <span
                            key={`whitelist-${column}`}
                            className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm flex items-center cursor-pointer hover:bg-blue-700"
                            onClick={() => { if ((values || []).length > 1) { openTagModal('whitelist', column); } else { toggleSingleTagMode('whitelist', column); } }}
                            title={values.length === 1 ? `${column}: ${values[0]}` : `${column}: ${values.length} selected`}
                          >
                            {values.length === 1 ? (
                              <>
                                {column}: {values[0] === '(Blank)' ? <i>(Blank)</i> : 
                                  values[0].length > 30 ? `${values[0].substring(0, 30)}...` : values[0]}
                              </>
                            ) : (
                              <>
                                {column} ({values.length})
                              </>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setWhitelistFilters(prev => {
                                  const { [column]: _omit, ...rest } = prev;
                                  return rest;
                                });
                              }}
                              className="ml-2 text-white rounded p-2 -m-2"
                              aria-label={`Clear ${column} whitelist filters`}
                            >
                              ×
                            </button>
                          </span>
                        ))}

                        {Object.entries(blacklistFilters).filter(([, values]) => (values || []).length > 0).map(([column, values]) => (
                          <span
                            key={`blacklist-${column}`}
                            className="bg-red-600 text-white px-3 py-1 rounded-full text-sm flex items-center cursor-pointer hover:bg-red-700"
                            onClick={() => { if ((values || []).length > 1) { openTagModal('blacklist', column); } else { toggleSingleTagMode('blacklist', column); } }}
                            title={values.length === 1 ? `${column}: ${values[0]}` : `${column}: ${values.length} excluded`}
                          >
                            {values.length === 1 ? (
                              <>
                                {column}: {values[0] === '(Blank)' ? <i>(Blank)</i> : 
                                  values[0].length > 30 ? `${values[0].substring(0, 30)}...` : values[0]}
                              </>
                            ) : (
                              <>
                                {column} ({values.length})
                              </>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setBlacklistFilters(prev => {
                                  const { [column]: _omit, ...rest } = prev;
                                  return rest;
                                });
                              }}
                              className="ml-2 text-white rounded p-2 -m-2"
                              aria-label={`Clear ${column} blacklist filters`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      {(
                        Object.values(whitelistFilters).some(arr => arr.length > 0) ||
                        Object.values(blacklistFilters).some(arr => arr.length > 0)
                      ) && (
                        <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-sm flex items-center cursor-pointer hover:bg-gray-600 transition"
                          onClick={() => {
                            setWhitelistFilters({});
                            setBlacklistFilters({});
                          }}
                        >
                          Clear
                        </span>
                      )}

                      </div>

                      {/* Tag management modal */}
                      <TagModal
                        isOpen={isTagModalOpen}
                        onClose={() => setIsTagModalOpen(false)}
                        mode={tagModalMode}
                        column={tagModalColumn}
                        values={tagModalValues}
                        onRemoveValue={handleRemoveTagValue}
                        onClearAll={handleClearAllTags}
                        onMoveAll={handleMoveAllTags}
                        onToggleValue={handleToggleTagValue}
                      />

                      {/* Whitelist/Blacklist toggle moved next to filters above */}

                    {/* STEP 3 layout (replace your flex row + pl-[200px] block) */}
                    <div className="grid grid-cols-12 gap-4 w-full max-w-screen-2xl mx-auto px-4">

                      {(selectedSource === "Lead List" || selectedSource === "Project List") && predefinedHeaders.length > 0 && detectedHeaders.length > 0 && (
                        <aside className="col-span-12 lg:col-span-4 2xl:col-span-3 bg-gray-800 text-white p-3 rounded-md text-center [font-size:clamp(12px,0.95vw,16px)]">
                          <div className="mb-2">
                            <div className="flex items-center gap-2">
                            <select value={selectedPreset}onChange={(e) => {
                              setSelectedPreset(e.target.value);}} 
                              className="w-full px-2 py-1 rounded bg-gray-700 text-white text-center border border-gray-600 text-xs leading-tight"
                            >
                            <option value="">-- Select Preset  -- </option>
                            {presets.map((preset) => {
                              if (preset.source === selectedSource) {
                                return (
                                  <option key={preset.preset_name} value={preset.preset_name}>
                                    {preset.preset_name}
                                  </option>
                                );
                              }
                              return null; // Ensure something is returned if condition isn't met
                            })}
                            <option value="New Preset">➕   New Preset</option>
                            </select>
                              {selectedPreset && selectedPreset !== 'New Preset' && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const toDelete = selectedPreset;
                                    const confirm = await Swal.fire({
                                      title: 'Delete preset?',
                                      text: `This will delete "${toDelete}" for ${selectedSource}.`,
                                      icon: 'warning',
                                      showCancelButton: true,
                                      confirmButtonColor: '#dc2626',
                                      cancelButtonColor: '#374151',
                                      confirmButtonText: 'Delete'
                                    });
                                    if (!confirm.isConfirmed) return;
                                    try {
                                      const api = createApiInstance();
                                      const res = await api.delete('/deletePreset', { data: {
                                        name: toDelete,
                                        source: selectedSource,
                                        created_by: user.id,
                                      }});
                                      if (res.status === 200) {
                                        try {
                                          const refreshed = await api.get(`/getUserPresets?user_id=${user.id}`);
                                          if (Array.isArray(refreshed.data)) {
                                            setPresets(refreshed.data);
                                          }
                                        } catch (_) {}
                                        setSelectedPreset('');
                                        setNewPresetName('');
                                        await Swal.fire({
                                          title: 'Preset deleted',
                                          text: 'Selection reset to "-- Select Preset --"',
                                          icon: 'success',
                                          confirmButtonColor: '#16a34a'
                                        });
                                      } else {
                                        await Swal.fire({
                                          title: 'Failed to delete preset',
                                          text: `Server responded with status ${res.status}.`,
                                          icon: 'error',
                                          confirmButtonColor: '#dc2626'
                                        });
                                      }
                                    } catch (err) {
                                      const message = err?.response?.data?.message || err?.message || 'Unknown error';
                                      await Swal.fire({
                                        title: 'Failed to delete preset',
                                        text: message,
                                        icon: 'error',
                                        confirmButtonColor: '#dc2626'
                                      });
                                    }
                                  }}
                                  className="px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs"
                                  title="Delete selected preset"
                                >
                                  −
                                </button>
                              )}
                            </div>
                            {selectedPreset === "New Preset" ? (
                              <div key="new-preset-input" className="mb-1 mt-2 text-center">
                                <div className="flex gap-2 items-center">
                                <input
                                  type="text"
                                  value={newPresetName}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewPresetName(value);
                                    // Check if preset name exists as user types
                                    if (value.trim() && isPresetNameExists(value.trim())) {
                                      console.log("⚠️ Preset name already exists");
                                    }
                                  }}
                                    className={`text-center flex-1 px-2 py-1 rounded bg-gray-700 text-white border text-xs leading-tight ${
                                    isPresetNameExists(newPresetName.trim())
                                        ? "border-yellow-500"
                                      : "border-gray-600"
                                  }`}
                                  placeholder="Enter new preset name"
                                />
                                  <button
                                    type="button"
                                    onClick={handleCreatePresetNow}
                                    className="px-2 py-1 rounded bg-hs-orange hover:bg-orange-600 text-white text-xs"
                                    title="Create preset now"
                                  >
                                    +
                                  </button>
                                </div>

                                {selectedPreset === "New Preset" && newPresetName.trim() && isPresetNameExists(newPresetName.trim()) && (
                                  <p className="text-yellow-500 text-[11px] mt-1">⚠️ A preset with this name already exists for {selectedSource}</p>
                                )}
                              </div>
                            ) : null}
                          </div>
                          {selectedSource === "Lead List" && (
                            <div className="mb-1 mt-2 text-center">
                              <div className="flex gap-2 items-center">
                                <input
                                  type="text"
                                  value={rawProjectTitle}
                                  onChange={(e) => setRawProjectTitle(e.target.value)}
                                  className={`text-center flex-1 px-2 py-1 rounded bg-gray-700 text-white border text-xs leading-tight ${
                                    !newProjectTitle.trim() ? "border-red-500" : "border-gray-600"
                                  }`}
                                  placeholder="Enter project title"
                                />
                              </div>
                              {!rawProjectTitle.trim() && (
                                <p className="text-red-500 text-[11px] mt-1">Project Title is required.</p>
                              )}
                              {(newProjectID && rawProjectTitle.trim()) && (
                                <div className="mt-1 px-2 py-1 bg-gray-700/60 rounded flex items-center justify-center relative">
                                  <div className="inline-flex items-center gap-1">
                                    <p className="text-xs leading-4 font-mono text-blue-400 break-words">{newProjectTitle}</p>
                                    <span
                                      className="ml-0 w-4 h-4 rounded-full bg-gray-600 text-white text-[10px] leading-4 inline-flex items-center justify-center cursor-help relative group"
                                      aria-label="HubSpot deal title info"
                                      title="This is the HubSpot deal title that will appear in HubSpot"
                                    >
                                      i
                                       <span className="absolute left-1/2 -translate-x-1/2 -top-8 w-56 px-2 py-1 bg-gray-900 text-white text-[11px] rounded shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none z-[9999] whitespace-normal break-words transform-gpu">
                                        This is the HubSpot deal title that will appear in your HubSpot pipeline.
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mb-2">Ensure columns are correctly mapped.</p>
                          
                          <div className="overflow-x-auto overflow-y-visible max-h-100">
                            <table className="w-full border border-gray-700 text-sm">
                              <thead>
                                <tr className="bg-gray-700 text-xs">
                                  <th className="px-2 py-1 border border-gray-600 text-center">Required Column</th>
                                  <th className="px-2 py-1 border border-gray-600 text-center">Detected Header</th>
                                </tr>
                              </thead>
                              <tbody>
                                {predefinedHeaders.map((requiredHeader, index) => {
                                  const mergedSynonyms = extractSynonymsFromPresets(presets);
                                  const headerSynonyms = mergedSynonyms[requiredHeader] || [];
                                  
                                  return (
                                    <tr key={index} className={`border border-gray-700`}>
                                      <td className="px-2 py-1 text-gray-300 relative align-middle whitespace-nowrap text-xs">
                                        {requiredHeader === "First Name" ? (
                                          <div className="group relative inline-block">
                                            <span className="cursor-help text-hs-orange">First Name</span>
                                            <div className="absolute left-1/2 transform ml-16 bottom-[30px] -translate-x-1/2 mb-1 w-max max-w-[18rem] px-2 py-1 bg-gray-900 text-white text-[11px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[9999] text-left pointer-events-none whitespace-normal break-words">
                                              If not separated, map to "Full Name".
                                            </div>
                                            <span className="block text-[11px] text-gray-400">(Full Name)</span>
                                          </div>
                                        ) : requiredHeader === "Last Name" ? (
                                          <div className="group relative inline-block">
                                            <span className="cursor-help text-hs-orange">Last Name</span>
                                            <div className="absolute left-1/2 transform ml-16 -translate-x-1/2 bottom-full mb-1 w-max max-w-[18rem] px-2 py-1 bg-gray-900 text-white text-[11px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[9999] text-left pointer-events-none whitespace-normal break-words">
                                              Map to "No Match" if name isn't separated.
                                            </div>
                                          </div>
                                        ) : requiredHeader === "Phone" || requiredHeader === "Mobile Phone" || requiredHeader === "Company Phone" ? (
                                          <div className="group relative inline-block">
                                            <span className={`cursor-help ${
                                              requiredHeader === "Phone" ? "text-blue-300" :
                                              requiredHeader === "Mobile Phone" ? "text-green-300" :
                                              "text-purple-300"
                                            }`}>{requiredHeader}</span>
                                            <div className="absolute left-1/2 transform ml-16 -translate-x-1/2 bottom-full mb-1 w-max max-w-[22rem] px-2 py-1 bg-gray-900 text-white text-[11px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[9999] text-left pointer-events-none whitespace-normal break-words">
                                              Map {requiredHeader === "Phone" ? "primary work" : requiredHeader === "Mobile Phone" ? "mobile/cell" : "company/fax"} phone numbers here.
                                            </div>
                                          </div>
                                        ) : (
                                          requiredHeader
                                        )}
                                      </td>
                                      <td className="px-2 py-1 align-middle text-xs">
                                        {(() => {
                                          const mappedValue = (columnMappings[requiredHeader] || "").trim();
                                          const conflictHeaders = mappedValue && mappedValue !== "__blank__"
                                            ? (duplicateAssignments[mappedValue.toLowerCase()] || [])
                                            : [];
                                          const hasConflict = conflictHeaders.length > 1;
                                          return (
                                            <select
                                              value={columnMappings[requiredHeader] || ""}
                                              onChange={(e) => handleMappingChange(requiredHeader, e.target.value)}
                                              className={`w-full px-2 py-1 rounded bg-gray-700 border text-xs leading-tight ${hasConflict ? "border-red-500 text-red-400 focus:text-white" : "border-gray-500 text-white"}`}
                                              title={hasConflict ? `Also selected for: ${conflictHeaders.filter(h => h !== requiredHeader).join(", ")}` : ""}
                                            >
                                              <option value="">-- No Match --</option>
                                              {detectedHeaders
                                                .filter((detected) => detected !== 'Location')
                                                .map((detected, idx) => {
                                                const optionValue = detected.trim() === "" ? "__blank__" : detected;
                                                const isMatch = detected.toLowerCase() === requiredHeader.toLowerCase() ||
                                                  headerSynonyms.some(syn => detected.toLowerCase().includes(syn.toLowerCase()));
                                                const isCurrent = (columnMappings[requiredHeader] || "") === optionValue;
                                                const isConflictedOption = hasConflict && isCurrent;
                                                const optionClass = isConflictedOption ? "text-red-400" : (isMatch ? "text-green-400" : "");
                                                return (
                                                  <option key={idx} value={optionValue} className={optionClass}>
                                                    {detected} {isMatch ? "✓" : ""}
                                                  </option>
                                                );
                                              })}
                                            </select>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                </aside>
              )}
              <section className={`col-span-12 ${(selectedSource === "Lead List" || selectedSource === "Project List") ? "lg:col-span-8 2xl:col-span-9" : "lg:col-span-12"}`}>
                <div className={`hs-table bg-white rounded-lg shadow-md overflow-x-auto`}>
                  <DataTable
                    columns={columns}
                    data={filteredResults}
                    pagination
                    paginationPerPage={15}
                    highlightOnHover
                    striped
                    responsive
                    fixedHeader	
                    fixedHeaderScrollHeight="600px"
                    customStyles={{
                      table: { style: { fontSize: 'clamp(5px, 0.95vw, 7px)' } },
                      pagination: {
                        style: {
                          position: "sticky",
                          bottom: 0,
                          zIndex: 10,
                          backgroundColor: "#ffffff",
                          borderTop: "1px solid #e5e7eb",
                        },
                      },
                      
                      headRow: {
                        style: {
                          whiteSpace: "normal",
                          textAlign: "center",
                          color: "black",
                        },
                      },
                      headCells: {
                        style: {
                          justifyContent: "center",
                          alignItems: "center",
                          padding: 'clamp(6px, 0.9vw, 12px)',
                          borderRadius: "0px",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          textAlign: "center",
                          width: 'clamp(100px, 15vw, 180px)',
                        },
                      },
                      rows: {
                        style: {
                          textAlign: "center",
                          borderRadius: "0px",
                          maxHeight: "unset",
                        },
                      },
                      cells: {
                        style: {
                          justifyContent: "center",
                          textAlign: "center",
                          padding: 'clamp(8px, 1vw, 14px)',
                          borderRadius: "0px",
                          maxHeight: "unset",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        },
                      },
                    }}
                    
                  />
                </div>
              </section>
              </div>
            </div>
            <div className="fixed left-8 top-1/2 transform -translate-y-1/2 z-50">
              <button
                onClick={() => setCurrentStep(2)}
                className="bg-gray-600/60 hover:bg-gray-700/80 text-white w-16 h-16 rounded-full font-extrabold text-2xl transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-110 flex items-center justify-center"
                title="Back to Summary"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5" />
                  <path d="M11 19l-7-7 7-7" />
                </svg>
              </button>
        </div>
          </div>
        )}
        <div>
        </div>
        {filterModal && (
          <FilterModal toggleModal={handleModalFilter} settings={user?.role === 'agent' ? user?.selectedFranchisee?.settings : selectedFranchisee?.settings}/>
        )}

        {isFlattenHelpOpen && (
          <GuideModal isOpen={isFlattenHelpOpen} onClose={() => setIsFlattenHelpOpen(false)}>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 shadow-lg">
              <div className="text-gray-800">
                <h3 className="text-xl font-bold mb-4 text-center text-gray-900">Flatten Contacts</h3>
                <p className="text-sm text-gray-700 mb-4 text-center">
                  Turn this on if your file has multiple contacts in the same row (e.g., columns like "Contact 1 …", "Contact 2 …").
                  Otherwise, keep it off.
                </p>
                <div className="flex justify-center">
                  <img
                    src="https://drive.google.com/thumbnail?id=1RoK9O-TEHGfLChifmPUZvsV5SWsVQdnd&sz=w1600"
                    alt="Example of multi-contact row"
                    className="max-w-full max-h-[600px] object-contain rounded-lg shadow-md"
                  />
                </div>
              </div>
            </div>
          </GuideModal>
        )}

        {isAddProjectIdHelpOpen && (
          <GuideModal isOpen={isAddProjectIdHelpOpen} onClose={() => setIsAddProjectIdHelpOpen(false)}>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 shadow-lg">
              <div className="text-gray-800">
                <h3 className="text-xl font-bold mb-4 text-center text-gray-900">Add Project ID</h3>
                <p className="text-sm text-gray-700 mb-4 text-center">
                  Turn this on if your Project List file does not include a Project ID column. Otherwise, keep it off.
                </p>
                <div className="flex justify-center">
                  <img
                    src="https://drive.google.com/thumbnail?id=1MISo19v7qfCn0NnUV0_GRLLMhrKyhgYD&sz=w1600"
                    alt="Example of generating/adding a Project ID"
                    className="max-w-full max-h-[600px] object-contain rounded-lg shadow-md"
                  />
                </div>
              </div>
            </div>
          </GuideModal>
        )}

        {isDropdownsHelpOpen && (
          <GuideModal isOpen={isDropdownsHelpOpen} onClose={() => setIsDropdownsHelpOpen(false)}>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 shadow-lg">
              <div className="text-gray-800">
                <h3 className="text-xl font-bold mb-4 text-center text-gray-900">Selecting Sheet and Columns</h3>
                <div className="space-y-4 text-sm leading-relaxed">
                {sheetNames.length > 1 && (
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="font-semibold text-gray-900 mb-2">Select Sheet</p>
                    <p className="text-gray-700">Choose the worksheet inside your Excel file that contains your contacts.</p>
                  </div>
                )}
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="font-semibold text-gray-900 mb-2">Select Email Column</p>
                    <p className="text-gray-700">Pick the column that holds <u>email addresses</u>. Always required.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="font-semibold text-gray-900 mb-2">Select Zip Column</p>
                    <p className="text-gray-700">Pick the <u>ZIP/Postal code</u> column used for the <i>"Filter Settings"</i>. Optional.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="font-semibold text-gray-900 mb-2">Select Project Title Column</p>
                    <p className="text-gray-700">Pick the column that holds <u>project titles</u>. Required when using <i>Add Project ID</i> on Project List.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="font-semibold text-gray-900 mb-2">Tips</p>
                    <ul className="list-disc ml-5 text-gray-700">
                      <li>If you switch sheets, reselect your columns before filtering.</li>
                      <li>Suggested matches are marked with a ✓.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </GuideModal>
        )}

        {isSummaryHelpOpen && (
          <GuideModal
            isOpen={isSummaryHelpOpen}
            onClose={() => setIsSummaryHelpOpen(false)}
            footerExtras={
              <a
                href="https://www.loom.com/share/7c6a52f208394a45b73049d03c0e36a3"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors duration-200"
              >
                Watch Explanation
              </a>
            }
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 shadow-lg">
              <div className="text-gray-800">
                <h3 className="text-xl font-bold mb-4 text-center text-gray-900">Summary Guide</h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-2">Contact Types</h4>
                    <ul className="list-disc ml-5 text-sm space-y-2 text-gray-700">
                      <li><strong>Valid Contacts</strong>: rows with a valid Email, de-duplicated per email + project.</li>
                      <li><strong>Invalid Contacts</strong>: rows missing Email (or invalid), optionally replaced with temp emails if enabled.</li>
                      <li><strong>Duplicate Emails</strong>: extra copies of the same email within the same project (one kept per project).</li>
                      <li><strong>Downloads</strong>: export each list for review; projects and companies reflect mapped fields.</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-2">Generate Temp Emails</h4>
                    <p className="text-sm text-gray-700 mb-3">When enabled, invalid rows (missing/invalid Email) are included with a generated temporary email so you can import and reconcile later.</p>
                    <ul className="list-disc ml-5 text-sm space-y-1 text-gray-700">
                      <li><strong>Format</strong>: <code className="bg-gray-100 px-1 rounded">noemail+{`{parts}`}_{`{hash}`}@temporary.com</code></li>
                      <li><strong>PlanHub / ConstructConnect</strong>: {`{parts}`} is derived from <code className="bg-gray-100 px-1 rounded">Name</code> and <code className="bg-gray-100 px-1 rounded">Phone</code> only (stable across file changes).</li>
                      <li><strong>Project List / Lead List</strong>: {`{parts}`} is derived from multiple columns (Name, Company, Project Title/ID, Phone, Address, City, State, Zip) for uniqueness.</li>
                      <li><strong>Consistency</strong>: the same contact gets the same temp email within a given data set.</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-2">Name Collision Checker</h4>
                    <p className="text-sm text-gray-700 mb-3">Checks temp email contacts against existing HubSpot contacts to prevent duplicates before importing.</p>
                    <ul className="list-disc ml-5 text-sm space-y-1 text-gray-700">
                      <li><strong>Checks temp emails only</strong>: compares names from invalid email contacts</li>
                      <li><strong>No collisions found</strong>: all temp contacts are new - proceed with import</li>
                      <li><strong>Collisions found</strong>: review and exclude duplicates before importing</li>
                    </ul>
                    <div className="text-center mt-3">
                      <a
                        href="https://www.loom.com/share/e983d6a7289645b6b0593ce317b198d7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                      >
                        📹 Watch Tutorial
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </GuideModal>
        )}

        {/* Collision Detection Modal */}
        {showCollisionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto">
              {/* Drag handle */}
              <div className="absolute top-0 left-0 right-10 h-6 cursor-grab active:cursor-grabbing" />
              <div className="select-none cursor-grab active:cursor-grabbing mb-3" />
              
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Name Collision Detection</h2>
                <button
                  onClick={() => {
                    setShowCollisionModal(false);
                    setCollisionSearchTerm('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                The following names {useFullName 
                  ? `from the "${selectedNameColumn}" column` 
                  : `from the "${selectedFirstNameColumn}" and "${selectedLastNameColumn}" columns`
                } were found in HubSpot. 
                You can choose to exclude these contacts from receiving temp emails and being included in the valid contacts list.
              </p>
              
              {/* Search bar */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={collisionSearchTerm}
                  onChange={(e) => setCollisionSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Exclude All / Include All buttons */}
              <div className="mb-4 flex gap-2">
                <button
                  onClick={() => {
                    const filteredContacts = collisionContacts
                      .filter(contact => {
                        const fullName = `${contact.firstname || ''} ${contact.lastname || ''}`.trim();
                        return fullName.toLowerCase().includes(collisionSearchTerm.toLowerCase());
                      })
                      .map(contact => `${contact.firstname || ''} ${contact.lastname || ''}`.trim());
                    setExcludedContacts(new Set(filteredContacts));
                  }}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                >
                  Exclude All ({collisionContacts.filter(contact => {
                    const fullName = `${contact.firstname || ''} ${contact.lastname || ''}`.trim();
                    return fullName.toLowerCase().includes(collisionSearchTerm.toLowerCase());
                  }).length})
                </button>
                <button
                  onClick={() => setExcludedContacts(new Set())}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                >
                  Include All
                </button>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {collisionContacts
                  .filter(contact => {
                    const fullName = `${contact.firstname || ''} ${contact.lastname || ''}`.trim();
                    return fullName.toLowerCase().includes(collisionSearchTerm.toLowerCase());
                  })
                  .map((contact, index) => {
                    const fullName = `${contact.firstname || ''} ${contact.lastname || ''}`.trim();
                    const isExcluded = excludedContacts.has(fullName);
                    return (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                          isExcluded 
                            ? 'border-red-300 bg-red-50 hover:bg-red-100' 
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setExcludedContacts(prev => {
                            const newSet = new Set(prev);
                            if (isExcluded) {
                              newSet.delete(fullName);
                            } else {
                              newSet.add(fullName);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <div className="flex-1">
                          <div className={`font-semibold ${isExcluded ? 'text-red-900' : 'text-gray-900'}`}>
                            {fullName}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isExcluded 
                              ? 'border-red-500 bg-red-500' 
                              : 'border-gray-300 bg-white'
                          }`}>
                            {isExcluded && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <span className={`text-sm font-medium ${isExcluded ? 'text-red-700' : 'text-gray-700'}`}>
                            {isExcluded ? 'Excluded' : 'Exclude'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCollisionModal(false);
                    setCollisionSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Filter out excluded contacts from invalidData using the selected name columns
                    const filteredInvalidData = originalInvalidData.filter(contact => {
                      let nameValue = '';
                      
                      if (useFullName) {
                        const name = contact[selectedNameColumn];
                        nameValue = name ? name.toString().trim() : '';
                      } else {
                        const firstName = contact[selectedFirstNameColumn];
                        const lastName = contact[selectedLastNameColumn];
                        if (firstName && lastName) {
                          nameValue = `${firstName.toString().trim()} ${lastName.toString().trim()}`;
                        }
                      }
                      
                      return !excludedContacts.has(nameValue);
                    });
                    
                    setInvalidData(filteredInvalidData);
                    setShowCollisionModal(false);
                    // Don't clear collisionContacts - keep them for potential rechecking
                    // Don't clear excludedContacts - keep them for potential rechecking
                    setCollisionSearchTerm('');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Apply Exclusions ({excludedContacts.size} selected)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No Collision Found Modal */}
        {showNoCollisionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Name Collisions Found</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Great! No existing contacts with matching names were found in HubSpot for your temp email contacts. 
                  All temp email contacts will be imported as new records.
                </p>
                <button
                  onClick={() => {
                    setShowNoCollisionModal(false);
                    setCurrentStep(3);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        
      </div>
    </div>
  );
}

export default ProjectUpload;
